import React, { useEffect, useState } from "react";
import { ResponsiveContainer, BarChart, Bar, Cell, XAxis, YAxis, Tooltip } from "recharts";
import { LineChartComponent, ProgressBar, SectionCard, StatTile } from "./budgetComponents";
import FlowLogo from "./FlowLogo";
import {
  DEBT_METHODS,
  INCOME_TYPES,
  RECOMMENDATION_RATES,
  buildGoalTimeline,
  estimateDebtPayoff,
  formatCurrency,
  formatMonths,
  getDebtMethodReason,
  parseNumber,
  safePercent,
  toMonthly,
} from "./budgetUtils";

const createInitialGoalForm = () => ({
  name: "",
  type: "savings",
  targetAmount: "",
  linkedDebtId: "",
  contributionStyle: "monthly",
  contributionMode: "recommended",
  recommendationLevel: "balanced",
  customContribution: "",
});

const createDefaultCustomPayDates = () => [{ id: 1, date: "", amount: "" }];
const createInitialMixedIncome = () => ({
  w2InputMode: "gross",
  w2PayStructure: "fixed",
  w2Frequency: "Bi-weekly",
  w2BaseIncome: "",
  w2WithholdingPercent: "",
  w2WithholdingAmount: "",
  w2HourlyRate: "",
  w2TypicalHours: "",
  w2ActualHours: "",
  w2OvertimeHours: "",
  w2OvertimeMultiplier: "1.5",
  w2Payday: "Friday",
  w2LastPaycheckDate: "",
  contractorFrequency: "Monthly",
  contractorBaseIncome: "",
  contractorTaxPercent: "",
  contractorPayday: "Friday",
  contractorLastPaycheckDate: "",
  contractorWriteOffMode: "medium",
  contractorCustomWriteOffPercent: 35,
  contractorCustomPayDates: createDefaultCustomPayDates(),
});
const createAdditionalIncomeRow = () => ({
  id: Date.now() + Math.random(),
  source: "",
  frequency: "Monthly",
  amount: "",
});
const hasAdditionalIncomeContent = (row = {}) => Boolean(row.source?.trim()) || parseNumber(row.amount) > 0;
const removeEmptyAdditionalIncomeRows = (rows = []) => rows.filter(hasAdditionalIncomeContent);
const createDefaultDeductions = () => ["Medicaid", "Social Security", "Retirement", "Health Insurance", "Child Support", "Other"].map((name) => ({ name, mode: "%", value: "" }));
const getTodayInputValue = () => new Date().toISOString().slice(0, 10);
const createDefaultTransactions = () => [];
const createInitialTransactionForm = () => ({
  date: getTodayInputValue(),
  amount: "",
  type: "expense",
  category: "",
  account: "Checking",
  description: "",
});
const createQuickRepeatTransactionForm = (current = {}) => ({
  date: current.date || getTodayInputValue(),
  amount: "",
  type: current.type || "expense",
  category: current.category || "",
  account: current.account || "Checking",
  description: "",
});
const sharedBudgetCategories = [
  { key: "housing", label: "Housing", defaultPlanType: "fixed" },
  { key: "utilities", label: "Utilities", defaultPlanType: "fixed" },
  { key: "insurance", label: "Insurance", defaultPlanType: "fixed" },
  { key: "phone-internet", label: "Phone / Internet", defaultPlanType: "fixed" },
  { key: "food", label: "Food", defaultPlanType: "flexible" },
  { key: "transportation", label: "Transportation", defaultPlanType: "flexible" },
  { key: "subscriptions", label: "Subscriptions", defaultPlanType: "fixed" },
  { key: "entertainment", label: "Entertainment", defaultPlanType: "flexible" },
  { key: "health", label: "Health", defaultPlanType: "flexible" },
  { key: "debt-payment", label: "Debt Payment", defaultPlanType: "fixed" },
  { key: "tax-payment", label: "Tax Payment", defaultPlanType: "fixed" },
  { key: "savings-transfer", label: "Savings Transfer", defaultPlanType: "flexible" },
  { key: "income", label: "Income", defaultPlanType: "flexible" },
  { key: "other", label: "Other", defaultPlanType: "flexible" },
];
const expenseCategoryOptions = sharedBudgetCategories.filter((category) => category.key !== "income" && category.key !== "savings-transfer");
const baseTransactionCategoryOptions = sharedBudgetCategories.map((category) => category.label);
const transactionTypeOptions = [
  { id: "expense", label: "Expense" },
  { id: "income", label: "Income" },
  { id: "transfer", label: "Transfer" },
];
const transactionAccountOptions = ["Checking", "Savings", "Credit Card", "Cash", "Business Checking", "Other"];
const quickTransactionAccountOptions = ["Checking", "Credit Card", "Cash", "Savings", "Business Checking"];
const quickTransactionPresets = [
  { id: "groceries", label: "Groceries", type: "expense", category: "Food", account: "Checking", description: "Grocery run" },
  { id: "gas", label: "Gas", type: "expense", category: "Transportation", account: "Checking", description: "Gas fill-up" },
  { id: "meal", label: "Meal", type: "expense", category: "Food", account: "Checking", description: "Meal purchase" },
  { id: "bill", label: "Bill", type: "expense", category: "Utilities", account: "Checking", description: "Bill payment" },
  { id: "paycheck", label: "Paycheck", type: "income", category: "Income", account: "Checking", description: "Paycheck deposit" },
  { id: "card", label: "Card Purchase", type: "expense", category: "Other", account: "Credit Card", description: "Card purchase" },
];
const createDefaultExpenses = () => [
  { id: 1, name: "Mortgage / Rent", amount: "", planType: "fixed", categoryKey: "housing" },
  { id: 2, name: "Utilities", amount: "", planType: "fixed", categoryKey: "utilities" },
  { id: 3, name: "Insurance", amount: "", planType: "fixed", categoryKey: "insurance" },
  { id: 4, name: "Phone / Internet", amount: "", planType: "fixed", categoryKey: "phone-internet" },
  { id: 5, name: "Food", amount: "", planType: "flexible", categoryKey: "food" },
  { id: 6, name: "Gas / Transportation", amount: "", planType: "flexible", categoryKey: "transportation" },
  { id: 7, name: "Subscriptions", amount: "", planType: "flexible", categoryKey: "subscriptions" },
  { id: 8, name: "Debt Payments", amount: "", planType: "fixed", categoryKey: "debt-payment" },
  { id: 9, name: "Health / Medical", amount: "", planType: "flexible", categoryKey: "health" },
  { id: 10, name: "Miscellaneous", amount: "", planType: "flexible", categoryKey: "other" },
];
const liteOnboardingCoreExpenseNames = new Set([
  "mortgage / rent",
  "rent / mortgage",
  "utilities",
  "insurance",
  "phone / internet",
]);
const liteExpenseQuickAdds = [
  { name: "Food", planType: "flexible", categoryKey: "food" },
  { name: "Gas / Transportation", planType: "flexible", categoryKey: "transportation" },
  { name: "Subscriptions", planType: "flexible", categoryKey: "subscriptions" },
  { name: "Debt Payments", planType: "fixed", categoryKey: "debt-payment" },
  { name: "Health / Medical", planType: "flexible", categoryKey: "health" },
  { name: "Miscellaneous", planType: "flexible", categoryKey: "other" },
];
const inferExpensePlanType = (name = "") => {
  const normalized = name.trim().toLowerCase();
  if (!normalized) return "flexible";

  const fixedKeywords = [
    "mortgage",
    "rent",
    "car payment",
    "loan",
    "insurance",
    "phone",
    "internet",
    "utilities",
    "electric",
    "water",
    "gas bill",
    "child care",
    "daycare",
    "subscription",
  ];

  return fixedKeywords.some((keyword) => normalized.includes(keyword)) ? "fixed" : "flexible";
};

const normalizeExpenses = (expenses = []) =>
  expenses.map((row, index) => {
    const { type, ...rest } = row || {};
    const categoryKey = rest.categoryKey || inferExpenseCategoryKey(rest.name);
    const sharedCategory = sharedBudgetCategories.find((category) => category.key === categoryKey);
    return {
      id: rest.id ?? index + 1,
      name: rest.name ?? "",
      amount: rest.amount ?? "",
      planType: rest.planType || sharedCategory?.defaultPlanType || inferExpensePlanType(rest.name),
      categoryKey,
    };
  });
const normalizeBudgetCategoryKey = (value = "") => {
  const normalized = value.trim().toLowerCase();
  if (!normalized) return "other";

  if (normalized.includes("mortgage") || normalized.includes("rent") || normalized.includes("housing")) return "housing";
  if (normalized.includes("utility") || normalized.includes("electric") || normalized.includes("water") || normalized.includes("power")) return "utilities";
  if (normalized.includes("insurance")) return "insurance";
  if (normalized.includes("phone") || normalized.includes("internet")) return "phone-internet";
  if (normalized.includes("food") || normalized.includes("grocery") || normalized.includes("meal") || normalized.includes("restaurant")) return "food";
  if (normalized.includes("transport") || normalized.includes("gas") || normalized.includes("fuel")) return "transportation";
  if (normalized.includes("subscription") || normalized.includes("stream")) return "subscriptions";
  if (normalized.includes("entertainment")) return "entertainment";
  if (normalized.includes("health") || normalized.includes("medical")) return "health";
  if (normalized.includes("debt")) return "debt-payment";
  if (normalized.includes("tax")) return "tax-payment";
  if (normalized.includes("income") || normalized.includes("paycheck")) return "income";
  if (normalized.includes("saving") || normalized.includes("transfer")) return "savings-transfer";
  return normalized;
};
const getSharedCategoryLabel = (key = "") => sharedBudgetCategories.find((category) => category.key === key)?.label || key.split("-").filter(Boolean).map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(" ") || "Other";
const inferExpenseCategoryKey = (name = "") => normalizeBudgetCategoryKey(name);
const getDefaultExpenseCategoryKey = (planType = "flexible") =>
  expenseCategoryOptions.find((category) => category.defaultPlanType === planType)?.key || "other";
const weekdayOptions = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const getCurrentMonthKey = () => getTodayInputValue().slice(0, 7);
const formatMonthLabel = (monthKey) => new Date(`${monthKey}-01T00:00:00`).toLocaleString("default", { month: "long", year: "numeric" });

const countWeekdayInMonth = (monthKey, weekdayName) => {
  if (!weekdayName) return null;

  const weekdayIndex = weekdayOptions.indexOf(weekdayName);
  if (weekdayIndex === -1) return null;

  const [year, month] = monthKey.split("-").map(Number);
  const cursor = new Date(year, month - 1, 1);
  const targetMonth = cursor.getMonth();
  let count = 0;

  while (cursor.getMonth() === targetMonth) {
    if (cursor.getDay() === weekdayIndex) count += 1;
    cursor.setDate(cursor.getDate() + 1);
  }

  return count;
};

const countBiweeklyPaychecksInMonth = (monthKey, anchorDate) => {
  if (!anchorDate) return null;

  const anchor = new Date(`${anchorDate}T00:00:00`);
  if (Number.isNaN(anchor.getTime())) return null;

  const [year, month] = monthKey.split("-").map(Number);
  const monthStart = new Date(year, month - 1, 1);
  const monthEnd = new Date(year, month, 0);
  const cursor = new Date(anchor);

  while (cursor > monthStart) {
    cursor.setDate(cursor.getDate() - 14);
  }

  while (cursor < monthStart) {
    cursor.setDate(cursor.getDate() + 14);
  }

  let count = 0;
  while (cursor <= monthEnd) {
    count += 1;
    cursor.setDate(cursor.getDate() + 14);
  }

  return count;
};

const getCalendarIncomePreview = ({ frequency, amount, monthKey, weekdayName, anchorDate, customRows = [] }) => {
  const parsedAmount = parseNumber(amount);

  if (frequency === "Weekly") {
    const paycheckCount = countWeekdayInMonth(monthKey, weekdayName);
    return paycheckCount === null ? { monthlyAmount: null, paycheckCount: null, usesAnchor: false } : { monthlyAmount: parsedAmount * paycheckCount, paycheckCount, usesAnchor: true };
  }

  if (frequency === "Bi-weekly") {
    const paycheckCount = countBiweeklyPaychecksInMonth(monthKey, anchorDate);
    return paycheckCount === null ? { monthlyAmount: null, paycheckCount: null, usesAnchor: false } : { monthlyAmount: parsedAmount * paycheckCount, paycheckCount, usesAnchor: true };
  }

  if (frequency === "Monthly") {
    return { monthlyAmount: parsedAmount, paycheckCount: 1, usesAnchor: true };
  }

  if (frequency === "Other") {
    const monthlyAmount = customRows
      .filter((row) => row.date && row.date.slice(0, 7) === monthKey)
      .reduce((sum, row) => sum + parseNumber(row.amount), 0);
    const paycheckCount = customRows.filter((row) => row.date && row.date.slice(0, 7) === monthKey && parseNumber(row.amount) > 0).length;
    return { monthlyAmount, paycheckCount, usesAnchor: paycheckCount > 0 };
  }

  return { monthlyAmount: parsedAmount, paycheckCount: null, usesAnchor: true };
};

const calculateMonthlyIncomeFromSchedule = (frequency, baseAmount, customRows = []) => {
  if (frequency === "Other") {
    const monthMap = {};
    customRows.forEach((row) => {
      if (!row.date) return;
      const key = row.date.slice(0, 7);
      const amount = parseNumber(row.amount);
      if (!amount) return;
      monthMap[key] = (monthMap[key] || 0) + amount;
    });

    const monthTotals = Object.entries(monthMap)
      .map(([key, total]) => ({
        key,
        total,
        label: new Date(`${key}-01`).toLocaleString("default", { month: "short", year: "numeric" }),
      }))
      .sort((a, b) => a.key.localeCompare(b.key));

    const totalGross = monthTotals.reduce((sum, month) => sum + month.total, 0);
    const validEntryCount = customRows.filter((row) => row.date && parseNumber(row.amount) > 0).length;

    return {
      monthly: monthTotals.length ? totalGross / monthTotals.length : 0,
      monthTotals,
      validEntryCount,
    };
  }

  return {
    monthly: toMonthly(parseNumber(baseAmount), frequency),
    monthTotals: [],
    validEntryCount: 0,
  };
};
const createInitialSettingsForm = (profile = {}, accountEmail = "") => ({
  fullName: profile?.fullName ?? "",
  preferredName: profile?.preferredName ?? "",
  dateOfBirth: profile?.dateOfBirth ?? "",
  pendingEmail: accountEmail,
  themeMode: profile?.preferences?.themeMode ?? "theme-dark-plum",
  accentColor: profile?.preferences?.accentColor ?? "plum",
});

const themeOptions = [
  { id: "theme-dark-plum", label: "Plum Night", tone: "Dark", description: "The original ultra-dark plum look." },
  { id: "theme-dark-midnight", label: "Midnight Slate", tone: "Dark", description: "Cooler dark panels with a calmer nighttime feel." },
  { id: "theme-light-pearl", label: "Pearl Light", tone: "Light", description: "Bright, soft surfaces with high readability." },
  { id: "theme-light-sand", label: "Sand Light", tone: "Light", description: "Warm light neutrals for a softer daytime feel." },
];

const accentOptions = [
  { id: "plum", label: "Plum" },
  { id: "ocean", label: "Ocean" },
  { id: "emerald", label: "Emerald" },
  { id: "sunrise", label: "Sunrise" },
];

const stateOptions = [
  ["AL", "Alabama"],
  ["AK", "Alaska"],
  ["AZ", "Arizona"],
  ["AR", "Arkansas"],
  ["CA", "California"],
  ["CO", "Colorado"],
  ["CT", "Connecticut"],
  ["DE", "Delaware"],
  ["FL", "Florida"],
  ["GA", "Georgia"],
  ["HI", "Hawaii"],
  ["ID", "Idaho"],
  ["IL", "Illinois"],
  ["IN", "Indiana"],
  ["IA", "Iowa"],
  ["KS", "Kansas"],
  ["KY", "Kentucky"],
  ["LA", "Louisiana"],
  ["ME", "Maine"],
  ["MD", "Maryland"],
  ["MA", "Massachusetts"],
  ["MI", "Michigan"],
  ["MN", "Minnesota"],
  ["MS", "Mississippi"],
  ["MO", "Missouri"],
  ["MT", "Montana"],
  ["NE", "Nebraska"],
  ["NV", "Nevada"],
  ["NH", "New Hampshire"],
  ["NJ", "New Jersey"],
  ["NM", "New Mexico"],
  ["NY", "New York"],
  ["NC", "North Carolina"],
  ["ND", "North Dakota"],
  ["OH", "Ohio"],
  ["OK", "Oklahoma"],
  ["OR", "Oregon"],
  ["PA", "Pennsylvania"],
  ["RI", "Rhode Island"],
  ["SC", "South Carolina"],
  ["SD", "South Dakota"],
  ["TN", "Tennessee"],
  ["TX", "Texas"],
  ["UT", "Utah"],
  ["VT", "Vermont"],
  ["VA", "Virginia"],
  ["WA", "Washington"],
  ["WV", "West Virginia"],
  ["WI", "Wisconsin"],
  ["WY", "Wyoming"],
  ["DC", "District of Columbia"],
].map(([code, label]) => ({ code, label }));

const noStateIncomeTaxStates = new Set(["AK", "FL", "NV", "NH", "SD", "TN", "TX", "WA", "WY"]);
const federalStandardDeduction2026 = {
  single: 16700,
  "married-joint": 33400,
  "married-separate": 16700,
  "head-of-household": 25050,
};
const federalBrackets2026 = {
  single: [
    { upTo: 12400, rate: 0.1 },
    { upTo: 50400, rate: 0.12 },
    { upTo: 107300, rate: 0.22 },
    { upTo: 204100, rate: 0.24 },
    { upTo: 259950, rate: 0.32 },
    { upTo: 627550, rate: 0.35 },
    { upTo: Infinity, rate: 0.37 },
  ],
  "married-joint": [
    { upTo: 24800, rate: 0.1 },
    { upTo: 100800, rate: 0.12 },
    { upTo: 214600, rate: 0.22 },
    { upTo: 408200, rate: 0.24 },
    { upTo: 519900, rate: 0.32 },
    { upTo: 751600, rate: 0.35 },
    { upTo: Infinity, rate: 0.37 },
  ],
  "married-separate": [
    { upTo: 12400, rate: 0.1 },
    { upTo: 50400, rate: 0.12 },
    { upTo: 107300, rate: 0.22 },
    { upTo: 204100, rate: 0.24 },
    { upTo: 259950, rate: 0.32 },
    { upTo: 375800, rate: 0.35 },
    { upTo: Infinity, rate: 0.37 },
  ],
  "head-of-household": [
    { upTo: 17700, rate: 0.1 },
    { upTo: 67400, rate: 0.12 },
    { upTo: 107300, rate: 0.22 },
    { upTo: 204100, rate: 0.24 },
    { upTo: 259950, rate: 0.32 },
    { upTo: 627550, rate: 0.35 },
    { upTo: Infinity, rate: 0.37 },
  ],
};
const socialSecurityRate = 0.062;
const socialSecurityWageBase2026 = 184500;
const medicareRate = 0.0145;
const additionalMedicareRate = 0.009;
const additionalMedicareThreshold2026 = {
  single: 200000,
  "married-joint": 250000,
  "married-separate": 125000,
  "head-of-household": 200000,
};

const getPaychecksPerYear = (frequency) => {
  if (frequency === "Weekly") return 52;
  if (frequency === "Bi-weekly") return 26;
  if (frequency === "Monthly") return 12;
  return null;
};

const estimateAnnualFederalTax = (taxableIncome, filingStatus) => {
  const brackets = federalBrackets2026[filingStatus] || federalBrackets2026.single;
  let tax = 0;
  let lowerBound = 0;
  let marginalRate = brackets[0].rate;

  for (const bracket of brackets) {
    if (taxableIncome <= lowerBound) break;
    const taxableAtRate = Math.min(taxableIncome, bracket.upTo) - lowerBound;
    if (taxableAtRate > 0) {
      tax += taxableAtRate * bracket.rate;
      marginalRate = bracket.rate;
    }
    lowerBound = bracket.upTo;
  }

  return { tax: Math.max(0, tax), marginalRate };
};

const estimatePayrollTaxesPerPaycheck = ({
  grossPerPaycheck,
  frequency,
  filingStatus,
  qualifyingChildren,
  otherDependents,
  extraFederalWithholding,
  residenceState,
  manualStateWithholdingAmount,
}) => {
  const paychecksPerYear = getPaychecksPerYear(frequency);
  const gross = parseNumber(grossPerPaycheck);
  if (!gross || !paychecksPerYear) {
    return {
      federal: 0,
      socialSecurity: 0,
      medicare: 0,
      additionalMedicare: 0,
      state: 0,
      total: 0,
      takeHome: 0,
      marginalFederalRate: 0,
      paychecksPerYear,
      annualGross: 0,
      noIncomeTaxState: noStateIncomeTaxStates.has(residenceState),
    };
  }

  const annualGross = gross * paychecksPerYear;
  const standardDeduction = federalStandardDeduction2026[filingStatus] ?? federalStandardDeduction2026.single;
  const annualTaxableIncome = Math.max(0, annualGross - standardDeduction);
  const federalEstimate = estimateAnnualFederalTax(annualTaxableIncome, filingStatus);
  const dependentCredit = Math.max(0, parseNumber(qualifyingChildren)) * 2000 + Math.max(0, parseNumber(otherDependents)) * 500;
  const annualFederalAfterCredits = Math.max(0, federalEstimate.tax - dependentCredit);
  const federal = annualFederalAfterCredits / paychecksPerYear + parseNumber(extraFederalWithholding);
  const socialSecurity = Math.min(gross, socialSecurityWageBase2026 / paychecksPerYear) * socialSecurityRate;
  const medicare = gross * medicareRate;
  const additionalMedicareThreshold = additionalMedicareThreshold2026[filingStatus] ?? additionalMedicareThreshold2026.single;
  const additionalMedicare = Math.max(0, annualGross - additionalMedicareThreshold) * additionalMedicareRate / paychecksPerYear;
  const state = noStateIncomeTaxStates.has(residenceState) ? 0 : Math.max(0, parseNumber(manualStateWithholdingAmount));
  const total = federal + socialSecurity + medicare + additionalMedicare + state;

  return {
    federal,
    socialSecurity,
    medicare,
    additionalMedicare,
    state,
    total,
    takeHome: Math.max(0, gross - total),
    marginalFederalRate: federalEstimate.marginalRate,
    paychecksPerYear,
    annualGross,
    noIncomeTaxState: noStateIncomeTaxStates.has(residenceState),
  };
};

const estimateGrossFromTakeHome = ({
  targetTakeHome,
  frequency,
  filingStatus,
  qualifyingChildren,
  otherDependents,
  extraFederalWithholding,
  residenceState,
  manualStateWithholdingAmount,
}) => {
  const target = parseNumber(targetTakeHome);
  const paychecksPerYear = getPaychecksPerYear(frequency);
  if (!target || !paychecksPerYear) return { gross: target, withholding: 0, payroll: estimatePayrollTaxesPerPaycheck({ grossPerPaycheck: target, frequency, filingStatus, qualifyingChildren, otherDependents, extraFederalWithholding, residenceState, manualStateWithholdingAmount }) };

  let low = target;
  let high = Math.max(target * 2, target + 1000);
  let bestGross = high;
  let bestPayroll = estimatePayrollTaxesPerPaycheck({
    grossPerPaycheck: high,
    frequency,
    filingStatus,
    qualifyingChildren,
    otherDependents,
    extraFederalWithholding,
    residenceState,
    manualStateWithholdingAmount,
  });

  while (bestPayroll.takeHome < target && high < target * 5) {
    high *= 1.2;
    bestGross = high;
    bestPayroll = estimatePayrollTaxesPerPaycheck({
      grossPerPaycheck: high,
      frequency,
      filingStatus,
      qualifyingChildren,
      otherDependents,
      extraFederalWithholding,
      residenceState,
      manualStateWithholdingAmount,
    });
  }

  for (let index = 0; index < 24; index += 1) {
    const mid = (low + high) / 2;
    const payroll = estimatePayrollTaxesPerPaycheck({
      grossPerPaycheck: mid,
      frequency,
      filingStatus,
      qualifyingChildren,
      otherDependents,
      extraFederalWithholding,
      residenceState,
      manualStateWithholdingAmount,
    });

    bestGross = mid;
    bestPayroll = payroll;
    if (payroll.takeHome > target) {
      high = mid;
    } else {
      low = mid;
    }
  }

  return { gross: bestGross, withholding: bestPayroll.total, payroll: bestPayroll };
};

const createDefaultTaxProfile = (incomeType = INCOME_TYPES.W2) => {
  if (incomeType === INCOME_TYPES.SELF_EMPLOYED || incomeType === INCOME_TYPES.BOTH) {
    return {
      entityType: "sole-prop-llc",
      paymentStyle: "estimated-quarterly",
      w2ResidenceState: "FL",
      w2FilingStatus: "single",
      w2QualifyingChildren: "",
      w2OtherDependents: "",
      w2ExtraFederalWithholding: "",
      w2StateWithholdingAmount: "",
    };
  }

  return {
    entityType: "w2-only",
    paymentStyle: "withholding",
    w2ResidenceState: "FL",
    w2FilingStatus: "single",
    w2QualifyingChildren: "",
    w2OtherDependents: "",
    w2ExtraFederalWithholding: "",
    w2StateWithholdingAmount: "",
  };
};

const taxEntityOptions = [
  { id: "w2-only", label: "W-2 Employee Only", description: "Taxes are usually handled through employer withholding." },
  { id: "sole-prop-llc", label: "Sole Proprietor / Single-Member LLC", description: "Best for self-employed owners making personal estimated payments." },
  { id: "partnership-llc", label: "Partnership / Multi-Member LLC", description: "Useful when owners may still need personal estimated tax planning." },
  { id: "s-corp-owner", label: "S-Corp Owner", description: "Helpful when salary withholding and owner distributions both matter." },
  { id: "c-corp", label: "C-Corp / Corporation", description: "Corporate taxes are usually handled separately from this personal planner." },
  { id: "not-sure", label: "Not Sure Yet", description: "Use this if you want a planning estimate while you figure out your tax setup." },
];

const taxPaymentOptions = [
  { id: "withholding", label: "Payroll / Employer Withholding", description: "Use this when taxes are mostly being withheld already." },
  { id: "estimated-quarterly", label: "I Pay Estimated Taxes", description: "Use due dates and set-aside guidance for owner-paid estimated taxes." },
  { id: "business-handled", label: "Business Handles Taxes Separately", description: "Use this when the business or accountant handles tax planning outside this app." },
  { id: "planning-only", label: "Planning Estimate Only", description: "See annual and monthly estimates without due-date guidance." },
];

export default function BudgetDashboard({
  initialState = {},
  accountEmail,
  accountEmailVerified,
  accountNotice = "",
  userProfile,
  saveStatus,
  onLogout,
  onBudgetStateChange,
  onProfileUpdate,
  onSendPasswordReset,
  onSendEmailVerification,
  onRequestEmailChange,
  onRefreshAccountStatus,
  isDemoMode = false,
  productMode = "full",
  onPrimaryAccountAction = null,
  primaryAccountActionLabel = "Log Out",
  isAdmin = false,
  onOpenAdminConsole = null,
}) {
  const seed = initialState || {};
  const [activeTab, setActiveTab] = useState("control");
  const [liteOnboardingComplete, setLiteOnboardingComplete] = useState(Boolean(seed.liteOnboardingComplete));
  const [demoLiteComplete, setDemoLiteComplete] = useState(false);
  const [liteOnboardingStep, setLiteOnboardingStep] = useState(Boolean(seed.liteOnboardingComplete) ? "done" : "welcome");
  const [showLiteUpgradePrompt, setShowLiteUpgradePrompt] = useState(false);
  const [selectedLiteUpgrade, setSelectedLiteUpgrade] = useState(null);
  const [showLiteAdditionalIncomeInputs, setShowLiteAdditionalIncomeInputs] = useState(
    Boolean((seed.additionalIncomes || []).some(hasAdditionalIncomeContent))
  );
  const [incomeType, setIncomeType] = useState(seed.incomeType ?? INCOME_TYPES.W2);
  const [incomeFrequency, setIncomeFrequency] = useState(seed.incomeFrequency ?? "Weekly");
  const [baseIncome, setBaseIncome] = useState(seed.baseIncome ?? "");
  const [taxPercent, setTaxPercent] = useState(seed.taxPercent ?? "");
  const [writeOffMode, setWriteOffMode] = useState(seed.writeOffMode ?? "medium");
  const [customWriteOffPercent, setCustomWriteOffPercent] = useState(seed.customWriteOffPercent ?? 35);
  const [customPayDates, setCustomPayDates] = useState(seed.customPayDates ?? createDefaultCustomPayDates());
  const [primaryPayday, setPrimaryPayday] = useState(seed.primaryPayday ?? "Friday");
  const [primaryLastPaycheckDate, setPrimaryLastPaycheckDate] = useState(seed.primaryLastPaycheckDate ?? "");
  const [incomePreviewMonth, setIncomePreviewMonth] = useState(seed.incomePreviewMonth ?? getCurrentMonthKey());
  const [mixedIncome, setMixedIncome] = useState({ ...createInitialMixedIncome(), ...(seed.mixedIncome ?? {}), contractorCustomPayDates: seed?.mixedIncome?.contractorCustomPayDates?.length > 0 ? seed.mixedIncome.contractorCustomPayDates : createDefaultCustomPayDates() });
  const [deductions, setDeductions] = useState(seed.deductions ?? createDefaultDeductions());
  const [additionalIncomes, setAdditionalIncomes] = useState(removeEmptyAdditionalIncomeRows(seed.additionalIncomes ?? []));
  const [expenses, setExpenses] = useState(normalizeExpenses(seed.expenses ?? createDefaultExpenses()));
  const [transactions, setTransactions] = useState(seed.transactions ?? createDefaultTransactions());
  const [transactionForm, setTransactionForm] = useState(seed.transactionForm ?? createInitialTransactionForm());
  const [transactionMonth, setTransactionMonth] = useState(seed.transactionMonth ?? getTodayInputValue().slice(0, 7));
  const [transactionMessage, setTransactionMessage] = useState("");
  const [transactionTone, setTransactionTone] = useState("idle");
  const [editingTransactionId, setEditingTransactionId] = useState(null);
  const [transactionEntryMode, setTransactionEntryMode] = useState("quick");
  const [debts, setDebts] = useState(seed.debts ?? [
    { id: 1, name: "Credit Card", type: "Credit Card", amount: "", balance: "", interest: "" },
    { id: 2, name: "Auto Loan", type: "Loan", amount: "", balance: "", interest: "" },
  ]);
  const [customGoals, setCustomGoals] = useState(seed.customGoals ?? []);
  const [goalForm, setGoalForm] = useState(seed.goalForm ?? createInitialGoalForm());
  const [timelineFocus, setTimelineFocus] = useState(seed.timelineFocus ?? "");
  const [debtStrategyTarget, setDebtStrategyTarget] = useState(seed.debtStrategyTarget ?? "all");
  const [debtStrategyMethod, setDebtStrategyMethod] = useState(seed.debtStrategyMethod ?? "snowball");
  const [debtStrategyExtraAmount, setDebtStrategyExtraAmount] = useState(seed.debtStrategyExtraAmount ?? "");
  const [taxProfile, setTaxProfile] = useState(seed.taxProfile ?? createDefaultTaxProfile(seed.incomeType ?? INCOME_TYPES.W2));
  const [settingsForm, setSettingsForm] = useState(createInitialSettingsForm(userProfile, accountEmail));
  const [settingsBusy, setSettingsBusy] = useState(false);
  const [settingsMessage, setSettingsMessage] = useState("");
  const [settingsTone, setSettingsTone] = useState("idle");
  const activeThemeMode = settingsForm.themeMode || userProfile?.preferences?.themeMode || "theme-dark-plum";
  const activeAccentColor = settingsForm.accentColor || userProfile?.preferences?.accentColor || "plum";
  const displayName = settingsForm.preferredName || settingsForm.fullName || userProfile?.preferredName || userProfile?.fullName || accountEmail;
  const isLiteMode = productMode === "lite";
  const effectiveLiteOnboardingComplete = isLiteMode ? (isDemoMode ? demoLiteComplete : liteOnboardingComplete) : true;
  const shouldShowLiteOnboarding = isLiteMode && !effectiveLiteOnboardingComplete;
  const isBothIncomeType = incomeType === INCOME_TYPES.BOTH;
  const isW2 = incomeType === INCOME_TYPES.W2;
  const hasMixedIncomeSeed =
    parseNumber(mixedIncome.w2BaseIncome) > 0 ||
    parseNumber(mixedIncome.w2HourlyRate) > 0 ||
    parseNumber(mixedIncome.w2ActualHours) > 0 ||
    parseNumber(mixedIncome.w2TypicalHours) > 0 ||
    parseNumber(mixedIncome.contractorBaseIncome) > 0 ||
    parseNumber(mixedIncome.w2WithholdingPercent) > 0 ||
    parseNumber(mixedIncome.w2WithholdingAmount) > 0 ||
    parseNumber(mixedIncome.contractorTaxPercent) > 0 ||
    mixedIncome.contractorCustomPayDates.some((row) => row.date && parseNumber(row.amount) > 0);
  const isFirstBudgetSession = !baseIncome && !taxPercent && !hasMixedIncomeSeed && !expenses.some((row) => parseNumber(row.amount) > 0) && !debts.some((row) => parseNumber(row.amount) > 0 || parseNumber(row.balance) > 0) && customGoals.length === 0 && additionalIncomes.every((row) => !parseNumber(row.amount)) && transactions.length === 0;
  const setupChecklist = {
    income: !!(baseIncome || hasMixedIncomeSeed),
    plannedExpenses: expenses.some((row) => parseNumber(row.amount) > 0),
    debt: debts.some((row) => parseNumber(row.amount) > 0 || parseNumber(row.balance) > 0),
    transactions: transactions.length > 0,
  };
  const incompleteSetupCount = Object.values(setupChecklist).filter((value) => !value).length;
  const shouldShowSetupGuidance = isFirstBudgetSession || incompleteSetupCount >= 2;
  const welcomeMessage = isDemoMode
    ? "Try the demo, then create your account to save your own numbers."
    : shouldShowSetupGuidance
    ? "Welcome back. Start with income, then add your monthly bills so the app can show what is really left."
    : `Welcome back, ${displayName}.`;
  const shellWelcomeMessage = shouldShowLiteOnboarding
    ? "Let’s get a clear picture of your money in under 2 minutes."
    : isLiteMode
    ? "See what is left this month at a glance."
    : welcomeMessage;
  const accountPanelTitle = isDemoMode ? "Demo" : "Account";
  const savePanelTitle = isDemoMode ? "Session" : "Cloud Save";
  const savePanelStatus = isDemoMode
    ? "Nothing saves"
    : settingsBusy || saveStatus === "saving"
    ? "Saving..."
    : saveStatus === "saved"
    ? "Saved"
    : saveStatus === "error"
    ? "Save issue"
    : "Ready";

  const setupChecklistItems = [
    {
      id: "income",
      label: "Add income details",
      complete: setupChecklist.income,
      actionLabel: "Open Income Setup",
      tab: "setup",
      description: "Set up how money comes in first so the app can calculate what is really left this month.",
    },
    {
      id: "plannedExpenses",
      label: "Add monthly bills",
      complete: setupChecklist.plannedExpenses,
      actionLabel: "Open Expense Planning",
      tab: "expenses",
      description: "Enter your regular monthly bills and planned spending targets before you trust the leftover number.",
    },
    {
      id: "debt",
      label: "Add debt payments",
      complete: setupChecklist.debt,
      actionLabel: "Open Debt Planning",
      tab: "expenses",
      description: "Track credit cards and loans so payoff guidance and real monthly pressure are accurate.",
    },
    {
      id: "transactions",
      label: "Add real transactions",
      complete: setupChecklist.transactions,
      actionLabel: "Open Transactions",
      tab: "transactions",
      description: "Log real spending and deposits so the app can compare your plan against what actually happened.",
    },
  ];
  const nextSetupItem = setupChecklistItems.find((item) => !item.complete) ?? null;
  const shouldShowTransactionReminder = !shouldShowSetupGuidance && !setupChecklist.transactions;

  const selectedTaxEntity = taxProfile?.entityType ?? createDefaultTaxProfile(incomeType).entityType;
  const selectedTaxPaymentStyle = taxProfile?.paymentStyle ?? createDefaultTaxProfile(incomeType).paymentStyle;
  const entityUsesWriteOffEstimate = ["sole-prop-llc", "partnership-llc", "s-corp-owner", "not-sure"].includes(selectedTaxEntity);
  const estimatedPlannerEligible = ["sole-prop-llc", "partnership-llc", "s-corp-owner", "not-sure"].includes(selectedTaxEntity);
  const taxReserveEnabled = estimatedPlannerEligible && selectedTaxPaymentStyle === "estimated-quarterly";
  const withholdingHandlesTaxes = selectedTaxPaymentStyle === "withholding" && !isBothIncomeType;
  const businessTaxesHandledSeparately = selectedTaxPaymentStyle === "business-handled" || selectedTaxEntity === "c-corp";

  const w2InputMode = (isW2 || isBothIncomeType) ? mixedIncome.w2InputMode || "gross" : "gross";
  const w2PayStructure = (isW2 || isBothIncomeType) ? mixedIncome.w2PayStructure || "fixed" : "fixed";
  const activeW2Frequency = isBothIncomeType ? mixedIncome.w2Frequency : incomeFrequency;
  const activeW2ResidenceState = taxProfile?.w2ResidenceState || "FL";
  const activeW2FilingStatus = taxProfile?.w2FilingStatus || "single";
  const activeW2TypicalHours = parseNumber(mixedIncome.w2TypicalHours);
  const activeW2ActualHours = parseNumber(mixedIncome.w2ActualHours);
  const activeW2OvertimeHours = parseNumber(mixedIncome.w2OvertimeHours);
  const activeW2OvertimeMultiplier = parseNumber(mixedIncome.w2OvertimeMultiplier) || 1.5;
  const activeW2HourlyRate = parseNumber(mixedIncome.w2HourlyRate);
  const computedW2GrossPerPaycheck = w2PayStructure === "hourly" && activeW2Frequency !== "Other"
    ? activeW2HourlyRate * (activeW2ActualHours || activeW2TypicalHours) + activeW2HourlyRate * activeW2OvertimeHours * activeW2OvertimeMultiplier
    : null;
  const activeManualStateWithholdingAmount = taxProfile?.w2StateWithholdingAmount || "";
  const autoPayrollFromHourlyGross = estimatePayrollTaxesPerPaycheck({
    grossPerPaycheck: computedW2GrossPerPaycheck,
    frequency: activeW2Frequency,
    filingStatus: activeW2FilingStatus,
    qualifyingChildren: taxProfile?.w2QualifyingChildren,
    otherDependents: taxProfile?.w2OtherDependents,
    extraFederalWithholding: taxProfile?.w2ExtraFederalWithholding,
    residenceState: activeW2ResidenceState,
    manualStateWithholdingAmount: activeManualStateWithholdingAmount,
  });
  const fixedW2TakeHomeGrossEstimate = estimateGrossFromTakeHome({
    targetTakeHome: isBothIncomeType ? mixedIncome.w2BaseIncome : baseIncome,
    frequency: activeW2Frequency,
    filingStatus: activeW2FilingStatus,
    qualifyingChildren: taxProfile?.w2QualifyingChildren,
    otherDependents: taxProfile?.w2OtherDependents,
    extraFederalWithholding: taxProfile?.w2ExtraFederalWithholding,
    residenceState: activeW2ResidenceState,
    manualStateWithholdingAmount: activeManualStateWithholdingAmount,
  });
  const activeW2EnteredPaycheckAmount =
    w2PayStructure === "hourly" && activeW2Frequency !== "Other"
      ? w2InputMode === "take-home"
        ? autoPayrollFromHourlyGross.takeHome
        : computedW2GrossPerPaycheck
      : isBothIncomeType
      ? mixedIncome.w2BaseIncome
      : baseIncome;
  const activeW2GrossPaycheckAmount =
    w2PayStructure === "hourly" && activeW2Frequency !== "Other"
      ? computedW2GrossPerPaycheck
      : w2InputMode === "take-home"
      ? fixedW2TakeHomeGrossEstimate.gross
      : parseNumber(activeW2EnteredPaycheckAmount);
  const primaryIncomeResult = calculateMonthlyIncomeFromSchedule(incomeFrequency, isW2 ? activeW2EnteredPaycheckAmount : baseIncome, customPayDates);
  const mixedW2Result = calculateMonthlyIncomeFromSchedule(mixedIncome.w2Frequency, activeW2EnteredPaycheckAmount, []);
  const mixedContractorResult = calculateMonthlyIncomeFromSchedule(mixedIncome.contractorFrequency, mixedIncome.contractorBaseIncome, mixedIncome.contractorCustomPayDates);
  const currentMonthKey = getCurrentMonthKey();
  const currentMonthLabel = formatMonthLabel(currentMonthKey);
  const transactionMonthLabel = formatMonthLabel(transactionMonth);
  const previewMonthLabel = formatMonthLabel(incomePreviewMonth);

  const soloAverageMonthly = primaryIncomeResult.monthly;
  const averageW2EnteredMonthly = isBothIncomeType ? mixedW2Result.monthly : isW2 ? soloAverageMonthly : 0;
  const averageContractorGrossMonthly = isBothIncomeType ? mixedContractorResult.monthly : isW2 ? 0 : soloAverageMonthly;
  const monthTotals = isBothIncomeType ? mixedContractorResult.monthTotals : primaryIncomeResult.monthTotals;
  const validCustomPayDateCount = isBothIncomeType ? mixedContractorResult.validEntryCount : primaryIncomeResult.validEntryCount;

  const currentPrimaryCalendar = getCalendarIncomePreview({
    frequency: incomeFrequency,
    amount: isW2 ? activeW2EnteredPaycheckAmount : baseIncome,
    monthKey: currentMonthKey,
    weekdayName: primaryPayday,
    anchorDate: primaryLastPaycheckDate,
    customRows: customPayDates,
  });
  const previewPrimaryCalendar = getCalendarIncomePreview({
    frequency: incomeFrequency,
    amount: isW2 ? activeW2EnteredPaycheckAmount : baseIncome,
    monthKey: incomePreviewMonth,
    weekdayName: primaryPayday,
    anchorDate: primaryLastPaycheckDate,
    customRows: customPayDates,
  });
  const currentW2Calendar = getCalendarIncomePreview({
    frequency: mixedIncome.w2Frequency,
    amount: activeW2EnteredPaycheckAmount,
    monthKey: currentMonthKey,
    weekdayName: mixedIncome.w2Payday,
    anchorDate: mixedIncome.w2LastPaycheckDate,
    customRows: [],
  });
  const previewW2Calendar = getCalendarIncomePreview({
    frequency: mixedIncome.w2Frequency,
    amount: activeW2EnteredPaycheckAmount,
    monthKey: incomePreviewMonth,
    weekdayName: mixedIncome.w2Payday,
    anchorDate: mixedIncome.w2LastPaycheckDate,
    customRows: [],
  });
  const currentContractorCalendar = getCalendarIncomePreview({
    frequency: isBothIncomeType ? mixedIncome.contractorFrequency : incomeFrequency,
    amount: isBothIncomeType ? mixedIncome.contractorBaseIncome : baseIncome,
    monthKey: currentMonthKey,
    weekdayName: isBothIncomeType ? mixedIncome.contractorPayday : primaryPayday,
    anchorDate: isBothIncomeType ? mixedIncome.contractorLastPaycheckDate : primaryLastPaycheckDate,
    customRows: isBothIncomeType ? mixedIncome.contractorCustomPayDates : customPayDates,
  });
  const previewContractorCalendar = getCalendarIncomePreview({
    frequency: isBothIncomeType ? mixedIncome.contractorFrequency : incomeFrequency,
    amount: isBothIncomeType ? mixedIncome.contractorBaseIncome : baseIncome,
    monthKey: incomePreviewMonth,
    weekdayName: isBothIncomeType ? mixedIncome.contractorPayday : primaryPayday,
    anchorDate: isBothIncomeType ? mixedIncome.contractorLastPaycheckDate : primaryLastPaycheckDate,
    customRows: isBothIncomeType ? mixedIncome.contractorCustomPayDates : customPayDates,
  });

  const currentW2EnteredMonthly = isBothIncomeType
    ? currentW2Calendar.monthlyAmount ?? averageW2EnteredMonthly
    : isW2
    ? currentPrimaryCalendar.monthlyAmount ?? soloAverageMonthly
    : 0;
  const currentContractorGrossMonthly = isBothIncomeType
    ? currentContractorCalendar.monthlyAmount ?? averageContractorGrossMonthly
    : isW2
    ? 0
    : currentPrimaryCalendar.monthlyAmount ?? soloAverageMonthly;
  const previewW2EnteredMonthly = isBothIncomeType
    ? previewW2Calendar.monthlyAmount ?? averageW2EnteredMonthly
    : isW2
    ? previewPrimaryCalendar.monthlyAmount ?? soloAverageMonthly
    : 0;
  const previewContractorGrossMonthly = isBothIncomeType
    ? previewContractorCalendar.monthlyAmount ?? averageContractorGrossMonthly
    : isW2
    ? 0
    : previewPrimaryCalendar.monthlyAmount ?? soloAverageMonthly;

  const primaryTaxRate = parseNumber(taxPercent) / 100 || 0;
  const autoW2PayrollEstimate = estimatePayrollTaxesPerPaycheck({
    grossPerPaycheck: activeW2GrossPaycheckAmount,
    frequency: activeW2Frequency,
    filingStatus: activeW2FilingStatus,
    qualifyingChildren: taxProfile?.w2QualifyingChildren,
    otherDependents: taxProfile?.w2OtherDependents,
    extraFederalWithholding: taxProfile?.w2ExtraFederalWithholding,
    residenceState: activeW2ResidenceState,
    manualStateWithholdingAmount: activeManualStateWithholdingAmount,
  });
  const w2WithholdingRate = activeW2GrossPaycheckAmount > 0 ? autoW2PayrollEstimate.total / activeW2GrossPaycheckAmount : 0;
  const contractorTaxRate = isBothIncomeType ? parseNumber(mixedIncome.contractorTaxPercent) / 100 || 0 : primaryTaxRate;
  const averageW2WithholdingAmountMonthly = isBothIncomeType
    ? toMonthly(w2InputMode === "take-home" && w2PayStructure !== "hourly" && parseNumber(mixedIncome.w2WithholdingAmount) > 0 ? parseNumber(mixedIncome.w2WithholdingAmount) : autoW2PayrollEstimate.total, mixedIncome.w2Frequency)
    : isW2
    ? incomeFrequency === "Other"
      ? parseNumber(mixedIncome.w2WithholdingAmount) || autoW2PayrollEstimate.total
      : toMonthly(w2InputMode === "take-home" && w2PayStructure !== "hourly" && parseNumber(mixedIncome.w2WithholdingAmount) > 0 ? parseNumber(mixedIncome.w2WithholdingAmount) : autoW2PayrollEstimate.total, incomeFrequency)
    : 0;
  const currentW2PaycheckCount = isBothIncomeType
    ? currentW2Calendar.paycheckCount
    : isW2
    ? currentPrimaryCalendar.paycheckCount
    : null;
  const previewW2PaycheckCount = isBothIncomeType
    ? previewW2Calendar.paycheckCount
    : isW2
    ? previewPrimaryCalendar.paycheckCount
    : null;
  const currentW2WithholdingAmountMonthly = isBothIncomeType
    ? mixedIncome.w2Frequency === "Other"
      ? currentW2Calendar.monthlyAmount > 0
        ? (parseNumber(mixedIncome.w2WithholdingAmount) || autoW2PayrollEstimate.total)
        : 0
      : currentW2PaycheckCount !== null
      ? (w2InputMode === "take-home" && w2PayStructure !== "hourly" && parseNumber(mixedIncome.w2WithholdingAmount) > 0 ? parseNumber(mixedIncome.w2WithholdingAmount) : autoW2PayrollEstimate.total) * currentW2PaycheckCount
      : averageW2WithholdingAmountMonthly
    : isW2
    ? incomeFrequency === "Other"
      ? currentPrimaryCalendar.monthlyAmount > 0
        ? (parseNumber(mixedIncome.w2WithholdingAmount) || autoW2PayrollEstimate.total)
        : 0
      : currentW2PaycheckCount !== null
      ? (w2InputMode === "take-home" && w2PayStructure !== "hourly" && parseNumber(mixedIncome.w2WithholdingAmount) > 0 ? parseNumber(mixedIncome.w2WithholdingAmount) : autoW2PayrollEstimate.total) * currentW2PaycheckCount
      : averageW2WithholdingAmountMonthly
    : 0;
  const previewW2WithholdingAmountMonthly = isBothIncomeType
    ? mixedIncome.w2Frequency === "Other"
      ? previewW2Calendar.monthlyAmount > 0
        ? (parseNumber(mixedIncome.w2WithholdingAmount) || autoW2PayrollEstimate.total)
        : 0
      : previewW2PaycheckCount !== null
      ? (w2InputMode === "take-home" && w2PayStructure !== "hourly" && parseNumber(mixedIncome.w2WithholdingAmount) > 0 ? parseNumber(mixedIncome.w2WithholdingAmount) : autoW2PayrollEstimate.total) * previewW2PaycheckCount
      : averageW2WithholdingAmountMonthly
    : isW2
    ? incomeFrequency === "Other"
      ? previewPrimaryCalendar.monthlyAmount > 0
        ? (parseNumber(mixedIncome.w2WithholdingAmount) || autoW2PayrollEstimate.total)
        : 0
      : previewW2PaycheckCount !== null
      ? (w2InputMode === "take-home" && w2PayStructure !== "hourly" && parseNumber(mixedIncome.w2WithholdingAmount) > 0 ? parseNumber(mixedIncome.w2WithholdingAmount) : autoW2PayrollEstimate.total) * previewW2PaycheckCount
      : averageW2WithholdingAmountMonthly
    : 0;
  const averageTaxWithheldMonthly = isBothIncomeType
    ? w2InputMode === "take-home"
      ? averageW2WithholdingAmountMonthly
      : averageW2EnteredMonthly * w2WithholdingRate
    : withholdingHandlesTaxes
    ? isW2 && w2InputMode === "take-home"
      ? averageW2WithholdingAmountMonthly
      : soloAverageMonthly * w2WithholdingRate
    : 0;
  const currentTaxWithheldMonthly = isBothIncomeType
    ? w2InputMode === "take-home"
      ? currentW2WithholdingAmountMonthly
      : currentW2EnteredMonthly * w2WithholdingRate
    : withholdingHandlesTaxes
    ? isW2 && w2InputMode === "take-home"
      ? currentW2WithholdingAmountMonthly
      : currentW2EnteredMonthly * w2WithholdingRate
    : 0;
  const autoW2FederalWithholdingPerPaycheck = autoW2PayrollEstimate.federal;
  const autoW2SocialSecurityPerPaycheck = autoW2PayrollEstimate.socialSecurity;
  const autoW2MedicarePerPaycheck = autoW2PayrollEstimate.medicare + autoW2PayrollEstimate.additionalMedicare;
  const autoW2StateWithholdingPerPaycheck = autoW2PayrollEstimate.state;
  const autoW2TakeHomePerPaycheck = autoW2PayrollEstimate.takeHome;
  const autoW2GrossDisplayPerPaycheck = activeW2GrossPaycheckAmount;
  const autoW2MarginalRateLabel = autoW2PayrollEstimate.marginalFederalRate > 0 ? `${Math.round(autoW2PayrollEstimate.marginalFederalRate * 100)}% federal bracket` : "No federal withholding estimate yet";
  const previewTaxWithheldMonthly = isBothIncomeType
    ? w2InputMode === "take-home"
      ? previewW2WithholdingAmountMonthly
      : previewW2EnteredMonthly * w2WithholdingRate
    : withholdingHandlesTaxes
    ? isW2 && w2InputMode === "take-home"
      ? previewW2WithholdingAmountMonthly
      : previewW2EnteredMonthly * w2WithholdingRate
    : 0;
  const averageW2GrossMonthly = isBothIncomeType
    ? w2InputMode === "take-home"
      ? averageW2EnteredMonthly + averageTaxWithheldMonthly
      : averageW2EnteredMonthly
    : isW2
    ? w2InputMode === "take-home"
      ? soloAverageMonthly + averageTaxWithheldMonthly
      : soloAverageMonthly
    : 0;
  const currentW2GrossMonthly = isBothIncomeType
    ? w2InputMode === "take-home"
      ? currentW2EnteredMonthly + currentTaxWithheldMonthly
      : currentW2EnteredMonthly
    : isW2
    ? w2InputMode === "take-home"
      ? currentW2EnteredMonthly + currentTaxWithheldMonthly
      : currentW2EnteredMonthly
    : 0;
  const previewW2GrossMonthly = isBothIncomeType
    ? w2InputMode === "take-home"
      ? previewW2EnteredMonthly + previewTaxWithheldMonthly
      : previewW2EnteredMonthly
    : isW2
    ? w2InputMode === "take-home"
      ? previewW2EnteredMonthly + previewTaxWithheldMonthly
      : previewW2EnteredMonthly
    : 0;
  const averageW2PlanningMonthly = isBothIncomeType
    ? w2InputMode === "take-home"
      ? averageW2EnteredMonthly
      : Math.max(0, averageW2EnteredMonthly - averageTaxWithheldMonthly)
    : isW2
    ? w2InputMode === "take-home"
      ? soloAverageMonthly
      : Math.max(0, soloAverageMonthly - averageTaxWithheldMonthly)
    : 0;
  const currentW2PlanningMonthly = isBothIncomeType
    ? w2InputMode === "take-home"
      ? currentW2EnteredMonthly
      : Math.max(0, currentW2EnteredMonthly - currentTaxWithheldMonthly)
    : isW2
    ? w2InputMode === "take-home"
      ? currentW2EnteredMonthly
      : Math.max(0, currentW2EnteredMonthly - currentTaxWithheldMonthly)
    : 0;
  const averageGrossMonthly = isBothIncomeType ? averageW2GrossMonthly + averageContractorGrossMonthly : isW2 ? averageW2GrossMonthly : soloAverageMonthly;
  const currentGrossMonthly = isBothIncomeType ? currentW2GrossMonthly + currentContractorGrossMonthly : isW2 ? currentW2GrossMonthly : currentContractorGrossMonthly;
  const previewGrossMonthly = isBothIncomeType ? previewW2GrossMonthly + previewContractorGrossMonthly : isW2 ? previewW2GrossMonthly : previewContractorGrossMonthly;

  const contractorFrequencyForPlanning = isBothIncomeType ? mixedIncome.contractorFrequency : incomeFrequency;
  const averageContractorPaychecksPerMonth = contractorFrequencyForPlanning === "Weekly" ? 52 / 12 : contractorFrequencyForPlanning === "Bi-weekly" ? 26 / 12 : contractorFrequencyForPlanning === "Monthly" ? 1 : monthTotals.length > 0 ? validCustomPayDateCount / monthTotals.length : null;
  const currentContractorPaychecksPerMonth = currentContractorCalendar.paycheckCount ?? averageContractorPaychecksPerMonth;
  const paychecksPerMonth = isBothIncomeType ? (currentW2PaycheckCount || currentContractorPaychecksPerMonth) : currentContractorPaychecksPerMonth;

  const additionalMonthly = additionalIncomes.reduce((sum, row) => {
    const amount = parseNumber(row.amount);
    return amount ? sum + toMonthly(amount, row.frequency) : sum;
  }, 0);

  const averageIncomeBeforeDeductions = isBothIncomeType
    ? averageW2PlanningMonthly + averageContractorGrossMonthly + additionalMonthly
    : isW2
    ? averageW2PlanningMonthly + additionalMonthly
    : averageGrossMonthly + additionalMonthly;
  const currentIncomeBeforeDeductions = isBothIncomeType
    ? currentW2PlanningMonthly + currentContractorGrossMonthly + additionalMonthly
    : isW2
    ? currentW2PlanningMonthly + additionalMonthly
    : currentGrossMonthly + additionalMonthly;
  const otherDeductionsMonthly = deductions.reduce((sum, deduction) => {
    const value = parseNumber(deduction.value);
    if (!value) return sum;
    return deduction.mode === "%" ? sum + currentGrossMonthly * (value / 100) : sum + value;
  }, 0);

  const spendableIncomeBeforeTaxReserve = currentIncomeBeforeDeductions - otherDeductionsMonthly;
  const averageSpendableIncomeBeforeTaxReserve = averageIncomeBeforeDeductions - otherDeductionsMonthly;
  const writeOffDefaults = { low: 15, medium: 35, high: 60 };
  const activeWriteOffMode = isBothIncomeType ? mixedIncome.contractorWriteOffMode : writeOffMode;
  const activeCustomWriteOffPercent = isBothIncomeType ? mixedIncome.contractorCustomWriteOffPercent : customWriteOffPercent;
  const effectiveWriteOffPercent = Math.min(Math.max(activeWriteOffMode === "custom" ? parseNumber(activeCustomWriteOffPercent) : writeOffDefaults[activeWriteOffMode] || 0, 0), 90);
  const businessIncomeAnnual = averageContractorGrossMonthly * 12;
  const additionalIncomeAnnual = additionalMonthly * 12;
  const businessTaxableAnnual = entityUsesWriteOffEstimate ? Math.max(0, businessIncomeAnnual * (1 - effectiveWriteOffPercent / 100)) : 0;
  const estimatedTaxableIncome = entityUsesWriteOffEstimate ? businessTaxableAnnual + (isBothIncomeType ? 0 : additionalIncomeAnnual) : 0;
  const estimatedAnnualTax = estimatedTaxableIncome > 0 ? estimatedTaxableIncome * contractorTaxRate : 0;
  const taxSavingsTargetMonthly = taxReserveEnabled ? estimatedAnnualTax / 12 : 0;
  const currentTaxableContractorMonthly = entityUsesWriteOffEstimate ? Math.max(0, currentContractorGrossMonthly * (1 - effectiveWriteOffPercent / 100)) : currentContractorGrossMonthly;
  const currentTaxSavingsTargetMonthly = taxReserveEnabled ? currentTaxableContractorMonthly * contractorTaxRate : 0;
  const suggestedTaxPerPaycheck = taxReserveEnabled && currentTaxSavingsTargetMonthly > 0 && currentContractorPaychecksPerMonth ? currentTaxSavingsTargetMonthly / currentContractorPaychecksPerMonth : 0;
  const planningIncomeMonthly = spendableIncomeBeforeTaxReserve - currentTaxSavingsTargetMonthly;
  const averagePlanningIncomeMonthly = averageSpendableIncomeBeforeTaxReserve - taxSavingsTargetMonthly;
  const annualNet = averagePlanningIncomeMonthly * 12;

  const totalMonthlyExpenses = expenses.reduce((sum, row) => {
    const amount = parseNumber(row.amount);
    return amount ? sum + amount : sum;
  }, 0);
  const fixedMonthlyExpenses = expenses.filter((row) => row.planType === "fixed").reduce((sum, row) => sum + parseNumber(row.amount), 0);
  const flexibleMonthlyExpenses = expenses.filter((row) => row.planType !== "fixed").reduce((sum, row) => sum + parseNumber(row.amount), 0);
  const totalMonthlyDebt = debts.reduce((sum, row) => sum + parseNumber(row.amount), 0);
  const totalDebtBalance = debts.reduce((sum, row) => sum + parseNumber(row.balance), 0);
  const leftoverMonthly = planningIncomeMonthly - totalMonthlyExpenses - totalMonthlyDebt;
  const leftoverPositive = Math.max(0, leftoverMonthly);
  const liteIncomeMonthly = primaryIncomeResult.monthly + additionalMonthly;
  const liteExpensesMonthly = totalMonthlyExpenses;
  const liteLeftoverMonthly = liteIncomeMonthly - liteExpensesMonthly;
  const liteLeftoverPositive = Math.max(0, liteLeftoverMonthly);
  const liteSnapshotMax = Math.max(liteIncomeMonthly, liteExpensesMonthly, liteLeftoverPositive, 1);
  const liteSnapshotBars = [
    { label: "Income", value: liteIncomeMonthly, color: "bg-[#C9A6FF]" },
    { label: "Expenses", value: liteExpensesMonthly, color: "bg-[#8D63E0]" },
    { label: "Left", value: liteLeftoverPositive, color: "bg-[#CFFBE4]" },
  ];
  const liteVisibleAdditionalIncomes = showLiteAdditionalIncomeInputs
    ? additionalIncomes
    : additionalIncomes.filter(hasAdditionalIncomeContent);
  const liteStarterExpenseRowCount = 10;
  const liteOnboardingExpenseRows = expenses.filter((row) => {
    const normalizedName = row.name.trim().toLowerCase();
    return liteOnboardingCoreExpenseNames.has(normalizedName) || row.isLiteAdded || parseNumber(row.amount) > 0;
  });
  const litePostOnboardingExpenseRows = expenses.filter((row, index) => index < liteStarterExpenseRowCount || row.name.trim() || parseNumber(row.amount) > 0);
  const hasLiteIncomeSetup = parseNumber(baseIncome) > 0 || additionalIncomes.some((row) => parseNumber(row.amount) > 0);
  const hasLiteExpenseSetup = expenses.some((row) => parseNumber(row.amount) > 0);
  const liteResultHeadline = liteLeftoverMonthly >= 0
    ? `You have ${formatCurrency(liteLeftoverMonthly)} left this month`
    : `You are ${formatCurrency(Math.abs(liteLeftoverMonthly))} short this month`;
  const liteResultSupportText = liteLeftoverMonthly >= 0
    ? "This is what is left after your monthly income covers the expenses you entered."
    : "Your current expenses are higher than the income you entered, so this month needs adjustment.";

  const expenseCategoryBreakdown = expenses
    .map((row) => ({ name: row.name || "Unnamed", amount: parseNumber(row.amount), planType: row.planType || "flexible" }))
    .filter((item) => item.amount > 0);
  const totalExpensesForBreakdown = expenseCategoryBreakdown.reduce((sum, item) => sum + item.amount, 0);
  const topExpenseCategories = expenseCategoryBreakdown
    .map((item) => ({ ...item, percentOfNet: safePercent(item.amount, planningIncomeMonthly), percentOfExpenses: totalExpensesForBreakdown > 0 ? (item.amount / totalExpensesForBreakdown) * 100 : 0 }))
    .sort((a, b) => b.amount - a.amount)
    .slice(0, 3);

  const plannedExpenseCategoryOptions = Array.from(
    new Set(expenses.map((row) => getSharedCategoryLabel(row.categoryKey || inferExpenseCategoryKey(row.name))).filter(Boolean))
  );
  const transactionCategoryOptions = Array.from(new Set([...plannedExpenseCategoryOptions, ...baseTransactionCategoryOptions]));
  const quickTransactionCategoryOptions = Array.from(
    new Set([
      ...plannedExpenseCategoryOptions,
      "Housing",
      "Food",
      "Transportation",
      "Utilities",
      "Subscriptions",
      "Debt Payment",
      "Tax Payment",
      "Income",
    ])
  ).slice(0, 8);
  const filteredTransactions = transactions
    .filter((row) => row.date && row.date.slice(0, 7) === transactionMonth)
    .sort((a, b) => {
      const dateCompare = b.date.localeCompare(a.date);
      if (dateCompare !== 0) return dateCompare;
      return (b.id || 0) - (a.id || 0);
    });
  const currentMonthTransactions = transactions
    .filter((row) => row.date && row.date.slice(0, 7) === currentMonthKey)
    .sort((a, b) => {
      const dateCompare = b.date.localeCompare(a.date);
      if (dateCompare !== 0) return dateCompare;
      return (b.id || 0) - (a.id || 0);
    });
  const expenseTransactions = filteredTransactions.filter((row) => row.type === "expense");
  const incomeTransactions = filteredTransactions.filter((row) => row.type === "income");
  const actualExpenseMonthly = expenseTransactions.reduce((sum, row) => sum + parseNumber(row.amount), 0);
  const actualIncomeMonthly = incomeTransactions.reduce((sum, row) => sum + parseNumber(row.amount), 0);
  const transactionNetCashFlow = actualIncomeMonthly - actualExpenseMonthly;
  const currentMonthExpenseTransactions = currentMonthTransactions.filter((row) => row.type === "expense");
  const currentMonthIncomeTransactions = currentMonthTransactions.filter((row) => row.type === "income");
  const currentMonthActualExpense = currentMonthExpenseTransactions.reduce((sum, row) => sum + parseNumber(row.amount), 0);
  const currentMonthActualIncome = currentMonthIncomeTransactions.reduce((sum, row) => sum + parseNumber(row.amount), 0);
  const currentMonthTransactionNetCashFlow = currentMonthActualIncome - currentMonthActualExpense;
  const actualCategoryMap = expenseTransactions.reduce((map, row) => {
    const key = normalizeBudgetCategoryKey(row.category || "Other");
    map[key] = (map[key] || 0) + parseNumber(row.amount);
    return map;
  }, {});
  const currentMonthActualCategoryMap = currentMonthExpenseTransactions.reduce((map, row) => {
    const key = normalizeBudgetCategoryKey(row.category || "Other");
    map[key] = (map[key] || 0) + parseNumber(row.amount);
    return map;
  }, {});
  const currentMonthPlannedVsActualRows = expenses
    .map((row) => {
      const planned = parseNumber(row.amount);
      const actual = currentMonthActualCategoryMap[row.categoryKey || normalizeBudgetCategoryKey(row.name || "")] || 0;
      return {
        id: row.id,
        name: row.name || "Unnamed",
        planType: row.planType || "flexible",
        categoryKey: row.categoryKey || normalizeBudgetCategoryKey(row.name || ""),
        planned,
        actual,
        variance: actual - planned,
      };
    })
    .filter((row) => row.planned > 0 || row.actual > 0);
  const selectedMonthPlannedVsActualRows = expenses
    .map((row) => {
      const planned = parseNumber(row.amount);
      const actual = actualCategoryMap[row.categoryKey || normalizeBudgetCategoryKey(row.name || "")] || 0;
      return {
        id: row.id,
        name: row.name || "Unnamed",
        planType: row.planType || "flexible",
        categoryKey: row.categoryKey || normalizeBudgetCategoryKey(row.name || ""),
        planned,
        actual,
        variance: actual - planned,
      };
    })
    .filter((row) => row.planned > 0 || row.actual > 0);
  const topActualCategories = Object.entries(actualCategoryMap)
    .map(([key, amount]) => ({
      name: getSharedCategoryLabel(key),
      amount,
      share: safePercent(amount, actualExpenseMonthly),
    }))
    .sort((a, b) => b.amount - a.amount)
    .slice(0, 4);
  const topCurrentMonthActualCategories = Object.entries(currentMonthActualCategoryMap)
    .map(([key, amount]) => ({
      name: getSharedCategoryLabel(key),
      amount,
      share: safePercent(amount, currentMonthActualExpense),
    }))
    .sort((a, b) => b.amount - a.amount)
    .slice(0, 4);
  const largestFlexibleCategory = [...expenseCategoryBreakdown]
    .filter((row) => row.planType !== "fixed")
    .sort((a, b) => b.amount - a.amount)[0];

  const debtPercentOfNet = safePercent(totalMonthlyDebt, planningIncomeMonthly);
  const leftoverPercentOfNet = safePercent(leftoverPositive, planningIncomeMonthly);
  const estimatedMonthsToDebtFree = leftoverPositive > 0 && totalDebtBalance > 0 ? totalDebtBalance / leftoverPositive : null;
  const emergencyFundTarget = planningIncomeMonthly > 0 ? planningIncomeMonthly * 3 : 0;
  const monthsToEmergencyFund = leftoverPositive > 0 && emergencyFundTarget > 0 ? emergencyFundTarget / leftoverPositive : null;
  const recommendationMonthlyAmounts = {
    safe: leftoverPositive * RECOMMENDATION_RATES.safe,
    balanced: leftoverPositive * RECOMMENDATION_RATES.balanced,
    aggressive: leftoverPositive * RECOMMENDATION_RATES.aggressive,
  };

  const debtDetails = debts.map((row) => {
    const balance = parseNumber(row.balance);
    const monthlyPayment = parseNumber(row.amount);
    const apr = parseNumber(row.interest);
    return {
      ...row,
      balance,
      monthlyPayment,
      apr,
      payoff: estimateDebtPayoff(balance, monthlyPayment, apr, 0),
      fasterPayoff: leftoverPositive > 0 ? estimateDebtPayoff(balance, monthlyPayment, apr, leftoverPositive * RECOMMENDATION_RATES.safe) : null,
    };
  });

  const highestAprDebt = [...debtDetails].filter((debt) => debt.balance > 0).sort((a, b) => b.apr - a.apr)[0];
  const smallestBalanceDebt = [...debtDetails].filter((debt) => debt.balance > 0).sort((a, b) => a.balance - b.balance)[0];
  const weightedDebtApr = totalDebtBalance > 0 ? debtDetails.reduce((sum, debt) => sum + debt.balance * debt.apr, 0) / totalDebtBalance : 0;

  const getGoalTargetAmount = (goal) => {
    if (goal.type === "all-debt") return totalDebtBalance;
    if (goal.type === "debt") {
      const linkedDebt = debtDetails.find((debt) => String(debt.id) === String(goal.linkedDebtId));
      return linkedDebt ? linkedDebt.balance : parseNumber(goal.targetAmount);
    }
    return parseNumber(goal.targetAmount);
  };

  const getGoalContributionValue = (goal) => {
    if (goal.contributionMode === "custom") return parseNumber(goal.customContribution);
    const monthlyRecommendation = recommendationMonthlyAmounts[goal.recommendationLevel] || 0;
    return goal.contributionStyle === "per-paycheck" ? (paychecksPerMonth ? monthlyRecommendation / paychecksPerMonth : 0) : monthlyRecommendation;
  };

  const getGoalMonthlyEquivalent = (goal) => goal.contributionStyle === "per-paycheck" ? (paychecksPerMonth ? getGoalContributionValue(goal) * paychecksPerMonth : 0) : getGoalContributionValue(goal);

  const goalAnalyses = customGoals.map((goal) => {
    const targetAmount = getGoalTargetAmount(goal);
    const contributionValue = getGoalContributionValue(goal);
    const monthlyEquivalent = getGoalMonthlyEquivalent(goal);
    const linkedDebt = debtDetails.find((debt) => String(debt.id) === String(goal.linkedDebtId));
    const baseDebtPayment = goal.type === "debt" ? linkedDebt?.monthlyPayment || 0 : goal.type === "all-debt" ? totalMonthlyDebt : 0;
    const progressMonthlyAmount = goal.type === "debt" || goal.type === "all-debt" ? baseDebtPayment + monthlyEquivalent : monthlyEquivalent;
    const debtEstimate = goal.type === "debt" && linkedDebt ? estimateDebtPayoff(linkedDebt.balance, linkedDebt.monthlyPayment, linkedDebt.apr, monthlyEquivalent) : null;
    const allDebtEstimate = goal.type === "all-debt" ? estimateDebtPayoff(totalDebtBalance, totalMonthlyDebt, weightedDebtApr, monthlyEquivalent) : null;
    const estimatedMonths = goal.type === "debt" ? debtEstimate?.months ?? null : goal.type === "all-debt" ? allDebtEstimate?.months ?? null : monthlyEquivalent > 0 ? targetAmount / monthlyEquivalent : null;

    return {
      ...goal,
      targetAmount,
      contributionValue,
      monthlyEquivalent,
      progressMonthlyAmount,
      linkedDebt,
      estimatedMonths,
      requiredFor6Months: targetAmount > 0 ? targetAmount / 6 : 0,
      requiredFor12Months: targetAmount > 0 ? targetAmount / 12 : 0,
      requiredFor24Months: targetAmount > 0 ? targetAmount / 24 : 0,
      annualPathPercent: targetAmount > 0 ? (progressMonthlyAmount * 12 * 100) / targetAmount : 0,
      unrealistic: monthlyEquivalent > leftoverPositive && leftoverPositive > 0 ? "This contribution is likely too aggressive for your current leftover cash. Consider using Safe or Balanced first." : leftoverPositive <= 0 ? "There is no monthly leftover right now, so the recommended contribution is $0 until cash flow improves." : null,
      goalMessage: goal.type === "debt" ? debtEstimate?.status === "ok" ? `This estimate includes the debt's current payment plus ${formatCurrency(monthlyEquivalent)} of extra payoff money.` : debtEstimate?.message : goal.type === "all-debt" ? allDebtEstimate?.status === "ok" ? `This estimate uses all current debt payments plus ${formatCurrency(monthlyEquivalent)} of extra payoff money each month.` : allDebtEstimate?.message : monthlyEquivalent > 0 ? `At this pace, this goal could be reached in about ${formatMonths(estimatedMonths).toLowerCase()}.` : "Choose a contribution to unlock the timeline.",
    };
  });

  const debtStrategyPool = debtDetails.filter((debt) => debt.balance > 0);
  const debtPriorityOrder = [...debtStrategyPool].sort((a, b) => {
    if (debtStrategyMethod === "snowball") return a.balance - b.balance;
    if (debtStrategyMethod === "avalanche") return b.apr - a.apr || a.balance - b.balance;
    return b.monthlyPayment - a.monthlyPayment || b.balance - a.balance;
  });
  const selectedStrategyDebt = debtStrategyTarget === "all" ? null : debtDetails.find((debt) => String(debt.id) === String(debtStrategyTarget));
  const firstPriorityDebt = debtStrategyTarget === "all" ? debtPriorityOrder[0] : selectedStrategyDebt;
  const firstPriorityEstimate = firstPriorityDebt ? estimateDebtPayoff(firstPriorityDebt.balance, firstPriorityDebt.monthlyPayment, firstPriorityDebt.apr, parseNumber(debtStrategyExtraAmount)) : null;

  const insights = [];
  const pressureSignals = [
    { id: "planned-bills", label: "planned bills", amount: totalMonthlyExpenses },
    { id: "debt", label: "debt payments", amount: totalMonthlyDebt },
    { id: "tax", label: "owner-paid tax reserve", amount: currentTaxSavingsTargetMonthly },
  ].filter((signal) => signal.amount > 0).sort((a, b) => b.amount - a.amount);
  const topPressureSignal = pressureSignals[0] ?? null;

  if (leftoverMonthly < 0) {
    insights.push({
      title: "Bleeding Money This Month",
      body: topPressureSignal
        ? `Your current month plan is short by ${formatCurrency(Math.abs(leftoverMonthly))}. The biggest pressure on your cash flow right now is ${topPressureSignal.label} at ${formatCurrency(topPressureSignal.amount)} this month.`
        : `Your current month plan is short by ${formatCurrency(Math.abs(leftoverMonthly))}. Reduce fixed pressure or raise income before adding new goals.`,
      actionLabel: topPressureSignal?.id === "tax" ? "Open Taxes" : "Open Expense Planning",
      actionTab: topPressureSignal?.id === "tax" ? "taxes" : "expenses",
      action: largestFlexibleCategory
        ? `Recommended move: cut ${largestFlexibleCategory.name} first by about ${formatCurrency(Math.min(Math.abs(leftoverMonthly), largestFlexibleCategory.amount))} before touching fixed essentials.`
        : topPressureSignal?.id === "tax"
        ? "Recommended move: review the Taxes tab and lower the monthly reserve target only if it no longer matches your current income picture."
        : "Recommended move: review Expense Planning and reduce the largest non-essential category before adding goals or extra debt payments."
    });
  } else if (leftoverMonthly === 0) {
    insights.push({
      title: "Cash Flow Warning",
      body: "You are breaking even this month. There is no real cushion yet, so one surprise expense can push the budget into the red.",
      actionLabel: "Open Transactions",
      actionTab: "transactions",
      action: "Recommended move: keep flexible spending conservative and log transactions this month so you can catch drift before it turns negative."
    });
  } else if (leftoverPercentOfNet < 10) {
    insights.push({
      title: "Tight Month Warning",
      body: `Only about ${Math.round(leftoverPercentOfNet)}% of your planning income is left this month, so keep goals and extra spending conservative.`,
      actionLabel: "Open Goals",
      actionTab: "goals",
      action: "Recommended move: pause any aggressive goal contribution for this month and protect the cushion first."
    });
  }

  if (debtPercentOfNet >= 20) insights.push({ title: "Debt Warning", body: `Debt payments are taking ${Math.round(debtPercentOfNet)}% of your planning income. Paying down one target debt should be a top priority.`, actionLabel: "Open Goals", actionTab: "goals", action: firstPriorityDebt ? `Recommended move: focus extra debt money on ${firstPriorityDebt.name} first instead of spreading it across every balance.` : "Recommended move: focus on one debt target before adding more goals." });
  if (highestAprDebt && highestAprDebt.apr >= 20) insights.push({ title: "Debt Warning", body: `${highestAprDebt.name} has the highest APR at ${highestAprDebt.apr.toFixed(1)}%. It is a strong avalanche candidate.`, actionLabel: "Open Goals", actionTab: "goals", action: `Recommended move: if you have extra debt money this month, send it to ${highestAprDebt.name} first.` });
  if (leftoverPositive > 0) insights.push({ title: "Savings Opportunity", body: `A balanced contribution is about ${formatCurrency(recommendationMonthlyAmounts.balanced)} per month, which keeps goal momentum without crushing your bills.`, actionLabel: "Open Goals", actionTab: "goals", action: `Recommended move: use the balanced amount as your ceiling unless this month has extra income or unusually low expenses.` });
  if (leftoverPositive > 0 && emergencyFundTarget > 0) insights.push({ title: "Goal Recommendation", body: `A 3-month emergency fund of ${formatCurrency(emergencyFundTarget)} would take about ${formatMonths(monthsToEmergencyFund).toLowerCase()}. A $1,000 starter fund may be the easiest first win.`, actionLabel: "Open Goals", actionTab: "goals", action: "Recommended move: if you do not already have a small emergency buffer, make the first $1,000 your immediate goal before stretching into bigger targets." });
  if (taxReserveEnabled && estimatedAnnualTax > 0) insights.push({ title: "Tax Reminder", body: `You are projecting about ${formatCurrency(estimatedAnnualTax)} in annual owner-paid taxes. Set aside roughly ${formatCurrency(currentTaxSavingsTargetMonthly)} this month from your planning income.`, actionLabel: "Open Taxes", actionTab: "taxes", action: "Recommended move: move that tax reserve into a separate savings bucket as soon as the income hits so it does not look spendable." });
  if (topExpenseCategories[0]) insights.push({ title: leftoverMonthly <= 0 ? "Likely Spending Leak" : "Spending Concentration", body: `${topExpenseCategories[0].name} is your top category at ${formatCurrency(topExpenseCategories[0].amount)} per month, so that is where the biggest cost-cutting leverage likely lives.`, actionLabel: "Open Expense Planning", actionTab: "expenses", action: `Recommended move: review ${topExpenseCategories[0].name} first before trimming smaller categories.` });

  const requiredMonthlyOutflow = fixedMonthlyExpenses + totalMonthlyDebt + currentTaxSavingsTargetMonthly;
  const currentMonthShortfall = Math.max(0, Math.abs(leftoverMonthly));
  const currentMonthTrackedPlanGap = totalMonthlyExpenses > 0 ? currentMonthActualExpense - totalMonthlyExpenses : 0;
  const nextBestMove = shouldShowSetupGuidance && nextSetupItem
    ? {
        title: "Finish the foundation",
        body: nextSetupItem.description,
        actionLabel: nextSetupItem.actionLabel,
        tab: nextSetupItem.tab,
      }
    : !setupChecklist.transactions
    ? {
        title: "Start logging real activity",
        body: "Your plan is in place, but the app still needs real transactions to compare your monthly plan against reality.",
        actionLabel: "Open Quick Capture",
        tab: "transactions",
      }
    : leftoverMonthly < 0 && largestFlexibleCategory
    ? {
        title: "Cut the easiest category first",
        body: `Reduce ${largestFlexibleCategory.name} by about ${formatCurrency(Math.min(currentMonthShortfall, largestFlexibleCategory.amount))} to close the biggest part of this month's gap without touching fixed essentials first.`,
        actionLabel: "Review Expense Planning",
        tab: "expenses",
      }
    : leftoverMonthly < 0
    ? {
        title: "Reduce required pressure",
        body: topPressureSignal
          ? `Your month is short by ${formatCurrency(currentMonthShortfall)} and the biggest pressure is ${topPressureSignal.label}. Review that area first.`
          : `Your month is short by ${formatCurrency(currentMonthShortfall)}. Reduce fixed pressure or raise income before adding goals.`,
        actionLabel: topPressureSignal?.id === "tax" ? "Review Taxes" : "Review Expense Planning",
        tab: topPressureSignal?.id === "tax" ? "taxes" : "expenses",
      }
    : totalDebtBalance > 0 && leftoverPositive > 0
    ? {
        title: "Use this month to push debt",
        body: firstPriorityDebt
          ? `You have about ${formatCurrency(leftoverPositive)} left this month. A safe first move is sending part of it toward ${firstPriorityDebt.name}.`
          : `You have leftover cash this month. Use part of it to reduce debt pressure before adding more goals.`,
        actionLabel: "Open Goals",
        tab: "goals",
      }
    : {
        title: "Keep the month measured",
        body: "Your month currently has breathing room. Keep transactions updated so the app can warn you before spending drifts off plan.",
        actionLabel: "Open Quick Capture",
        tab: "transactions",
      };
  const controlCenterBreakdownTitle = leftoverMonthly < 0
    ? "Why This Month Feels Tight"
    : leftoverMonthly === 0
    ? "What Is Driving This Month"
    : "What Is Supporting This Month";
  const controlCenterBreakdownSubtitle = leftoverMonthly < 0
    ? "See whether the month is being squeezed by fixed bills, flexible spending, debt, or taxes."
    : leftoverMonthly === 0
    ? "See which parts of the plan are using up nearly all of this month's room."
    : "See which parts of the plan are taking the most room and which part of the month is still working in your favor.";
  const operatingRhythmCards = [
    {
      id: "daily",
      label: "Daily",
      title: "Quick Capture real activity",
      body: "Use Quick Capture for purchases, deposits, bill payments, and card spending so the month stays honest while it is happening.",
      actionLabel: "Open Quick Capture",
      tab: "transactions",
      quickCapture: true,
    },
    {
      id: "weekly",
      label: "Weekly",
      title: "Review the month before it drifts",
      body: "Open Control Center once a week, compare actual activity against the plan, and trim the biggest flexible category early if the month is tightening.",
      actionLabel: "Review Control Center",
      tab: "control",
    },
    {
      id: "monthly",
      label: "Monthly",
      title: "Reset the operating plan",
      body: "Confirm income, fixed essentials, flexible spending, debt pressure, and taxes at the start of each month so your guidance stays realistic.",
      actionLabel: "Review Expense Planning",
      tab: "expenses",
    },
  ];
  const fullTabs = [
    { id: "control", label: "Control Center" },
    { id: "setup", label: "Income Setup" },
    { id: "income", label: "Income" },
    { id: "expenses", label: "Expense Planning" },
    { id: "transactions", label: "Transactions" },
    { id: "goals", label: "Goals" },
    { id: "taxes", label: "Taxes" },
    { id: "insights", label: "Insights" },
  ];
  const liteTabs = [
    { id: "overview", label: "Overview" },
    { id: "income", label: "Income" },
    { id: "expenses", label: "Expenses" },
  ];
  const fullTabIds = fullTabs.map((tab) => tab.id);
  const liteTabIds = liteTabs.map((tab) => tab.id);
  const lockedLiteTabs = [
    { id: "future", label: "Plan your future \ud83d\udd12", title: "Plan your future", body: "Turn leftover money into savings goals, future targets, and a bigger plan for your money." },
    { id: "insights", label: "Get smart insights \ud83d\udd12", title: "Get smart insights", body: "See guidance that explains what is helping or hurting your month without doing the detective work yourself." },
    { id: "taxes", label: "Optimize your money \ud83d\udd12", title: "Optimize your money", body: "Add deeper planning tools like taxes, payoff strategy, and smarter monthly decision support." },
    { id: "control", label: "Build real control \ud83d\udd12", title: "Build real control", body: "Unlock the full Flow System for debt planning, advanced guidance, and stronger monthly control." },
  ];
  const tabs = isLiteMode ? (shouldShowLiteOnboarding ? [] : liteTabs) : fullTabs;

  const canCreateGoal = goalForm.type === "savings" || goalForm.type === "custom" ? parseNumber(goalForm.targetAmount) > 0 : goalForm.type === "debt" ? goalForm.linkedDebtId !== "" : totalDebtBalance > 0;
  const timelineOptions = goalAnalyses.map((goal) => ({ id: String(goal.id), label: goal.name }));
  const activeTimelineGoal = goalAnalyses.find((goal) => String(goal.id) === String(timelineFocus)) ?? goalAnalyses[0] ?? null;
  const timelineData = activeTimelineGoal ? buildGoalTimeline(activeTimelineGoal.progressMonthlyAmount, activeTimelineGoal.targetAmount, activeTimelineGoal.type === "debt" || activeTimelineGoal.type === "all-debt" ? "debt" : "savings") : [];

  const openTab = (tabId, options = {}) => {
    if (isLiteMode) {
      if (shouldShowLiteOnboarding) return;

      const lockedLiteTab = lockedLiteTabs.find((tab) => tab.id === tabId);
      if (lockedLiteTab) {
        setSelectedLiteUpgrade(lockedLiteTab);
        setShowLiteUpgradePrompt(true);
        return;
      }
    }

    if (tabId === "transactions" && options.quickCapture) {
      resetTransactionForm(createQuickRepeatTransactionForm(transactionForm));
      setEditingTransactionId(null);
      setTransactionEntryMode("quick");
      setTransactionTone("idle");
      setTransactionMessage("");
    }
    setActiveTab(tabId);
  };

  useEffect(() => {
    if (isLiteMode) {
      if (shouldShowLiteOnboarding) return;
      const liteAllowedTabs = [...liteTabIds, "settings"];
      if (!liteAllowedTabs.includes(activeTab)) {
        setActiveTab("overview");
      }
      return;
    }

    if (![...fullTabIds, "settings"].includes(activeTab)) {
      setActiveTab("control");
    }
  }, [activeTab, fullTabIds, isLiteMode, liteTabIds, shouldShowLiteOnboarding]);

  useEffect(() => {
    if (!isLiteMode) return;

    if (effectiveLiteOnboardingComplete) {
      setLiteOnboardingStep("done");
      return;
    }

    if (liteOnboardingStep === "done") {
      setLiteOnboardingStep("welcome");
    }
  }, [effectiveLiteOnboardingComplete, isLiteMode, liteOnboardingStep]);

  useEffect(() => {
    if (!isLiteMode || effectiveLiteOnboardingComplete || liteOnboardingStep === "done") return;

    window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    });
  }, [effectiveLiteOnboardingComplete, isLiteMode, liteOnboardingStep]);

  useEffect(() => {
    if (!isLiteMode || effectiveLiteOnboardingComplete) return;

    setExpenses((current) => {
      if (current.length >= 10) return current;

      const existingCategoryKeys = new Set(current.map((row) => row.categoryKey || inferExpenseCategoryKey(row.name)));
      const missingStarterRows = createDefaultExpenses()
        .filter((row) => !existingCategoryKeys.has(row.categoryKey))
        .map((row, index) => ({
          ...row,
          id: Date.now() + index + Math.random(),
        }));

      if (missingStarterRows.length === 0) return current;
      return [...current, ...missingStarterRows];
    });
  }, [effectiveLiteOnboardingComplete, isLiteMode]);

  const createGoalFromTemplate = (template) => {
    const goal = {
      id: Date.now() + Math.random(),
      name: template.name,
      type: template.type,
      targetAmount: String(template.targetAmount || ""),
      linkedDebtId: template.linkedDebtId || "",
      contributionStyle: "monthly",
      contributionMode: "recommended",
      recommendationLevel: "balanced",
      customContribution: "",
    };

    setCustomGoals((previous) => [...previous, goal]);
    setTimelineFocus(String(goal.id));
  };

  const handleAddGoal = () => {
    const linkedDebt = debtDetails.find((debt) => String(debt.id) === String(goalForm.linkedDebtId));
    const targetAmount = goalForm.type === "debt" ? linkedDebt?.balance || 0 : goalForm.type === "all-debt" ? totalDebtBalance : parseNumber(goalForm.targetAmount);
    const defaultName = goalForm.type === "all-debt" ? "Pay Off All Debt" : goalForm.type === "debt" ? linkedDebt ? `Pay Off ${linkedDebt.name}` : "Debt Payoff Goal" : goalForm.type === "custom" ? "Custom Goal" : "Savings Goal";
    const goal = { id: Date.now(), ...goalForm, name: goalForm.name.trim() || defaultName, targetAmount: String(targetAmount || goalForm.targetAmount) };
    setCustomGoals((previous) => [...previous, goal]);
    setGoalForm(createInitialGoalForm());
    setTimelineFocus(String(goal.id));
  };

  const updateGoal = (goalId, updates) => setCustomGoals((previous) => previous.map((goal) => goal.id === goalId ? { ...goal, ...updates } : goal));

  const updateExpenseRow = (expenseId, updates) => {
    setExpenses((current) => current.map((row) => (row.id === expenseId ? { ...row, ...updates } : row)));
  };

  const removeExpenseRow = (expenseId) => {
    setExpenses((current) => current.filter((row) => row.id !== expenseId));
  };

  const addExpenseRow = (planType = "flexible", defaults = {}) => {
    setExpenses((current) => [
      ...current,
      {
        id: Date.now() + Math.random(),
        name: defaults.name || "",
        amount: defaults.amount || "",
        planType: defaults.planType || planType,
        categoryKey: defaults.categoryKey || getDefaultExpenseCategoryKey(defaults.planType || planType),
        isLiteAdded: defaults.isLiteAdded || false,
      },
    ]);
  };

  const addLiteExpensePreset = (preset) => {
    setExpenses((current) => {
      const presetName = preset.name.trim().toLowerCase();
      const existingPreset = current.find((row) => row.name.trim().toLowerCase() === presetName);

      if (existingPreset) {
        return current.map((row) => (
          row.id === existingPreset.id
            ? { ...row, isLiteAdded: true, planType: preset.planType, categoryKey: preset.categoryKey }
            : row
        ));
      }

      return [
        ...current,
        {
          id: Date.now() + Math.random(),
          ...preset,
          amount: "",
          isLiteAdded: true,
        },
      ];
    });
  };

  const handleSettingsFieldChange = (field, value) => {
    setSettingsForm((current) => ({ ...current, [field]: value }));
    if (settingsMessage) {
      setSettingsMessage("");
      setSettingsTone("idle");
    }
  };

  const handleTransactionFieldChange = (field, value) => {
    setTransactionForm((current) => ({ ...current, [field]: value }));
    if (transactionMessage) {
      setTransactionMessage("");
      setTransactionTone("idle");
    }
  };

  const switchTransactionEntryMode = (mode) => {
    setTransactionEntryMode(mode);
    if (!editingTransactionId && mode === "quick") {
      setTransactionForm((current) => createQuickRepeatTransactionForm(current));
    }
    if (!editingTransactionId && transactionMessage) {
      setTransactionMessage("");
      setTransactionTone("idle");
    }
  };

  const applyQuickTransactionType = (type) => {
    handleTransactionFieldChange("type", type);
    if (type === "income" && !transactionForm.category) {
      handleTransactionFieldChange("category", "Income");
    }
    if (type === "transfer" && !transactionForm.category) {
      handleTransactionFieldChange("category", "Savings Transfer");
    }
  };

  const applyQuickTransactionCategory = (category) => {
    const inferredType = category === "Income" ? "income" : category === "Savings Transfer" ? "transfer" : transactionForm.type === "income" ? "expense" : transactionForm.type;
    setTransactionForm((current) => ({
      ...current,
      category,
      type: inferredType,
      description: current.description || category,
    }));
    if (transactionMessage) {
      setTransactionMessage("");
      setTransactionTone("idle");
    }
  };

  const applyQuickTransactionAccount = (account) => {
    handleTransactionFieldChange("account", account);
  };

  const applyQuickTransactionPreset = (preset) => {
    setTransactionForm((current) => ({
      ...createQuickRepeatTransactionForm(current),
      date: current.date || getTodayInputValue(),
      type: preset.type,
      category: preset.category,
      account: preset.account,
      description: preset.description,
    }));
    setEditingTransactionId(null);
    setTransactionEntryMode("quick");
    setTransactionTone("idle");
    setTransactionMessage(`${preset.label} preset ready. Add the amount and save.`);
    setActiveTab("transactions");
  };

  const resetTransactionForm = (nextForm = createInitialTransactionForm()) => {
    setTransactionForm(nextForm);
    setEditingTransactionId(null);
  };

  const renderWeekdaySelector = (value, onChange) => (
    <div className="flex flex-wrap gap-2">
      {weekdayOptions.map((day) => (
        <button
          key={day}
          type="button"
          onClick={() => onChange(day)}
          className={`px-3 py-1 rounded-full text-xs border transition-all ${value === day ? "bg-[#9E4CFF] text-black border-[#9E4CFF] shadow-[0_0_12px_#9E4CFF]" : "bg-black/70 border-[#2A0047] text-[#D8D8D8] hover:bg-black"}`}
        >
          {day}
        </button>
      ))}
    </div>
  );

  const handleAddTransaction = () => {
    if (!transactionForm.date || !parseNumber(transactionForm.amount) || !transactionForm.category.trim()) {
      setTransactionTone("error");
      setTransactionMessage("Add a date, amount, and category before saving this transaction.");
      return;
    }

    const wasEditing = Boolean(editingTransactionId);
    const wasQuickCapture = !wasEditing && transactionEntryMode === "quick";
    const nextTransaction = {
      id: editingTransactionId ?? Date.now(),
      date: transactionForm.date,
      amount: String(parseNumber(transactionForm.amount)),
      type: transactionForm.type,
      category: transactionForm.category.trim(),
      account: transactionForm.account.trim() || "Checking",
      description: transactionForm.description.trim(),
    };

    if (wasEditing) {
      setTransactions((current) =>
        current.map((row) => (row.id === editingTransactionId ? nextTransaction : row))
      );
      resetTransactionForm(
        createQuickRepeatTransactionForm({
          date: transactionForm.date,
          account: transactionForm.account,
        })
      );
      setTransactionEntryMode("quick");
      setTransactionTone("success");
      setTransactionMessage("Transaction updated. Back to Quick Capture.");
      return;
    }

    setTransactions((current) => [nextTransaction, ...current]);

    if (wasQuickCapture) {
      resetTransactionForm(createQuickRepeatTransactionForm(transactionForm));
      setTransactionEntryMode("quick");
      setTransactionTone("success");
      setTransactionMessage("Transaction added. Quick Capture is ready for the next entry.");
      return;
    }

    resetTransactionForm(createInitialTransactionForm());
    setTransactionEntryMode("detailed");
    setTransactionTone("success");
    setTransactionMessage("Transaction added. Detailed Entry is ready for another transaction.");
  };

  const handleEditTransaction = (transaction) => {
    setTransactionForm({
      date: transaction.date || getTodayInputValue(),
      amount: transaction.amount || "",
      type: transaction.type || "expense",
      category: transaction.category || "",
      account: transaction.account || "Checking",
      description: transaction.description || "",
    });
    setEditingTransactionId(transaction.id);
    setTransactionEntryMode("detailed");
    setTransactionTone("idle");
    setTransactionMessage("Editing transaction. Save when you're ready, or cancel to keep the original.");
    setActiveTab("transactions");
  };

  const handleCancelTransactionEdit = () => {
    resetTransactionForm(createQuickRepeatTransactionForm());
    setTransactionEntryMode("quick");
    setTransactionTone("success");
    setTransactionMessage("Edit canceled. Back to Quick Capture.");
  };

  const handleDeleteTransaction = (transactionId) => {
    setTransactions((current) => current.filter((row) => row.id !== transactionId));
    if (editingTransactionId === transactionId) {
      resetTransactionForm(createQuickRepeatTransactionForm());
      setTransactionEntryMode("quick");
    }
    setTransactionTone("success");
    setTransactionMessage("Transaction deleted.");
  };

  const handleSaveSettings = async () => {
    if (!settingsForm.fullName.trim()) {
      setSettingsTone("error");
      setSettingsMessage("Please enter your full name before saving settings.");
      return;
    }

    setSettingsBusy(true);
    const result = await onProfileUpdate({
      fullName: settingsForm.fullName.trim(),
      preferredName: settingsForm.preferredName.trim(),
      dateOfBirth: settingsForm.dateOfBirth,
      preferences: {
        themeMode: settingsForm.themeMode,
        accentColor: settingsForm.accentColor,
      },
    });
    setSettingsBusy(false);
    setSettingsTone(result.ok ? "success" : "error");
    setSettingsMessage(result.message);
  };

  const handlePasswordReset = async () => {
    setSettingsBusy(true);
    const result = await onSendPasswordReset(accountEmail);
    setSettingsBusy(false);
    setSettingsTone(result.ok ? "success" : "error");
    setSettingsMessage(result.message);
  };

  const handleVerificationEmail = async () => {
    setSettingsBusy(true);
    const result = await onSendEmailVerification();
    setSettingsBusy(false);
    setSettingsTone(result.ok ? "success" : "error");
    setSettingsMessage(result.message);
  };

  const handleEmailChangeRequest = async () => {
    setSettingsBusy(true);
    const result = await onRequestEmailChange(settingsForm.pendingEmail.trim());
    setSettingsBusy(false);
    setSettingsTone(result.ok ? "success" : "error");
    setSettingsMessage(result.message);
  };

  const handleRefreshAccountStatus = async () => {
    setSettingsBusy(true);
    const result = await onRefreshAccountStatus();
    setSettingsBusy(false);
    setSettingsTone(result.ok ? "success" : "error");
    setSettingsMessage(result.message);
  };

  const deleteGoal = (goalId) => {
    setCustomGoals((previous) => previous.filter((goal) => goal.id !== goalId));
    setTimelineFocus("");
  };
  const renderIncomeSetup = () => {
    const incomeTypeButtons = [
      { id: INCOME_TYPES.W2, label: "W-2 (Employee)" },
      { id: INCOME_TYPES.SELF_EMPLOYED, label: "Self-employed / 1099" },
      { id: INCOME_TYPES.BOTH, label: "Both" },
    ];
    const frequencyOptions = ["Weekly", "Bi-weekly", "Monthly", "Other"];
    const w2FrequencyOptions = ["Weekly", "Bi-weekly", "Monthly"];

    const updateMixedIncome = (field, value) => {
      setMixedIncome((current) => ({ ...current, [field]: value }));
    };

    const updateTaxProfileField = (field, value) => {
      setTaxProfile((current) => ({ ...current, [field]: value }));
    };

    const renderW2PayrollGuidance = () => (
      <div className="rounded-xl border border-[#2A0047] bg-black/40 p-4 flex flex-col gap-3">
        <div>
          <h4 className="text-sm font-semibold text-[#C9A6FF]">W-2 Payroll Setup</h4>
          <p className="text-[11px] text-[#D8D8D8] mt-1">
            The app auto-estimates federal withholding, Social Security, and Medicare. State withholding is auto-zero for no-income-tax states; otherwise add your real paystub state withholding if you want tighter net-pay accuracy.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="flex flex-col gap-2 text-sm">
            <span className="text-[#9E4CFF] uppercase tracking-widest">State</span>
            <select value={activeW2ResidenceState} onChange={(event) => updateTaxProfileField("w2ResidenceState", event.target.value)} className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#9E4CFF]">
              {stateOptions.map((option) => <option key={option.code} value={option.code}>{option.label}</option>)}
            </select>
          </div>
          <div className="flex flex-col gap-2 text-sm">
            <span className="text-[#9E4CFF] uppercase tracking-widest">Filing Status</span>
            <select value={activeW2FilingStatus} onChange={(event) => updateTaxProfileField("w2FilingStatus", event.target.value)} className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#9E4CFF]">
              <option value="single">Single</option>
              <option value="married-joint">Married Filing Jointly</option>
              <option value="married-separate">Married Filing Separately</option>
              <option value="head-of-household">Head of Household</option>
            </select>
          </div>
          <div className="flex flex-col gap-2 text-sm">
            <span className="text-[#9E4CFF] uppercase tracking-widest">Qualifying Children</span>
            <input type="number" min="0" value={taxProfile?.w2QualifyingChildren ?? ""} onChange={(event) => updateTaxProfileField("w2QualifyingChildren", event.target.value)} className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#9E4CFF]" />
          </div>
          <div className="flex flex-col gap-2 text-sm">
            <span className="text-[#9E4CFF] uppercase tracking-widest">Other Dependents</span>
            <input type="number" min="0" value={taxProfile?.w2OtherDependents ?? ""} onChange={(event) => updateTaxProfileField("w2OtherDependents", event.target.value)} className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#9E4CFF]" />
          </div>
          <div className="flex flex-col gap-2 text-sm">
            <span className="text-[#9E4CFF] uppercase tracking-widest">Extra Federal Withholding / Paycheck</span>
            <input type="number" min="0" placeholder="$0.00" value={taxProfile?.w2ExtraFederalWithholding ?? ""} onChange={(event) => updateTaxProfileField("w2ExtraFederalWithholding", event.target.value)} className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#9E4CFF]" />
          </div>
          <div className="flex flex-col gap-2 text-sm">
            <span className="text-[#9E4CFF] uppercase tracking-widest">State Withholding / Paycheck</span>
            <input
              type="number"
              min="0"
              placeholder={autoW2PayrollEstimate.noIncomeTaxState ? "$0.00 (no state income tax)" : "$0.00 optional"}
              value={taxProfile?.w2StateWithholdingAmount ?? ""}
              onChange={(event) => updateTaxProfileField("w2StateWithholdingAmount", event.target.value)}
              className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#9E4CFF]"
            />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs text-[#D8D8D8]">
          <div className="rounded-lg border border-[#2A0047] bg-[#130018] px-3 py-3">
            <div className="flex justify-between"><span className="uppercase tracking-widest text-[#9E4CFF]">Estimated federal</span><span className="font-semibold text-white">{formatCurrency(autoW2FederalWithholdingPerPaycheck)} / paycheck</span></div>
            <p className="mt-2">{autoW2MarginalRateLabel}</p>
          </div>
          <div className="rounded-lg border border-[#2A0047] bg-[#130018] px-3 py-3">
            <div className="flex justify-between"><span className="uppercase tracking-widest text-[#9E4CFF]">Payroll taxes</span><span className="font-semibold text-white">{formatCurrency(autoW2SocialSecurityPerPaycheck + autoW2MedicarePerPaycheck)} / paycheck</span></div>
            <p className="mt-2">Social Security {formatCurrency(autoW2SocialSecurityPerPaycheck)} + Medicare {formatCurrency(autoW2MedicarePerPaycheck)}</p>
          </div>
          <div className="rounded-lg border border-[#2A0047] bg-[#130018] px-3 py-3">
            <div className="flex justify-between"><span className="uppercase tracking-widest text-[#9E4CFF]">State withholding</span><span className="font-semibold text-white">{formatCurrency(autoW2StateWithholdingPerPaycheck)} / paycheck</span></div>
            <p className="mt-2">{autoW2PayrollEstimate.noIncomeTaxState ? `${activeW2ResidenceState} has no state income tax.` : "Use your paystub amount here if your state withholds tax."}</p>
          </div>
          <div className="rounded-lg border border-[#2A0047] bg-[#130018] px-3 py-3">
            <div className="flex justify-between"><span className="uppercase tracking-widest text-[#9E4CFF]">{w2InputMode === "take-home" ? "Estimated take-home" : "Estimated spendable"}</span><span className="font-semibold text-white">{formatCurrency(autoW2TakeHomePerPaycheck)} / paycheck</span></div>
            <p className="mt-2">Estimated gross {formatCurrency(autoW2GrossDisplayPerPaycheck)} before withholding.</p>
          </div>
        </div>
      </div>
    );

    const renderContractorCustomRows = () => (
      <div className="flex flex-col gap-3 text-sm">
        <span className="text-[#9E4CFF] uppercase tracking-widest">1099 Pay Dates and Amounts</span>
        <p className="text-xs text-[#D8D8D8]">Use this when your 1099 income lands on irregular dates. We average it by month so the planner stays realistic.</p>
        <div className="flex flex-col gap-2 max-h-44 overflow-y-auto pr-1">
          {mixedIncome.contractorCustomPayDates.map((row, index) => (
            <div key={row.id} className="grid grid-cols-2 gap-2 items-center">
              <input
                type="date"
                className="bg-black/70 border border-[#2A0047] rounded-md px-2 py-1 text-xs"
                value={row.date}
                onChange={(event) => {
                  const updated = [...mixedIncome.contractorCustomPayDates];
                  updated[index] = { ...row, date: event.target.value };
                  updateMixedIncome("contractorCustomPayDates", updated);
                }}
              />
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  placeholder="$0.00"
                  className="flex-1 bg-black/70 border border-[#2A0047] rounded-md px-2 py-1 text-xs text-right"
                  value={row.amount}
                  onChange={(event) => {
                    const updated = [...mixedIncome.contractorCustomPayDates];
                    updated[index] = { ...row, amount: event.target.value };
                    updateMixedIncome("contractorCustomPayDates", updated);
                  }}
                />
                {mixedIncome.contractorCustomPayDates.length > 1 ? (
                  <button
                    type="button"
                    className="text-xs text-red-300 hover:text-red-400"
                    onClick={() => updateMixedIncome("contractorCustomPayDates", mixedIncome.contractorCustomPayDates.filter((_, rowIndex) => rowIndex !== index))}
                  >
                    Remove
                  </button>
                ) : null}
              </div>
            </div>
          ))}
        </div>
        <button
          type="button"
          className="self-start px-3 py-1 rounded-full bg-[#9E4CFF] text-black text-xs font-semibold tracking-wide shadow-[0_0_12px_#9E4CFF]"
          onClick={() => updateMixedIncome("contractorCustomPayDates", [...mixedIncome.contractorCustomPayDates, { id: mixedIncome.contractorCustomPayDates.length + 1, date: "", amount: "" }])}
        >
          + Add 1099 Paycheck Entry
        </button>
      </div>
    );

    return (
      <div>
        <h2 className="text-2xl font-semibold text-[#C9A6FF] mb-4">Income Setup</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <SectionCard title="Income Settings" subtitle={shouldShowSetupGuidance ? "Start here. Add how money comes in first, then your monthly bills, so the app can show what is really left this month." : "Set up how money comes in before the app tells you what is safe to spend, save, or set aside."}>
            <div className="flex flex-col gap-2 text-sm">
              <span className="text-[#9E4CFF] uppercase tracking-widest">Income Type</span>
              <div className="flex flex-wrap gap-2">
                {incomeTypeButtons.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => setIncomeType(option.id)}
                    className={`px-3 py-1 rounded-full text-xs border transition-all ${incomeType === option.id ? "bg-[#9E4CFF] text-black border-[#9E4CFF] shadow-[0_0_12px_#9E4CFF]" : "bg-black/70 border-[#2A0047] text-[#D8D8D8] hover:bg-black"}`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-[#D8D8D8]">
                {isBothIncomeType
                  ? "Both keeps employee income and self-employed income separate so withholding and estimated taxes do not get blended into one confusing number."
                  : isW2
                  ? "W-2 income stays paycheck-focused and uses withholding as informational guidance."
                  : "Self-employed income uses a tax reserve target so you can plan for money that is not already withheld."}
              </p>
            </div>

            {!isBothIncomeType ? (
              <>
                <div className="flex flex-col gap-2 text-sm">
                  <span className="text-[#9E4CFF] uppercase tracking-widest">Income Frequency</span>
                  <div className="flex flex-wrap gap-2">
                    {frequencyOptions.map((option) => (
                      <button
                        key={option}
                        type="button"
                        onClick={() => setIncomeFrequency(option)}
                        className={`px-3 py-1 rounded-full text-xs border transition-all ${incomeFrequency === option ? "bg-[#9E4CFF] text-black border-[#9E4CFF] shadow-[0_0_12px_#9E4CFF]" : "bg-black/70 border-[#2A0047] text-[#D8D8D8] hover:bg-black"}`}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                </div>

                {incomeFrequency === "Weekly" ? (
                  <div className="flex flex-col gap-2 text-sm">
                    <span className="text-[#9E4CFF] uppercase tracking-widest">Weekly Payday</span>
                    {renderWeekdaySelector(primaryPayday, setPrimaryPayday)}
                    <p className="text-[11px] text-[#D8D8D8]">This anchors the calendar month preview so the app can count how many weekly paychecks land in each month.</p>
                  </div>
                ) : null}

                {incomeFrequency === "Bi-weekly" ? (
                  <div className="flex flex-col gap-2 text-sm">
                    <span className="text-[#9E4CFF] uppercase tracking-widest">Last Paycheck Date</span>
                    <input type="date" value={primaryLastPaycheckDate} onChange={(event) => setPrimaryLastPaycheckDate(event.target.value)} className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#9E4CFF]" />
                    <p className="text-[11px] text-[#D8D8D8]">Use the most recent paycheck date you know. The app uses it as the anchor for bi-weekly month previews.</p>
                  </div>
                ) : null}

                {isW2 ? (
                  <div className="flex flex-col gap-2 text-sm">
                    <span className="text-[#9E4CFF] uppercase tracking-widest">W-2 Entry Mode</span>
                    <div className="flex flex-wrap gap-2">
                      {[
                        { id: "gross", label: "Gross Paycheck" },
                        { id: "take-home", label: "Take-Home Paycheck" },
                      ].map((option) => (
                        <button
                          key={option.id}
                          type="button"
                          onClick={() => setMixedIncome((current) => ({ ...current, w2InputMode: option.id }))}
                          className={`px-3 py-1 rounded-full text-xs border transition-all ${w2InputMode === option.id ? "bg-[#9E4CFF] text-black border-[#9E4CFF] shadow-[0_0_12px_#9E4CFF]" : "bg-black/70 border-[#2A0047] text-[#D8D8D8] hover:bg-black"}`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                    <p className="text-[11px] text-[#D8D8D8]">Gross mode plans from pre-tax pay. Take-home mode plans from what actually lands after withholding.</p>
                  </div>
                ) : null}

                {isW2 && incomeFrequency !== "Other" ? (
                  <div className="flex flex-col gap-2 text-sm">
                    <span className="text-[#9E4CFF] uppercase tracking-widest">W-2 Pay Style</span>
                    <div className="flex flex-wrap gap-2">
                      {[
                        { id: "fixed", label: "Fixed Paycheck" },
                        { id: "hourly", label: "Hourly Worker" },
                      ].map((option) => (
                        <button
                          key={option.id}
                          type="button"
                          onClick={() => setMixedIncome((current) => ({ ...current, w2PayStructure: option.id }))}
                          className={`px-3 py-1 rounded-full text-xs border transition-all ${w2PayStructure === option.id ? "bg-[#9E4CFF] text-black border-[#9E4CFF] shadow-[0_0_12px_#9E4CFF]" : "bg-black/70 border-[#2A0047] text-[#D8D8D8] hover:bg-black"}`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                    <p className="text-[11px] text-[#D8D8D8]">{w2PayStructure === "hourly" ? "Hourly mode estimates your current paycheck from hours worked. Update hours each pay period to keep the month realistic." : "Use this when your paycheck is usually the same from period to period."}</p>
                  </div>
                ) : null}

                {incomeFrequency === "Other" ? (
                  <div className="flex flex-col gap-3 text-sm">
                    <span className="text-[#9E4CFF] uppercase tracking-widest">Custom Pay Dates and Amounts</span>
                    <div className="flex flex-col gap-2 max-h-44 overflow-y-auto pr-1">
                      {customPayDates.map((row, index) => (
                        <div key={row.id} className="grid grid-cols-2 gap-2 items-center">
                          <input
                            type="date"
                            className="bg-black/70 border border-[#2A0047] rounded-md px-2 py-1 text-xs"
                            value={row.date}
                            onChange={(event) => {
                              const updated = [...customPayDates];
                              updated[index] = { ...row, date: event.target.value };
                              setCustomPayDates(updated);
                            }}
                          />
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              placeholder="$0.00"
                              className="flex-1 bg-black/70 border border-[#2A0047] rounded-md px-2 py-1 text-xs text-right"
                              value={row.amount}
                              onChange={(event) => {
                                const updated = [...customPayDates];
                                updated[index] = { ...row, amount: event.target.value };
                                setCustomPayDates(updated);
                              }}
                            />
                            {customPayDates.length > 1 ? (
                              <button type="button" className="text-xs text-red-300 hover:text-red-400" onClick={() => setCustomPayDates((previous) => previous.filter((_, rowIndex) => rowIndex !== index))}>
                                Remove
                              </button>
                            ) : null}
                          </div>
                        </div>
                      ))}
                    </div>
                    <button type="button" className="self-start px-3 py-1 rounded-full bg-[#9E4CFF] text-black text-xs font-semibold tracking-wide shadow-[0_0_12px_#9E4CFF]" onClick={() => setCustomPayDates((previous) => [...previous, { id: previous.length + 1, date: "", amount: "" }])}>
                      + Add Paycheck Entry
                    </button>
                  </div>
                ) : w2PayStructure === "hourly" && isW2 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <div className="flex flex-col gap-2">
                      <span className="text-[#9E4CFF] uppercase tracking-widest">Hourly Rate</span>
                      <input type="number" placeholder="$0.00" value={mixedIncome.w2HourlyRate} onChange={(event) => setMixedIncome((current) => ({ ...current, w2HourlyRate: event.target.value }))} className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#9E4CFF]" />
                    </div>
                    <div className="flex flex-col gap-2">
                      <span className="text-[#9E4CFF] uppercase tracking-widest">Typical Hours / Paycheck</span>
                      <input type="number" placeholder="e.g. 40 or 80" value={mixedIncome.w2TypicalHours} onChange={(event) => setMixedIncome((current) => ({ ...current, w2TypicalHours: event.target.value }))} className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#9E4CFF]" />
                    </div>
                    <div className="flex flex-col gap-2">
                      <span className="text-[#9E4CFF] uppercase tracking-widest">Hours Worked This Paycheck</span>
                      <input type="number" placeholder="Use actual hours for this pay period" value={mixedIncome.w2ActualHours} onChange={(event) => setMixedIncome((current) => ({ ...current, w2ActualHours: event.target.value }))} className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#9E4CFF]" />
                    </div>
                    <div className="flex flex-col gap-2">
                      <span className="text-[#9E4CFF] uppercase tracking-widest">Overtime Hours</span>
                      <input type="number" placeholder="Optional" value={mixedIncome.w2OvertimeHours} onChange={(event) => setMixedIncome((current) => ({ ...current, w2OvertimeHours: event.target.value }))} className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#9E4CFF]" />
                    </div>
                    <div className="flex flex-col gap-2 md:col-span-2">
                      <span className="text-[#9E4CFF] uppercase tracking-widest">{w2InputMode === "take-home" ? "Estimated Take-Home Paycheck" : "Estimated Gross Paycheck"}</span>
                      <div className="rounded-lg border border-[#2A0047] bg-black/50 px-3 py-3 text-white font-semibold">{formatCurrency(w2InputMode === "take-home" ? autoW2TakeHomePerPaycheck : autoW2GrossDisplayPerPaycheck)}</div>
                      <p className="text-[11px] text-[#D8D8D8]">This estimate updates from your hourly rate and the hours you worked this pay period.</p>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col gap-2 text-sm">
                    <span className="text-[#9E4CFF] uppercase tracking-widest">{isW2 && w2InputMode === "take-home" ? "Take-Home Income Amount" : "Base Income Amount"}</span>
                    <input type="number" placeholder="$0.00" value={baseIncome} onChange={(event) => setBaseIncome(event.target.value)} className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#9E4CFF]" />
                  </div>
                )}

                {taxReserveEnabled && taxSavingsTargetMonthly > 0 ? (
                  <div className="rounded-lg border border-[#2A0047] bg-black/50 px-3 py-3 text-xs text-[#D8D8D8] space-y-3">
                    <div className="flex justify-between"><span className="uppercase tracking-widest text-[#9E4CFF]">Suggested Tax Savings</span><span className="font-semibold">{formatCurrency(currentTaxSavingsTargetMonthly)} / mo</span></div>
                    <p>Save this separately so tax season stays manageable.{suggestedTaxPerPaycheck > 0 ? ` That is about ${formatCurrency(suggestedTaxPerPaycheck)} per paycheck.` : ""}</p>
                  </div>
                ) : null}

                {!isW2 ? (
                  <div className="pt-2 border-t border-[#2A0047] space-y-2 text-sm">
                    <span className="text-[#9E4CFF] uppercase tracking-widest">Write-off style</span>
                    <div className="flex flex-wrap gap-2">
                      {[{ id: "low", label: "Light" }, { id: "medium", label: "Medium" }, { id: "high", label: "Heavy" }, { id: "custom", label: "Custom" }].map((option) => (
                        <button key={option.id} type="button" onClick={() => setWriteOffMode(option.id)} className={`px-3 py-1 rounded-full border text-[11px] ${writeOffMode === option.id ? "bg-[#9E4CFF] text-black border-[#9E4CFF]" : "bg-black/70 text-[#D8D8D8] border-[#2A0047]"}`}>
                          {option.label}
                        </button>
                      ))}
                    </div>
                    {writeOffMode === "custom" ? (
                      <div className="flex items-center gap-2">
                        <input type="number" min={0} max={90} className="w-20 bg-black/70 border border-[#2A0047] rounded-md px-2 py-1 text-right text-xs" value={customWriteOffPercent} onChange={(event) => setCustomWriteOffPercent(event.target.value)} />
                        <span className="text-[11px] text-[#D8D8D8]">% estimated write-offs</span>
                      </div>
                    ) : null}
                    <p className="text-[11px] text-[#9E4CFF]">Planning estimate: about {Math.round(effectiveWriteOffPercent)}% written off.</p>
                  </div>
                ) : null}

                {isW2 && w2InputMode === "take-home" && w2PayStructure !== "hourly" ? (
                  <div className="flex flex-col gap-2 text-sm">
                    <span className="text-[#9E4CFF] uppercase tracking-widest">{incomeFrequency === "Other" ? "Actual Taxes Withheld Monthly" : "Actual Taxes Withheld Per Paycheck"}</span>
                    <input type="number" placeholder="$0.00" value={mixedIncome.w2WithholdingAmount} onChange={(event) => setMixedIncome((current) => ({ ...current, w2WithholdingAmount: event.target.value }))} className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#9E4CFF]" />
                    <p className="text-[11px] text-[#D8D8D8]">Optional override. Leave it blank and the app will estimate withholding from your payroll setup instead.</p>
                  </div>
                ) : null}

                {isW2 ? renderW2PayrollGuidance() : (
                  <div className="flex flex-col gap-2 text-sm">
                    <span className="text-[#9E4CFF] uppercase tracking-widest">Federal Tax / Withholding %</span>
                    <input type="number" placeholder="e.g. 22" value={taxPercent} onChange={(event) => setTaxPercent(event.target.value)} className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#9E4CFF]" />
                    <p className="text-[11px] text-[#D8D8D8]">{withholdingHandlesTaxes ? "Use this as an informational withholding estimate if payroll is already handling taxes." : businessTaxesHandledSeparately ? "Use this only as a rough planning input if your business or accountant handles taxes separately." : "Use this to estimate what you should set aside for taxes."}</p>
                  </div>
                )}
              </>
            ) : (
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <div className="rounded-2xl border border-[#2A0047] bg-black/40 p-4 flex flex-col gap-4">
                  <div>
                    <h4 className="text-lg font-semibold text-[#C9A6FF]">W-2 Income</h4>
                    <p className="text-xs text-[#D8D8D8] mt-1">Use your employee paycheck details here. Choose gross pay if you want the app to estimate withholding, or take-home if you want to plan from what actually lands in your bank account.</p>
                  </div>
                  {mixedIncome.w2Frequency === "Weekly" ? (
                    <div className="flex flex-col gap-2 text-sm">
                      <span className="text-[#9E4CFF] uppercase tracking-widest">W-2 Payday</span>
                      {renderWeekdaySelector(mixedIncome.w2Payday, (value) => updateMixedIncome("w2Payday", value))}
                    </div>
                  ) : null}
                  {mixedIncome.w2Frequency === "Bi-weekly" ? (
                    <div className="flex flex-col gap-2 text-sm">
                      <span className="text-[#9E4CFF] uppercase tracking-widest">Last W-2 Paycheck Date</span>
                      <input type="date" value={mixedIncome.w2LastPaycheckDate} onChange={(event) => updateMixedIncome("w2LastPaycheckDate", event.target.value)} className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#9E4CFF]" />
                    </div>
                  ) : null}
                  <div className="flex flex-col gap-2 text-sm">
                    <span className="text-[#9E4CFF] uppercase tracking-widest">W-2 Entry Mode</span>
                    <div className="flex flex-wrap gap-2">
                      {[
                        { id: "gross", label: "Gross Paycheck" },
                        { id: "take-home", label: "Take-Home Paycheck" },
                      ].map((option) => (
                        <button key={option.id} type="button" onClick={() => updateMixedIncome("w2InputMode", option.id)} className={
                          `px-3 py-1 rounded-full text-xs border transition-all ${mixedIncome.w2InputMode === option.id ? "bg-[#9E4CFF] text-black border-[#9E4CFF] shadow-[0_0_12px_#9E4CFF]" : "bg-black/70 border-[#2A0047] text-[#D8D8D8] hover:bg-black"}`
                        }>
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 text-sm">
                    <span className="text-[#9E4CFF] uppercase tracking-widest">W-2 Pay Style</span>
                    <div className="flex flex-wrap gap-2">
                      {[
                        { id: "fixed", label: "Fixed Paycheck" },
                        { id: "hourly", label: "Hourly Worker" },
                      ].map((option) => (
                        <button key={option.id} type="button" onClick={() => updateMixedIncome("w2PayStructure", option.id)} className={
                          `px-3 py-1 rounded-full text-xs border transition-all ${mixedIncome.w2PayStructure === option.id ? "bg-[#9E4CFF] text-black border-[#9E4CFF] shadow-[0_0_12px_#9E4CFF]" : "bg-black/70 border-[#2A0047] text-[#D8D8D8] hover:bg-black"}`
                        }>
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 text-sm">
                    <span className="text-[#9E4CFF] uppercase tracking-widest">Pay Frequency</span>
                    <div className="flex flex-wrap gap-2">
                      {w2FrequencyOptions.map((option) => (
                        <button key={option} type="button" onClick={() => updateMixedIncome("w2Frequency", option)} className={
                          `px-3 py-1 rounded-full text-xs border transition-all ${mixedIncome.w2Frequency === option ? "bg-[#9E4CFF] text-black border-[#9E4CFF] shadow-[0_0_12px_#9E4CFF]" : "bg-black/70 border-[#2A0047] text-[#D8D8D8] hover:bg-black"}`
                        }>
                          {option}
                        </button>
                      ))}
                    </div>
                  </div>
                  {mixedIncome.w2PayStructure === "hourly" ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                      <div className="flex flex-col gap-2">
                        <span className="text-[#9E4CFF] uppercase tracking-widest">Hourly Rate</span>
                        <input type="number" placeholder="$0.00" value={mixedIncome.w2HourlyRate} onChange={(event) => updateMixedIncome("w2HourlyRate", event.target.value)} className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#9E4CFF]" />
                      </div>
                      <div className="flex flex-col gap-2">
                        <span className="text-[#9E4CFF] uppercase tracking-widest">Typical Hours / Paycheck</span>
                        <input type="number" placeholder="e.g. 40 or 80" value={mixedIncome.w2TypicalHours} onChange={(event) => updateMixedIncome("w2TypicalHours", event.target.value)} className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#9E4CFF]" />
                      </div>
                      <div className="flex flex-col gap-2">
                        <span className="text-[#9E4CFF] uppercase tracking-widest">Hours Worked This Paycheck</span>
                        <input type="number" placeholder="Use actual hours for this pay period" value={mixedIncome.w2ActualHours} onChange={(event) => updateMixedIncome("w2ActualHours", event.target.value)} className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#9E4CFF]" />
                      </div>
                      <div className="flex flex-col gap-2">
                        <span className="text-[#9E4CFF] uppercase tracking-widest">Overtime Hours</span>
                        <input type="number" placeholder="Optional" value={mixedIncome.w2OvertimeHours} onChange={(event) => updateMixedIncome("w2OvertimeHours", event.target.value)} className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#9E4CFF]" />
                      </div>
                      <div className="flex flex-col gap-2 md:col-span-2">
                        <span className="text-[#9E4CFF] uppercase tracking-widest">{mixedIncome.w2InputMode === "take-home" ? "Estimated Take-Home Paycheck" : "Estimated Gross Paycheck"}</span>
                        <div className="rounded-lg border border-[#2A0047] bg-black/50 px-3 py-3 text-white font-semibold">{formatCurrency(mixedIncome.w2InputMode === "take-home" ? autoW2TakeHomePerPaycheck : autoW2GrossDisplayPerPaycheck)}</div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-2 text-sm">
                      <span className="text-[#9E4CFF] uppercase tracking-widest">{mixedIncome.w2InputMode === "take-home" ? "Take-Home Paycheck Amount" : "Gross Paycheck Amount"}</span>
                      <input type="number" placeholder="$0.00" value={mixedIncome.w2BaseIncome} onChange={(event) => updateMixedIncome("w2BaseIncome", event.target.value)} className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#9E4CFF]" />
                    </div>
                  )}
                  {mixedIncome.w2InputMode === "take-home" && mixedIncome.w2PayStructure !== "hourly" ? (
                    <div className="flex flex-col gap-2 text-sm">
                      <span className="text-[#9E4CFF] uppercase tracking-widest">Actual Taxes Withheld Per Paycheck</span>
                      <input type="number" placeholder="$0.00" value={mixedIncome.w2WithholdingAmount} onChange={(event) => updateMixedIncome("w2WithholdingAmount", event.target.value)} className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#9E4CFF]" />
                      <p className="text-[11px] text-[#D8D8D8]">Optional override. Leave it blank and the app will estimate withholding from your payroll setup instead.</p>
                    </div>
                  ) : null}
                  {renderW2PayrollGuidance()}
                </div>

                <div className="rounded-2xl border border-[#2A0047] bg-black/40 p-4 flex flex-col gap-4">
                  <div>
                    <h4 className="text-lg font-semibold text-[#C9A6FF]">1099 / Self-Employed Income</h4>
                    <p className="text-xs text-[#D8D8D8] mt-1">Use this for money you need to plan taxes on yourself. The app uses this side for estimated-tax guidance.</p>
                  </div>
                  <div className="flex flex-col gap-2 text-sm">
                    <span className="text-[#9E4CFF] uppercase tracking-widest">Income Frequency</span>
                    <div className="flex flex-wrap gap-2">
                      {frequencyOptions.map((option) => (
                        <button key={option} type="button" onClick={() => updateMixedIncome("contractorFrequency", option)} className={`px-3 py-1 rounded-full text-xs border transition-all ${mixedIncome.contractorFrequency === option ? "bg-[#9E4CFF] text-black border-[#9E4CFF] shadow-[0_0_12px_#9E4CFF]" : "bg-black/70 border-[#2A0047] text-[#D8D8D8] hover:bg-black"}`}>
                          {option}
                        </button>
                      ))}
                    </div>
                  </div>
                  {mixedIncome.contractorFrequency === "Weekly" ? (
                    <div className="flex flex-col gap-2 text-sm">
                      <span className="text-[#9E4CFF] uppercase tracking-widest">1099 Payday</span>
                      {renderWeekdaySelector(mixedIncome.contractorPayday, (value) => updateMixedIncome("contractorPayday", value))}
                    </div>
                  ) : null}
                  {mixedIncome.contractorFrequency === "Bi-weekly" ? (
                    <div className="flex flex-col gap-2 text-sm">
                      <span className="text-[#9E4CFF] uppercase tracking-widest">Last 1099 Paycheck Date</span>
                      <input type="date" value={mixedIncome.contractorLastPaycheckDate} onChange={(event) => updateMixedIncome("contractorLastPaycheckDate", event.target.value)} className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#9E4CFF]" />
                    </div>
                  ) : null}
                  {mixedIncome.contractorFrequency === "Other" ? renderContractorCustomRows() : (
                    <div className="flex flex-col gap-2 text-sm">
                      <span className="text-[#9E4CFF] uppercase tracking-widest">1099 Income Amount</span>
                      <input type="number" placeholder="$0.00" value={mixedIncome.contractorBaseIncome} onChange={(event) => updateMixedIncome("contractorBaseIncome", event.target.value)} className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#9E4CFF]" />
                    </div>
                  )}
                  <div className="flex flex-col gap-2 text-sm">
                    <span className="text-[#9E4CFF] uppercase tracking-widest">Estimated Tax %</span>
                    <input type="number" placeholder="e.g. 25" value={mixedIncome.contractorTaxPercent} onChange={(event) => updateMixedIncome("contractorTaxPercent", event.target.value)} className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#9E4CFF]" />
                  </div>
                  <div className="pt-2 border-t border-[#2A0047] space-y-2 text-sm">
                    <span className="text-[#9E4CFF] uppercase tracking-widest">Write-off style</span>
                    <div className="flex flex-wrap gap-2">
                      {[{ id: "low", label: "Light" }, { id: "medium", label: "Medium" }, { id: "high", label: "Heavy" }, { id: "custom", label: "Custom" }].map((option) => (
                        <button key={option.id} type="button" onClick={() => updateMixedIncome("contractorWriteOffMode", option.id)} className={`px-3 py-1 rounded-full border text-[11px] ${mixedIncome.contractorWriteOffMode === option.id ? "bg-[#9E4CFF] text-black border-[#9E4CFF]" : "bg-black/70 text-[#D8D8D8] border-[#2A0047]"}`}>
                          {option.label}
                        </button>
                      ))}
                    </div>
                    {mixedIncome.contractorWriteOffMode === "custom" ? (
                      <div className="flex items-center gap-2">
                        <input type="number" min={0} max={90} className="w-20 bg-black/70 border border-[#2A0047] rounded-md px-2 py-1 text-right text-xs" value={mixedIncome.contractorCustomWriteOffPercent} onChange={(event) => updateMixedIncome("contractorCustomWriteOffPercent", event.target.value)} />
                        <span className="text-[11px] text-[#D8D8D8]">% estimated write-offs</span>
                      </div>
                    ) : null}
                    <p className="text-[11px] text-[#9E4CFF]">Planning estimate: about {Math.round(effectiveWriteOffPercent)}% written off on the 1099 side.</p>
                  </div>
                </div>
              </div>
            )}
          </SectionCard>

          <SectionCard title="Projected Monthly Income" subtitle={isBothIncomeType ? "Your W-2 and 1099 streams stay separate here, then combine into one planning number." : "This card separates calendar reality from your longer-range planning baseline."} dark>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div><span className="text-[#9E4CFF] uppercase tracking-widest text-xs">{(isBothIncomeType || isW2) && w2InputMode === "take-home" ? "Estimated Current Gross" : "Current Month Gross"}</span><div className="text-2xl font-semibold text-white">{formatCurrency(currentGrossMonthly)}</div></div>
              <div><span className="text-[#9E4CFF] uppercase tracking-widest text-xs">Current Month Planning Income</span><div className="text-2xl font-semibold text-white">{formatCurrency(planningIncomeMonthly)}</div></div>
              <div><span className="text-[#9E4CFF] uppercase tracking-widest text-xs">Average Monthly Gross</span><div className="text-sm font-semibold text-white">{formatCurrency(averageGrossMonthly)}</div></div>
              <div><span className="text-[#9E4CFF] uppercase tracking-widest text-xs">Annual Planning Baseline</span><div className="text-sm font-semibold text-white">{formatCurrency(annualNet)}</div></div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_220px] gap-3 border-t border-[#2A0047] pt-3 text-sm">
              <div className="rounded-xl border border-[#2A0047] bg-black/40 px-3 py-3">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-[#9E4CFF] uppercase tracking-widest text-xs">{currentMonthLabel}</span>
                  <span className="font-semibold text-white">{formatCurrency(currentGrossMonthly)}</span>
                </div>
                <p className="mt-1 text-[11px] text-[#D8D8D8]">This is the calendar-month income preview for the month you are in right now.</p>
              </div>
              <div className="flex flex-col gap-2">
                <span className="text-[#9E4CFF] uppercase tracking-widest text-xs">Preview Month</span>
                <input type="month" value={incomePreviewMonth} onChange={(event) => setIncomePreviewMonth(event.target.value || getCurrentMonthKey())} className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#9E4CFF]" />
              </div>
            </div>
            <div className="rounded-xl border border-[#2A0047] bg-black/30 px-3 py-3 text-xs text-[#D8D8D8]">
              <div className="flex items-center justify-between gap-3">
                <span className="text-[#9E4CFF] uppercase tracking-widest">{previewMonthLabel}</span>
                <span className="font-semibold text-white">{formatCurrency(previewGrossMonthly)}</span>
              </div>
              <p className="mt-1">Preview only. This does not change your planning calculations, goals, transactions, or expense planning.</p>
            </div>
            {isBothIncomeType ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 border-t border-[#2A0047] pt-3 text-xs text-[#D8D8D8]">
                <div className="rounded-xl border border-[#2A0047] bg-black/40 px-3 py-3">
                  <div className="flex justify-between"><span className="text-[#9E4CFF] uppercase tracking-widest">{w2InputMode === "take-home" ? "Current W-2 Take-Home" : "Current W-2 Gross"}</span><span className="font-semibold">{formatCurrency(w2InputMode === "take-home" ? currentW2PlanningMonthly : currentW2GrossMonthly)}</span></div>
                  <p className="mt-1 text-[11px]">{w2InputMode === "take-home" ? `Paystub taxes withheld: ${formatCurrency(currentTaxWithheldMonthly)} / mo` : `Estimated withholding: ${formatCurrency(currentTaxWithheldMonthly)} / mo`}</p>
                </div>
                <div className="rounded-xl border border-[#2A0047] bg-black/40 px-3 py-3">
                  <div className="flex justify-between"><span className="text-[#9E4CFF] uppercase tracking-widest">Current 1099 Gross</span><span className="font-semibold">{formatCurrency(currentContractorGrossMonthly)}</span></div>
                  <p className="mt-1 text-[11px]">Current tax reserve target: {formatCurrency(currentTaxSavingsTargetMonthly)} / mo</p>
                </div>
              </div>
            ) : null}
            {!isBothIncomeType && (isW2 || selectedTaxPaymentStyle === "withholding") && currentTaxWithheldMonthly > 0 ? <div className="border-t border-[#2A0047] pt-3 text-xs text-[#D8D8D8]"><div className="flex justify-between"><span className="text-[#9E4CFF] uppercase tracking-widest">{isW2 && w2InputMode === "take-home" ? "Paystub Taxes Withheld" : "Estimated Withholding"}</span><span className="font-semibold">{formatCurrency(currentTaxWithheldMonthly)} / mo</span></div></div> : null}
            {((!isBothIncomeType && incomeFrequency === "Other") || (isBothIncomeType && mixedIncome.contractorFrequency === "Other")) && monthTotals.length > 0 ? <div className="text-xs text-[#D8D8D8] flex flex-col gap-1 border-t border-[#2A0047] pt-3"><span className="text-[#9E4CFF] uppercase tracking-widest">Monthly gross from custom dates</span>{monthTotals.map((month) => <div key={month.key} className="flex justify-between"><span>{month.label}</span><span>{formatCurrency(month.total)}</span></div>)}</div> : null}
          </SectionCard>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <SectionCard title="Deductions" subtitle="Choose a percentage or flat amount for deductions you expect most months.">
            <div className="hidden sm:grid grid-cols-3 text-xs font-semibold text-[#9E4CFF] uppercase tracking-widest"><span>Type</span><span>Mode</span><span className="text-right">Value</span></div>
            <div className="flex flex-col gap-2 text-sm max-h-52 overflow-y-auto pr-1">
              {deductions.map((deduction, index) => (
                <div key={deduction.name} className="bg-black/40 rounded-lg px-3 py-3 flex flex-col gap-2 sm:grid sm:grid-cols-3 sm:items-center sm:gap-2">
                  <span className="text-sm sm:text-xs text-[#D8D8D8]">{deduction.name}</span>
                  <div className="grid grid-cols-2 gap-2 sm:contents">
                    <select className="bg-black/70 border border-[#2A0047] rounded-md px-2 py-2 text-sm sm:text-xs" value={deduction.mode} onChange={(event) => { const updated = [...deductions]; updated[index] = { ...deduction, mode: event.target.value }; setDeductions(updated); }}>
                      <option value="%">%</option>
                      <option value="$">$</option>
                    </select>
                    <input type="number" placeholder="0" className="bg-black/70 border border-[#2A0047] rounded-md px-2 py-2 text-sm sm:text-xs text-right" value={deduction.value} onChange={(event) => { const updated = [...deductions]; updated[index] = { ...deduction, value: event.target.value }; setDeductions(updated); }} />
                  </div>
                </div>
              ))}
            </div>
            <div className="border-t border-[#2A0047] pt-3 flex justify-between gap-3 text-sm"><span className="text-[#9E4CFF] uppercase tracking-widest text-xs">Total monthly deductions</span><span className="font-semibold text-white text-right">{formatCurrency(otherDeductionsMonthly)}</span></div>
          </SectionCard>

          <SectionCard title="Additional Income" subtitle="Track extra income here so your plan reflects the full picture, not just one paycheck." dark>
            <div className="hidden sm:grid grid-cols-4 text-xs font-semibold text-[#9E4CFF] uppercase tracking-widest"><span>Source</span><span>Frequency</span><span className="col-span-2 text-right">Amount</span></div>
            <div className="flex flex-col gap-2 text-sm max-h-44 overflow-y-auto pr-1">
              {additionalIncomes.map((row, index) => (
                <div key={row.id} className="bg-[#130018] rounded-lg px-3 py-3 flex flex-col gap-2 sm:grid sm:grid-cols-4 sm:items-center sm:gap-2">
                  <input type="text" placeholder="e.g. Uber" className="bg-black/70 border border-[#2A0047] rounded-md px-2 py-2 text-sm sm:text-xs" value={row.source} onChange={(event) => { const updated = [...additionalIncomes]; updated[index] = { ...row, source: event.target.value }; setAdditionalIncomes(updated); }} />
                  <select className="bg-black/70 border border-[#2A0047] rounded-md px-2 py-2 text-sm sm:text-xs" value={row.frequency} onChange={(event) => { const updated = [...additionalIncomes]; updated[index] = { ...row, frequency: event.target.value }; setAdditionalIncomes(updated); }}>
                    <option>Weekly</option><option>Bi-weekly</option><option>Monthly</option><option>One-time</option>
                  </select>
                  <input type="number" placeholder="$0.00" className="sm:col-span-2 bg-black/70 border border-[#2A0047] rounded-md px-2 py-2 text-sm sm:text-xs text-right" value={row.amount} onChange={(event) => { const updated = [...additionalIncomes]; updated[index] = { ...row, amount: event.target.value }; setAdditionalIncomes(updated); }} />
                </div>
              ))}
            </div>
            <button className="self-start px-3 py-2 rounded-full bg-[#9E4CFF] text-black text-sm sm:text-xs font-semibold tracking-wide shadow-[0_0_12px_#9E4CFF]" onClick={() => setAdditionalIncomes((previous) => [...previous, createAdditionalIncomeRow()])}>+ Add Income Source</button>
            <div className="border-t border-[#2A0047] pt-3 flex flex-col gap-1 text-sm"><div className="flex justify-between"><span className="text-[#9E4CFF] uppercase tracking-widest text-xs">Total additional monthly</span><span className="font-semibold text-white">{formatCurrency(additionalMonthly)}</span></div><div className="flex justify-between font-semibold"><span className="text-[#C9A6FF]">Planning income after tax reserve</span><span className="text-white">{formatCurrency(planningIncomeMonthly)}</span></div></div>
          </SectionCard>
        </div>
      </div>
    );
  };
  const renderIncomeOverview = () => (
    <div>
      <h2 className="text-2xl font-semibold text-[#C9A6FF] mb-4">Income Overview</h2>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <StatTile label="Planning Income" value={formatCurrency(planningIncomeMonthly)} />
        <StatTile label="Planned Monthly Expenses" value={formatCurrency(totalMonthlyExpenses)} />
        <StatTile label="Monthly Debt Payments" value={formatCurrency(totalMonthlyDebt)} />
        <StatTile label="Left After All Bills" value={formatCurrency(leftoverMonthly)} muted={leftoverMonthly < 0} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SectionCard title="Income Allocation" subtitle="How your current planning income is being allocated across your monthly plan." dark>
          <div className="h-64 w-full"><ResponsiveContainer width="100%" height="100%"><BarChart data={[{ name: "Expenses", value: totalMonthlyExpenses }, { name: "Debt", value: totalMonthlyDebt }, { name: "Leftover", value: leftoverPositive }]}><XAxis dataKey="name" stroke="#9E4CFF" tick={{ fill: "#C9A6FF" }} /><YAxis stroke="#9E4CFF" tick={{ fill: "#C9A6FF" }} /><Tooltip contentStyle={{ background: "#130018", border: "1px solid #2A0047", color: "#D8D8D8" }} labelStyle={{ color: "#C9A6FF" }} formatter={(value) => formatCurrency(value)} /><Bar dataKey="value">{["#9E4CFF", "#AF6BFF", "#FF6BD5"].map((color, index) => <Cell key={index} fill={color} />)}</Bar></BarChart></ResponsiveContainer></div>
          <div className="text-xs text-[#D8D8D8] space-y-1"><p>Fixed essentials use about {Math.round(safePercent(fixedMonthlyExpenses, planningIncomeMonthly))}% of your planning income.</p><p>Flexible spending uses about {Math.round(safePercent(flexibleMonthlyExpenses, planningIncomeMonthly))}%.</p><p>Debt uses about {Math.round(debtPercentOfNet)}%.</p><p>That leaves roughly {Math.round(leftoverPercentOfNet)}% free for goals or breathing room.</p></div>
        </SectionCard>
        <SectionCard title="Top Planned Categories" subtitle="These are the biggest parts of your monthly plan, so even small adjustments here can change your leftover meaningfully.">
          {topExpenseCategories.length === 0 ? <p className="text-xs text-[#D8D8D8]">Add a few planned expense categories first and this section will show where most of your monthly plan is concentrated.</p> : <div className="flex flex-col gap-3 text-sm">{topExpenseCategories.map((category, index) => <div key={`${category.name}-${index}`} className="flex flex-col gap-1 bg-black/40 rounded-lg px-3 py-2"><div className="flex justify-between items-center"><span className="text-[#C9A6FF] font-semibold">{index + 1}. {category.name}</span><span className="text-xs text-[#D8D8D8]">{formatCurrency(category.amount)}/mo</span></div><div className="flex gap-2 flex-wrap text-[11px] text-[#D8D8D8]"><span className="px-2 py-0.5 rounded-full bg-black/60 border border-[#2A0047]">{Math.round(category.percentOfNet)}% of planning income</span><span className="px-2 py-0.5 rounded-full bg-black/60 border border-[#2A0047]">{Math.round(category.percentOfExpenses)}% of planned expenses</span><span className="px-2 py-0.5 rounded-full bg-black/60 border border-[#2A0047]">{category.planType === "fixed" ? "Fixed essential" : "Flexible"}</span></div></div>)}</div>}
          {largestFlexibleCategory ? <div className="mt-4 rounded-xl border border-[#2A0047] bg-black/40 p-4"><p className="text-[#C9A6FF] font-semibold">Largest flexible category</p><p className="mt-2 text-xs text-[#D8D8D8]">{largestFlexibleCategory.name} is your largest flexible category at {formatCurrency(largestFlexibleCategory.amount)} per month. This is usually the easiest place to create breathing room without touching fixed essentials.</p></div> : null}
        </SectionCard>
      </div>
    </div>
  );

  const renderControlCenter = () => (
    <div>
      <h2 className="text-2xl font-semibold text-[#C9A6FF] mb-4">Monthly Control Center</h2>
      <p className="text-sm text-[#D8D8D8] mb-6 max-w-3xl">Use this as your home screen. It pulls together what is coming in, what must go out, what is hurting the month, and what to do next without making you jump through the rest of the app first.</p>
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-6">
        {operatingRhythmCards.map((card) => (
          <SectionCard key={card.id} title={`${card.label} Rhythm`} subtitle={card.title} dark={card.id === "weekly"}>
            <div className="flex flex-col gap-4 text-sm">
              <p className="text-[#D8D8D8]">{card.body}</p>
              <button
                type="button"
                onClick={() => openTab(card.tab, { quickCapture: card.quickCapture })}
                className="self-start px-4 py-2 rounded-lg bg-[#9E4CFF] text-black font-semibold tracking-wide shadow-[0_0_16px_#9E4CFF]"
              >
                {card.actionLabel}
              </button>
            </div>
          </SectionCard>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-6"><StatTile label={`${currentMonthLabel} Planning Income`} value={formatCurrency(planningIncomeMonthly)} /><StatTile label="Fixed Essentials" value={formatCurrency(fixedMonthlyExpenses)} /><StatTile label="Flexible Plan" value={formatCurrency(flexibleMonthlyExpenses)} /><StatTile label="Debt + Tax Pressure" value={formatCurrency(totalMonthlyDebt + currentTaxSavingsTargetMonthly)} /><StatTile label="Left This Month" value={formatCurrency(leftoverMonthly)} muted={leftoverMonthly < 0} /></div>
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-6">
        <SectionCard title="Required Outflows" subtitle="The monthly pressure that usually has to be covered before the month feels safe.">
          <div className="flex flex-col gap-3 text-sm">
            <div className="rounded-xl border border-[#2A0047] bg-black/40 px-4 py-3 flex items-center justify-between gap-3"><span>Fixed essentials</span><span className="font-semibold text-white">{formatCurrency(fixedMonthlyExpenses)}</span></div>
            <div className="rounded-xl border border-[#2A0047] bg-black/40 px-4 py-3 flex items-center justify-between gap-3"><span>Debt payments</span><span className="font-semibold text-white">{formatCurrency(totalMonthlyDebt)}</span></div>
            <div className="rounded-xl border border-[#2A0047] bg-black/40 px-4 py-3 flex items-center justify-between gap-3"><span>Tax reserve</span><span className="font-semibold text-white">{formatCurrency(currentTaxSavingsTargetMonthly)}</span></div>
            <div className="rounded-xl border border-[#2A0047] bg-[#130018] px-4 py-3 flex items-center justify-between gap-3"><span className="text-[#C9A6FF] font-semibold">Required outflow total</span><span className="font-semibold text-white">{formatCurrency(requiredMonthlyOutflow)}</span></div>
          </div>
        </SectionCard>
        <SectionCard title={leftoverMonthly < 0 ? "Biggest Pressure This Month" : leftoverMonthly === 0 ? "This Month At A Glance" : "Current Month Position"} subtitle={leftoverMonthly < 0 ? "The fastest read on what is pushing the month off balance." : leftoverMonthly === 0 ? "A fast read on what is using up most of the month." : "A fast read on what is taking room and what is still working in your favor."} dark>
          <div className="flex flex-col gap-4 text-sm">
            <div className="rounded-xl border border-[#2A0047] bg-[#130018] p-4">
              <p className="text-[#C9A6FF] font-semibold">{leftoverMonthly < 0 ? "Bleeding Money" : leftoverMonthly === 0 ? "Break-Even Month" : "Current Cushion"}</p>
              <p className="mt-2 text-[#D8D8D8]">{leftoverMonthly < 0 ? `You are short by ${formatCurrency(currentMonthShortfall)} this month.` : leftoverMonthly === 0 ? "You are exactly break-even right now, so there is no real cushion for surprises." : `You currently have about ${formatCurrency(leftoverPositive)} of room left this month.`}</p>
            </div>
            <div className="rounded-xl border border-[#2A0047] bg-black/40 p-4">
              <p className="text-[#C9A6FF] font-semibold">Leading pressure source</p>
              <p className="mt-2 text-[#D8D8D8]">{topPressureSignal ? `${topPressureSignal.label} at ${formatCurrency(topPressureSignal.amount)} is the largest direct pressure on this month.` : "There is no major pressure source yet because the monthly plan is still sparse."}</p>
            </div>
            <div className="rounded-xl border border-[#2A0047] bg-black/40 p-4">
              <p className="text-[#C9A6FF] font-semibold">Real activity so far</p>
              <p className="mt-2 text-[#D8D8D8]">{currentMonthTransactions.length > 0 ? `You have logged ${currentMonthTransactions.length} transaction${currentMonthTransactions.length === 1 ? "" : "s"} for ${currentMonthLabel}. Net cash movement is ${formatCurrency(currentMonthTransactionNetCashFlow)}.` : `No transactions are logged for ${currentMonthLabel} yet, so the app only knows the plan right now.`}</p>
              {currentMonthTransactions.length > 0 ? <p className="mt-2 text-xs text-[#D8D8D8]">Tracked expense activity is currently {currentMonthTrackedPlanGap > 0 ? `${formatCurrency(currentMonthTrackedPlanGap)} over` : `${formatCurrency(Math.abs(currentMonthTrackedPlanGap))} under`} the full planned expense amount for the month.</p> : null}
            </div>
          </div>
        </SectionCard>
        <SectionCard title="Next Best Move" subtitle="One practical action based on the numbers you have right now.">
          <div className="flex flex-col gap-4 text-sm">
            <div className="rounded-xl border border-[#2A0047] bg-black/40 p-4">
              <p className="text-[#C9A6FF] font-semibold">{nextBestMove.title}</p>
              <p className="mt-2 text-[#D8D8D8]">{nextBestMove.body}</p>
            </div>
            <button
              type="button"
              onClick={() => openTab(nextBestMove.tab, { quickCapture: nextBestMove.tab === "transactions" })}
              className="self-start px-4 py-2 rounded-lg bg-[#9E4CFF] text-black font-semibold tracking-wide shadow-[0_0_16px_#9E4CFF]"
            >
              {nextBestMove.actionLabel}
            </button>
            {largestFlexibleCategory ? <div className="rounded-xl border border-[#2A0047] bg-black/40 p-4 text-xs text-[#D8D8D8]">Largest flexible category right now: <span className="font-semibold text-white">{largestFlexibleCategory.name}</span> at {formatCurrency(largestFlexibleCategory.amount)} / mo.</div> : null}
          </div>
        </SectionCard>
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <SectionCard title="Planned vs Actual Direction" subtitle="A quick comparison between the monthly plan and the real activity logged so far.">
          {currentMonthTransactions.length === 0 ? <p className="text-sm text-[#D8D8D8]">Add transactions this month and this section will show whether actual spending is tracking above or below your plan.</p> : <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm"><div className="rounded-xl border border-[#2A0047] bg-black/40 px-4 py-4"><span className="text-[11px] uppercase tracking-widest text-[#9E4CFF]">Actual Expenses</span><div className="text-lg font-semibold mt-1 text-white">{formatCurrency(currentMonthActualExpense)}</div></div><div className="rounded-xl border border-[#2A0047] bg-black/40 px-4 py-4"><span className="text-[11px] uppercase tracking-widest text-[#9E4CFF]">Actual Income</span><div className="text-lg font-semibold mt-1 text-white">{formatCurrency(currentMonthActualIncome)}</div></div><div className="rounded-xl border border-[#2A0047] bg-black/40 px-4 py-4"><span className="text-[11px] uppercase tracking-widest text-[#9E4CFF]">Net Cash Movement</span><div className="text-lg font-semibold mt-1 text-white">{formatCurrency(currentMonthTransactionNetCashFlow)}</div></div></div>}
        </SectionCard>
        <SectionCard title={controlCenterBreakdownTitle} subtitle={controlCenterBreakdownSubtitle} dark>
          <div className="h-64 w-full"><ResponsiveContainer width="100%" height="100%"><BarChart data={[{ name: "Fixed", value: fixedMonthlyExpenses }, { name: "Flexible", value: flexibleMonthlyExpenses }, { name: "Debt", value: totalMonthlyDebt }, { name: "Tax", value: currentTaxSavingsTargetMonthly }, { name: "Leftover", value: Math.max(0, leftoverMonthly) }]}><XAxis dataKey="name" stroke="#9E4CFF" tick={{ fill: "#C9A6FF" }} /><YAxis stroke="#9E4CFF" tick={{ fill: "#C9A6FF" }} /><Tooltip contentStyle={{ background: "#130018", border: "1px solid #2A0047", color: "#D8D8D8" }} labelStyle={{ color: "#C9A6FF" }} formatter={(value) => formatCurrency(value)} /><Bar dataKey="value">{["#9E4CFF", "#AF6BFF", "#FF9BE5", "#FF6BD5", "#CFFBE4"].map((color, index) => <Cell key={index} fill={color} />)}</Bar></BarChart></ResponsiveContainer></div>
          {topCurrentMonthActualCategories[0] ? <p className="mt-4 text-xs text-[#D8D8D8]">Largest tracked spending category this month: <span className="font-semibold text-white">{topCurrentMonthActualCategories[0].name}</span> at {formatCurrency(topCurrentMonthActualCategories[0].amount)}.</p> : null}
        </SectionCard>
      </div>
    </div>
  );

  const goToNextLiteStep = () => {
    if (liteOnboardingStep === "welcome") setLiteOnboardingStep("income");
    if (liteOnboardingStep === "income") setLiteOnboardingStep("expenses");
    if (liteOnboardingStep === "expenses") setLiteOnboardingStep("result");
  };

  const goToPreviousLiteStep = () => {
    if (liteOnboardingStep === "income") setLiteOnboardingStep("welcome");
    if (liteOnboardingStep === "expenses") setLiteOnboardingStep("income");
    if (liteOnboardingStep === "result") setLiteOnboardingStep("expenses");
  };

  const finishLiteOnboarding = () => {
    if (isDemoMode) {
      setDemoLiteComplete(true);
    } else {
      setLiteOnboardingComplete(true);
    }
    setLiteOnboardingStep("done");
    setActiveTab("overview");
  };

  const renderLiteUpgradePanel = (title = "Want more control?", variant = "full") => (
    <div className={`rounded-2xl border border-[#2A0047] bg-[#130018] ${variant === "compact" ? "p-4 sm:p-5" : "p-4 sm:p-5"} flex flex-col gap-3`}>
      <h3 className={`${variant === "compact" ? "text-base sm:text-lg" : "text-lg"} font-semibold text-white text-center`}>{title}</h3>
      {variant === "compact" ? (
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-left">
            {[
              "Plan your future",
              "Get smart insights",
              "Optimize your money",
            ].map((label) => (
              <button
                key={label}
                type="button"
                onClick={() => {
                  const target = lockedLiteTabs.find((tab) => tab.label.startsWith(label));
                  if (!target) return;
                  setSelectedLiteUpgrade(target);
                  setShowLiteUpgradePrompt(true);
                }}
                className="rounded-xl border border-[#2A0047] bg-black/30 px-3 py-2.5 text-left hover:bg-black/50 transition-colors"
              >
                <p className="text-sm font-semibold text-[#C9A6FF]">{label} 🔒</p>
              </button>
            ))}
          </div>
          <p className="text-xs text-[#D8D8D8] text-center">Unlock FLOW System for goals, guidance, and stronger monthly control.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {lockedLiteTabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => {
                setSelectedLiteUpgrade(tab);
                setShowLiteUpgradePrompt(true);
              }}
              className="rounded-xl border border-[#2A0047] bg-black/30 px-4 py-3 text-left hover:bg-black/50 transition-colors"
            >
              <p className="text-sm font-semibold text-[#C9A6FF]">{tab.label}</p>
              <p className="mt-2 text-xs text-[#D8D8D8]">{tab.body}</p>
            </button>
          ))}
        </div>
      )}
    </div>
  );

  // eslint-disable-next-line no-unused-vars
  const renderLiteOnboardingWelcome = () => (
    <div className="min-h-[70vh] flex items-center justify-center">
      <div className="w-full max-w-2xl rounded-[28px] border border-[#2A0047] bg-black/70 p-8 sm:p-10 text-center shadow-2xl">
        <span className="text-[11px] uppercase tracking-[0.3em] text-[#9E4CFF]">Flow Lite</span>
        <h2 className="mt-4 text-4xl sm:text-5xl font-semibold text-white">Welcome to FLOW Lite</h2>
        <p className="mt-4 text-base text-[#D8D8D8] max-w-xl mx-auto">
          Let&apos;s get a clear picture of your money in under 2 minutes.
        </p>
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3 text-left">
          {["Add your income", "Add your expenses", "See what’s left"].map((item, index) => (
            <div key={item} className="rounded-2xl border border-[#2A0047] bg-black/30 px-4 py-4">
              <span className="text-[11px] uppercase tracking-[0.22em] text-[#9E4CFF]">Step {index + 1}</span>
              <p className="mt-2 text-sm font-semibold text-white">{item}</p>
            </div>
          ))}
        </div>
        {isDemoMode ? <p className="mt-6 text-xs text-[#C9A6FF]">Demo mode: explore freely. Changes stay in this browser session only.</p> : null}
        <button
          type="button"
          onClick={goToNextLiteStep}
          className="mt-8 w-full rounded-xl bg-[#9E4CFF] px-4 py-3 font-semibold text-black shadow-[0_0_20px_#9E4CFF]"
        >
          Start Setup
        </button>
      </div>
    </div>
  );

  // eslint-disable-next-line no-unused-vars
  const renderLiteOnboardingIncome = () => (
    <div className="min-h-[70vh] flex items-center justify-center">
      <div className="w-full max-w-3xl rounded-[28px] border border-[#2A0047] bg-black/70 p-8 sm:p-10 shadow-2xl flex flex-col gap-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-sm text-[#C9A6FF] uppercase tracking-[0.22em]">Step 1 of 3</p>
            <h2 className="mt-2 text-3xl sm:text-4xl font-semibold text-white">Your income</h2>
            <p className="mt-2 text-sm text-[#D8D8D8]">Add the income you can count on this month.</p>
          </div>
          <button type="button" onClick={goToPreviousLiteStep} className="px-3 py-2 rounded-lg border border-[#2A0047] bg-black/40 text-[#D8D8D8] text-sm font-semibold hover:bg-black/60">
            Back
          </button>
        </div>
        <div className="rounded-2xl border border-[#2A0047] bg-black/30 p-4 grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_160px] gap-3">
          <div className="flex flex-col gap-2">
            <span className="text-[11px] uppercase tracking-widest text-[#9E4CFF]">Income amount</span>
            <input type="number" value={baseIncome} onChange={(event) => setBaseIncome(event.target.value)} placeholder="$0.00" className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#9E4CFF]" />
          </div>
          <div className="flex flex-col gap-2">
            <span className="text-[11px] uppercase tracking-widest text-[#9E4CFF]">Frequency</span>
            <select value={incomeFrequency} onChange={(event) => setIncomeFrequency(event.target.value)} className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#9E4CFF]">
              {["Weekly", "Bi-weekly", "Monthly"].map((option) => <option key={option} value={option}>{option}</option>)}
            </select>
          </div>
        </div>
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between gap-3">
            <p className="text-base font-semibold text-[#C9A6FF]">Additional income</p>
            <button type="button" onClick={() => setAdditionalIncomes((previous) => [...previous, createAdditionalIncomeRow()])} className="px-3 py-2 rounded-lg bg-[#9E4CFF] text-black text-sm font-semibold shadow-[0_0_16px_#9E4CFF]">
              + Add Income Source
            </button>
          </div>
          <div className="flex flex-col gap-3">
            {additionalIncomes.map((row, index) => (
              <div key={row.id} className="rounded-xl border border-[#2A0047] bg-black/30 p-3 grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_140px_140px_96px] gap-2">
                <input type="text" placeholder="Source name (optional)" value={row.source} onChange={(event) => {
                  const updated = [...additionalIncomes];
                  updated[index] = { ...row, source: event.target.value };
                  setAdditionalIncomes(updated);
                }} className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#9E4CFF]" />
                <select value={row.frequency} onChange={(event) => {
                  const updated = [...additionalIncomes];
                  updated[index] = { ...row, frequency: event.target.value };
                  setAdditionalIncomes(updated);
                }} className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#9E4CFF]">
                  {["Weekly", "Bi-weekly", "Monthly"].map((option) => <option key={option} value={option}>{option}</option>)}
                </select>
                <input type="number" placeholder="$0.00" value={row.amount} onChange={(event) => {
                  const updated = [...additionalIncomes];
                  updated[index] = { ...row, amount: event.target.value };
                  setAdditionalIncomes(updated);
                }} className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#9E4CFF]" />
                <button type="button" onClick={() => setAdditionalIncomes((previous) => previous.filter((item) => item.id !== row.id))} className="px-3 py-2 rounded-lg border border-[#5C1734] bg-[#2A0012] text-[#FFB7D5] text-xs font-semibold hover:bg-[#3A001A]">
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-2xl border border-[#2A0047] bg-[#130018] px-5 py-4">
          <p className="text-sm text-[#D8D8D8]">Estimated Monthly Income</p>
          <p className="mt-2 text-4xl font-semibold text-white">{formatCurrency(liteIncomeMonthly)}</p>
        </div>
        <button
          type="button"
          onClick={goToNextLiteStep}
          disabled={!hasLiteIncomeSetup}
          className="w-full rounded-xl bg-[#9E4CFF] px-4 py-3 font-semibold text-black shadow-[0_0_20px_#9E4CFF] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Continue
        </button>
      </div>
    </div>
  );

  // eslint-disable-next-line no-unused-vars
  const renderLiteOnboardingExpenses = () => (
    <div className="min-h-[70vh] flex items-center justify-center">
      <div className="w-full max-w-3xl rounded-[28px] border border-[#2A0047] bg-black/70 p-8 sm:p-10 shadow-2xl flex flex-col gap-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-sm text-[#C9A6FF] uppercase tracking-[0.22em]">Step 2 of 3</p>
            <h2 className="mt-2 text-3xl sm:text-4xl font-semibold text-white">Your expenses</h2>
            <p className="mt-2 text-sm text-[#D8D8D8]">Start with the essentials. Add anything else your month needs.</p>
          </div>
          <button type="button" onClick={goToPreviousLiteStep} className="px-3 py-2 rounded-lg border border-[#2A0047] bg-black/40 text-[#D8D8D8] text-sm font-semibold hover:bg-black/60">
            Back
          </button>
        </div>
        <div className="flex flex-col gap-3">
          {liteOnboardingExpenseRows.map((row) => (
            <div key={row.id} className="rounded-xl border border-[#2A0047] bg-black/30 p-3 grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_140px_96px] gap-2">
              <input type="text" value={row.name} onChange={(event) => updateExpenseRow(row.id, { name: event.target.value })} placeholder="Expense name" className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#9E4CFF]" />
              <input type="number" value={row.amount} onChange={(event) => updateExpenseRow(row.id, { amount: event.target.value })} placeholder="0.00" className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#9E4CFF]" />
              <button type="button" onClick={() => removeExpenseRow(row.id)} className="px-3 py-2 rounded-lg border border-[#5C1734] bg-[#2A0012] text-[#FFB7D5] text-xs font-semibold hover:bg-[#3A001A]">
                Remove
              </button>
            </div>
          ))}
          <div className="rounded-2xl border border-dashed border-[#2A0047] bg-black/20 p-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-sm font-semibold text-white">Need another expense?</p>
                <p className="mt-1 text-xs text-[#D8D8D8]">Add a custom row or use one of the common options below.</p>
              </div>
              <button
                type="button"
                onClick={() => addExpenseRow("flexible", { isLiteAdded: true })}
                className="rounded-lg bg-[#9E4CFF] px-4 py-2 text-sm font-semibold tracking-wide text-black shadow-[0_0_16px_#9E4CFF]"
              >
                + Add Expense
              </button>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {liteExpenseQuickAdds.map((preset) => (
                <button
                  key={preset.name}
                  type="button"
                  onClick={() => addLiteExpensePreset(preset)}
                  className="rounded-full border border-[#2A0047] bg-black/50 px-3 py-2 text-xs font-semibold text-[#D8D8D8] hover:border-[#9E4CFF] hover:text-white"
                >
                  + {preset.name}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="rounded-2xl border border-[#2A0047] bg-[#130018] px-5 py-4">
          <p className="text-sm text-[#D8D8D8]">Monthly Expenses</p>
          <p className="mt-2 text-4xl font-semibold text-white">{formatCurrency(liteExpensesMonthly)}</p>
        </div>
        <button
          type="button"
          onClick={goToNextLiteStep}
          disabled={!hasLiteExpenseSetup}
          className="w-full rounded-xl bg-[#9E4CFF] px-4 py-3 font-semibold text-black shadow-[0_0_20px_#9E4CFF] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          See My Result
        </button>
      </div>
    </div>
  );

  const renderLiteOnboardingResult = () => (
    <div className="min-h-[70vh] flex items-center justify-center">
      <div className="w-full max-w-4xl rounded-[28px] border border-[#2A0047] bg-black/70 p-8 sm:p-10 shadow-2xl flex flex-col gap-8 text-center">
        <div>
          <p className="text-sm text-[#C9A6FF] uppercase tracking-[0.22em]">Step 3 of 3</p>
          <h2 className={`mt-4 text-4xl sm:text-6xl font-semibold ${liteLeftoverMonthly < 0 ? "text-[#FF9BE5]" : "text-white"}`}>{liteResultHeadline}</h2>
          <p className="mt-4 text-sm sm:text-base text-[#D8D8D8] max-w-2xl mx-auto">{liteResultSupportText}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
          <div className="rounded-2xl border border-[#2A0047] bg-black/40 p-5">
            <p className="text-sm text-[#D8D8D8]">Income</p>
            <p className="mt-2 text-3xl font-semibold text-white">{formatCurrency(liteIncomeMonthly)}</p>
          </div>
          <div className="rounded-2xl border border-[#2A0047] bg-black/40 p-5">
            <p className="text-sm text-[#D8D8D8]">Expenses</p>
            <p className="mt-2 text-3xl font-semibold text-white">{formatCurrency(liteExpensesMonthly)}</p>
          </div>
        </div>
        {renderLiteUpgradePanel("Want more control?", "compact")}
        <div className="flex flex-col sm:flex-row gap-3 pt-4 sm:pt-6 w-full max-w-xl mx-auto">
          <button type="button" onClick={goToPreviousLiteStep} className="w-full rounded-xl border border-[#2A0047] bg-black/40 px-4 py-3 font-semibold text-[#D8D8D8] hover:bg-black/60">
            Back
          </button>
          <button type="button" onClick={finishLiteOnboarding} className="w-full rounded-xl bg-[#9E4CFF] px-4 py-3 font-semibold text-black shadow-[0_0_20px_#9E4CFF]">
            Go to Dashboard
          </button>
        </div>
      </div>
    </div>
  );

  const renderLiteOnboarding = () => {
    switch (liteOnboardingStep) {
      case "welcome":
        return renderLiteOnboardingWelcomeV2();
      case "income":
        return renderLiteOnboardingIncomeV2();
      case "expenses":
        return renderLiteOnboardingExpensesV2();
      case "result":
        return renderLiteOnboardingResult();
      default:
        return renderLiteOnboardingWelcomeV2();
    }
  };

  // eslint-disable-next-line no-unused-vars
  const renderLiteDashboard = () => (
    <div className="flex flex-col gap-6">
      <div>
        <h2 className="text-2xl font-semibold text-[#C9A6FF] mb-3">Overview</h2>
        <p className="text-sm text-[#D8D8D8] max-w-3xl">A fast monthly snapshot of what is coming in, what is going out, and what is left.</p>
      </div>
      <SectionCard title="Left This Month" subtitle="The number that matters most in Flow Lite." dark>
        <div className="flex flex-col gap-4">
          <div className={`text-5xl sm:text-6xl font-semibold ${liteLeftoverMonthly < 0 ? "text-[#FF9BE5]" : "text-white"}`}>
            {formatCurrency(liteLeftoverMonthly)}
          </div>
          <p className="text-sm text-[#D8D8D8] max-w-2xl">{liteResultSupportText}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="rounded-xl border border-[#2A0047] bg-black/40 px-4 py-4">
              <span className="text-[11px] uppercase tracking-widest text-[#9E4CFF]">Income</span>
              <div className="mt-2 text-2xl font-semibold text-white">{formatCurrency(liteIncomeMonthly)}</div>
            </div>
            <div className="rounded-xl border border-[#2A0047] bg-black/40 px-4 py-4">
              <span className="text-[11px] uppercase tracking-widest text-[#9E4CFF]">Expenses</span>
              <div className="mt-2 text-2xl font-semibold text-white">{formatCurrency(liteExpensesMonthly)}</div>
            </div>
          </div>
        </div>
      </SectionCard>
      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.1fr)_minmax(300px,0.9fr)] gap-6">
        <SectionCard title="Monthly Snapshot" subtitle="A simple visual of income, expenses, and what is left.">
          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={[{ name: "Income", value: liteIncomeMonthly }, { name: "Expenses", value: liteExpensesMonthly }, { name: "Left", value: liteLeftoverPositive }]}>
                <XAxis dataKey="name" stroke="#9E4CFF" tick={{ fill: "#C9A6FF" }} />
                <YAxis stroke="#9E4CFF" tick={{ fill: "#C9A6FF" }} />
                <Tooltip contentStyle={{ background: "#130018", border: "1px solid #2A0047", color: "#D8D8D8" }} labelStyle={{ color: "#C9A6FF" }} formatter={(value) => formatCurrency(value)} />
                <Bar dataKey="value">{["#C9A6FF", "#8D63E0", "#CFFBE4"].map((color, index) => <Cell key={index} fill={color} />)}</Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>
        {renderLiteUpgradePanel()}
      </div>
    </div>
  );

  // eslint-disable-next-line no-unused-vars
  const renderLiteIncome = () => (
    <div>
      <h2 className="text-2xl font-semibold text-[#C9A6FF] mb-4">Income</h2>
      <p className="text-sm text-[#D8D8D8] mb-6 max-w-3xl">Keep this simple. Add your main income and any extra monthly income sources.</p>
      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.3fr)_320px] gap-6">
        <SectionCard title="Income Sources" subtitle="Flow Lite converts these inputs into one monthly total.">
          <div className="flex flex-col gap-5">
            <div className="rounded-2xl border border-[#2A0047] bg-black/30 p-4 grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_160px] gap-3">
              <div className="flex flex-col gap-2">
                <span className="text-[11px] uppercase tracking-widest text-[#9E4CFF]">Primary Income Amount</span>
                <input type="number" value={baseIncome} onChange={(event) => setBaseIncome(event.target.value)} placeholder="$0.00" className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#9E4CFF]" />
              </div>
              <div className="flex flex-col gap-2">
                <span className="text-[11px] uppercase tracking-widest text-[#9E4CFF]">Frequency</span>
                <select value={incomeFrequency} onChange={(event) => setIncomeFrequency(event.target.value)} className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#9E4CFF]">
                  {["Weekly", "Bi-weekly", "Monthly"].map((option) => <option key={option} value={option}>{option}</option>)}
                </select>
              </div>
            </div>
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between gap-3">
                <p className="text-base font-semibold text-[#C9A6FF]">Additional Income</p>
                <button type="button" onClick={() => setAdditionalIncomes((previous) => [...previous, createAdditionalIncomeRow()])} className="px-3 py-2 rounded-lg bg-[#9E4CFF] text-black text-sm font-semibold shadow-[0_0_16px_#9E4CFF]">+ Add Income Source</button>
              </div>
              <div className="flex flex-col gap-3">
                {additionalIncomes.map((row, index) => (
                  <div key={row.id} className="rounded-xl border border-[#2A0047] bg-black/30 p-3 grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_140px_140px_96px] gap-2">
                    <input type="text" placeholder="Source name (optional)" value={row.source} onChange={(event) => {
                      const updated = [...additionalIncomes];
                      updated[index] = { ...row, source: event.target.value };
                      setAdditionalIncomes(updated);
                    }} className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#9E4CFF]" />
                    <select value={row.frequency} onChange={(event) => {
                      const updated = [...additionalIncomes];
                      updated[index] = { ...row, frequency: event.target.value };
                      setAdditionalIncomes(updated);
                    }} className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#9E4CFF]">
                      {["Weekly", "Bi-weekly", "Monthly"].map((option) => <option key={option} value={option}>{option}</option>)}
                    </select>
                    <input type="number" placeholder="$0.00" value={row.amount} onChange={(event) => {
                      const updated = [...additionalIncomes];
                      updated[index] = { ...row, amount: event.target.value };
                      setAdditionalIncomes(updated);
                    }} className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#9E4CFF]" />
                    <button type="button" onClick={() => setAdditionalIncomes((previous) => previous.filter((item) => item.id !== row.id))} className="px-3 py-2 rounded-lg border border-[#5C1734] bg-[#2A0012] text-[#FFB7D5] text-xs font-semibold hover:bg-[#3A001A]">Remove</button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </SectionCard>
        <SectionCard title="Monthly Income Total" subtitle="The monthly total after converting the income above." dark>
          <div className="text-4xl font-semibold text-white">{formatCurrency(liteIncomeMonthly)}</div>
          <p className="mt-3 text-sm text-[#D8D8D8]">Primary income is converted into a monthly amount and any extra income sources are added on top.</p>
        </SectionCard>
      </div>
    </div>
  );

  // eslint-disable-next-line no-unused-vars
  const renderLiteExpenses = () => (
    <div>
      <h2 className="text-2xl font-semibold text-[#C9A6FF] mb-4">Expenses</h2>
      <p className="text-sm text-[#D8D8D8] mb-6 max-w-3xl">Add the categories you expect to pay this month so Flow Lite can show where your money is going.</p>
      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.3fr)_320px] gap-6">
        <SectionCard title="Monthly Expense Categories" subtitle="Keep this simple: expense name and monthly amount.">
          <div className="flex flex-col gap-3">
            {expenses.map((row) => (
              <div key={row.id} className="rounded-xl border border-[#2A0047] bg-black/30 p-3 grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_140px_96px] gap-2">
                <input type="text" value={row.name} onChange={(event) => updateExpenseRow(row.id, { name: event.target.value })} placeholder="Expense name" className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#9E4CFF]" />
                <input type="number" value={row.amount} onChange={(event) => updateExpenseRow(row.id, { amount: event.target.value })} placeholder="0.00" className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#9E4CFF]" />
                <button type="button" onClick={() => removeExpenseRow(row.id)} className="px-3 py-2 rounded-lg border border-[#5C1734] bg-[#2A0012] text-[#FFB7D5] text-xs font-semibold hover:bg-[#3A001A]">Remove</button>
              </div>
            ))}
            <button type="button" onClick={() => addExpenseRow("flexible")} className="self-start px-4 py-2 rounded-lg bg-[#9E4CFF] text-black text-sm font-semibold tracking-wide shadow-[0_0_16px_#9E4CFF]">+ Add Expense</button>
          </div>
        </SectionCard>
        <SectionCard title="Expense Total" subtitle="The total monthly amount across the categories you entered." dark>
          <div className="text-4xl font-semibold text-white">{formatCurrency(liteExpensesMonthly)}</div>
          <div className="mt-4 flex flex-col gap-2">
            {expenses.filter((row) => row.name.trim() || parseNumber(row.amount) > 0).slice(0, 5).map((row) => (
              <div key={row.id} className="rounded-xl border border-[#2A0047] bg-black/40 px-4 py-3 flex items-center justify-between gap-3 text-sm">
                <span className="text-[#D8D8D8]">{row.name || "Untitled Expense"}</span>
                <span className="font-semibold text-white">{formatCurrency(parseNumber(row.amount))}</span>
              </div>
            ))}
          </div>
        </SectionCard>
      </div>
    </div>
  );

  // eslint-disable-next-line no-unused-vars
  const renderLiteSummary = () => (
    <div className="flex flex-col gap-6">
      <div>
        <h2 className="text-2xl font-semibold text-[#C9A6FF] mb-3">Summary</h2>
        <p className="text-sm text-[#D8D8D8] max-w-3xl">A clean monthly summary of what came in, what goes out, and what is left.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="rounded-2xl border border-[#2A0047] bg-black/40 p-5">
          <p className="text-[11px] uppercase tracking-widest text-[#9E4CFF]">Income</p>
          <p className="mt-2 text-3xl font-semibold text-white">{formatCurrency(liteIncomeMonthly)}</p>
        </div>
        <div className="rounded-2xl border border-[#2A0047] bg-black/40 p-5">
          <p className="text-[11px] uppercase tracking-widest text-[#9E4CFF]">Expenses</p>
          <p className="mt-2 text-3xl font-semibold text-white">{formatCurrency(liteExpensesMonthly)}</p>
        </div>
        <div className="rounded-2xl border border-[#2A0047] bg-black/40 p-5">
          <p className="text-[11px] uppercase tracking-widest text-[#9E4CFF]">Left This Month</p>
          <p className={`mt-2 text-3xl font-semibold ${liteLeftoverMonthly < 0 ? "text-[#FF9BE5]" : "text-white"}`}>{formatCurrency(liteLeftoverMonthly)}</p>
        </div>
      </div>
      <div className="max-w-3xl">
        {renderLiteUpgradePanel("Unlock more with FLOW System", "compact")}
      </div>
    </div>
  );

  const renderLiteSnapshotVisual = () => (
    <div className="rounded-2xl border border-[#2A0047] bg-black/30 p-5 flex flex-col gap-4">
      {liteSnapshotBars.map((bar) => (
        <div key={bar.label} className="flex flex-col gap-2">
          <div className="flex items-center justify-between gap-3 text-sm">
            <span className="text-[#D8D8D8]">{bar.label}</span>
            <span className="font-semibold text-white">{formatCurrency(bar.value)}</span>
          </div>
          <div className="h-3 rounded-full bg-black/60 overflow-hidden">
            <div className={`${bar.color} h-full rounded-full transition-all`} style={{ width: `${Math.max((bar.value / liteSnapshotMax) * 100, 4)}%` }} />
          </div>
        </div>
      ))}
    </div>
  );

  const renderLiteOnboardingWelcomeV2 = () => (
    <div className="min-h-[70vh] flex items-center justify-center">
      <div className="w-full max-w-2xl rounded-[28px] border border-[#2A0047] bg-black/70 p-8 sm:p-10 text-center shadow-2xl">
        <span className="text-[11px] uppercase tracking-[0.3em] text-[#9E4CFF]">Flow Lite</span>
        <h2 className="mt-4 text-4xl sm:text-5xl font-semibold text-white">Welcome to FLOW Lite</h2>
        <p className="mt-4 text-base text-[#D8D8D8] max-w-xl mx-auto">
          Let&apos;s get a clear picture of your money in under 2 minutes.
        </p>
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3 text-left">
          {["Add your income", "Add your expenses", "See what's left"].map((item, index) => (
            <div key={item} className="rounded-2xl border border-[#2A0047] bg-black/30 px-4 py-4">
              <span className="text-[11px] uppercase tracking-[0.22em] text-[#9E4CFF]">Step {index + 1}</span>
              <p className="mt-2 text-sm font-semibold text-white">{item}</p>
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={goToNextLiteStep}
          className="mt-8 w-full rounded-xl bg-[#9E4CFF] px-4 py-3 font-semibold text-black shadow-[0_0_20px_#9E4CFF]"
        >
          Start Setup
        </button>
      </div>
    </div>
  );

  const renderLiteOnboardingIncomeV2 = () => (
    <div className="min-h-[70vh] flex items-center justify-center">
      <div className="w-full max-w-3xl rounded-[28px] border border-[#2A0047] bg-black/70 p-8 sm:p-10 shadow-2xl flex flex-col gap-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-sm text-[#C9A6FF] uppercase tracking-[0.22em]">Step 1 of 3</p>
            <h2 className="mt-2 text-3xl sm:text-4xl font-semibold text-white">Your income</h2>
            <p className="mt-2 text-sm text-[#D8D8D8]">How much money do you receive each month?</p>
          </div>
          <button type="button" onClick={goToPreviousLiteStep} className="px-3 py-2 rounded-lg border border-[#2A0047] bg-black/40 text-[#D8D8D8] text-sm font-semibold hover:bg-black/60">
            Back
          </button>
        </div>
        <div className="rounded-2xl border border-[#2A0047] bg-black/30 p-4 grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_160px] gap-3">
          <div className="flex flex-col gap-2">
            <span className="text-[11px] uppercase tracking-widest text-[#9E4CFF]">Income amount</span>
            <input type="number" value={baseIncome} onChange={(event) => setBaseIncome(event.target.value)} placeholder="$0.00" className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#9E4CFF]" />
          </div>
          <div className="flex flex-col gap-2">
            <span className="text-[11px] uppercase tracking-widest text-[#9E4CFF]">Frequency</span>
            <select value={incomeFrequency} onChange={(event) => setIncomeFrequency(event.target.value)} className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#9E4CFF]">
              {["Weekly", "Bi-weekly", "Monthly"].map((option) => <option key={option} value={option}>{option}</option>)}
            </select>
          </div>
        </div>
        <div className="flex flex-col gap-3">
          {!showLiteAdditionalIncomeInputs && liteVisibleAdditionalIncomes.length === 0 ? (
            <div className="rounded-xl border border-dashed border-[#2A0047] bg-black/20 px-4 py-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-[#D8D8D8]">Optional: add extra income only if it matters this month.</p>
              <button
                type="button"
                onClick={() => {
                  setShowLiteAdditionalIncomeInputs(true);
                  setAdditionalIncomes((previous) =>
                    [...removeEmptyAdditionalIncomeRows(previous), createAdditionalIncomeRow()]
                  );
                }}
                className="px-3 py-2 rounded-lg bg-[#9E4CFF] text-black text-sm font-semibold shadow-[0_0_16px_#9E4CFF]"
              >
                + Add Income Source
              </button>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between gap-3">
                <p className="text-base font-semibold text-[#C9A6FF]">Additional income</p>
                <button type="button" onClick={() => setAdditionalIncomes((previous) => [...previous, createAdditionalIncomeRow()])} className="px-3 py-2 rounded-lg bg-[#9E4CFF] text-black text-sm font-semibold shadow-[0_0_16px_#9E4CFF]">
                  + Add Income Source
                </button>
              </div>
              <div className="flex flex-col gap-3">
                {liteVisibleAdditionalIncomes.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-[#2A0047] bg-black/20 px-4 py-4 text-sm text-[#D8D8D8]">
                    No extra income added yet.
                  </div>
                ) : liteVisibleAdditionalIncomes.map((row) => {
                  const index = additionalIncomes.findIndex((item) => item.id === row.id);
                  return (
                    <div key={row.id} className="rounded-xl border border-[#2A0047] bg-black/30 p-3 grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_140px_140px_96px] gap-2">
                      <input type="text" placeholder="Source name (optional)" value={row.source} onChange={(event) => {
                        const updated = [...additionalIncomes];
                        updated[index] = { ...row, source: event.target.value };
                        setAdditionalIncomes(updated);
                      }} className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#9E4CFF]" />
                      <select value={row.frequency} onChange={(event) => {
                        const updated = [...additionalIncomes];
                        updated[index] = { ...row, frequency: event.target.value };
                        setAdditionalIncomes(updated);
                      }} className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#9E4CFF]">
                        {["Weekly", "Bi-weekly", "Monthly"].map((option) => <option key={option} value={option}>{option}</option>)}
                      </select>
                      <input type="number" placeholder="$0.00" value={row.amount} onChange={(event) => {
                        const updated = [...additionalIncomes];
                        updated[index] = { ...row, amount: event.target.value };
                        setAdditionalIncomes(updated);
                      }} className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#9E4CFF]" />
                      <button type="button" onClick={() => setAdditionalIncomes((previous) => previous.filter((item) => item.id !== row.id))} className="px-3 py-2 rounded-lg border border-[#5C1734] bg-[#2A0012] text-[#FFB7D5] text-xs font-semibold hover:bg-[#3A001A]">
                        Remove
                      </button>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
        <div className="rounded-2xl border border-[#2A0047] bg-[#130018] px-5 py-4">
          <p className="text-sm text-[#D8D8D8]">Estimated Monthly Income</p>
          <p className="mt-2 text-4xl font-semibold text-white">{formatCurrency(liteIncomeMonthly)}</p>
        </div>
        <button
          type="button"
          onClick={goToNextLiteStep}
          disabled={!hasLiteIncomeSetup}
          className="w-full rounded-xl bg-[#9E4CFF] px-4 py-3 font-semibold text-black shadow-[0_0_20px_#9E4CFF] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Continue
        </button>
      </div>
    </div>
  );

  const renderLiteOnboardingExpensesV2 = () => (
    <div className="min-h-[70vh] flex items-center justify-center">
      <div className="w-full max-w-3xl rounded-[28px] border border-[#2A0047] bg-black/70 p-8 sm:p-10 shadow-2xl flex flex-col gap-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-sm text-[#C9A6FF] uppercase tracking-[0.22em]">Step 2 of 3</p>
            <h2 className="mt-2 text-3xl sm:text-4xl font-semibold text-white">Your expenses</h2>
            <p className="mt-2 text-sm text-[#D8D8D8]">Start with the essentials. Add anything else your month needs.</p>
          </div>
          <button type="button" onClick={goToPreviousLiteStep} className="px-3 py-2 rounded-lg border border-[#2A0047] bg-black/40 text-[#D8D8D8] text-sm font-semibold hover:bg-black/60">
            Back
          </button>
        </div>
        <div className="flex flex-col gap-3">
          {liteOnboardingExpenseRows.map((row, index) => (
            <div key={row.id} className="rounded-xl border border-[#2A0047] bg-black/30 p-4 grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_150px_92px] gap-3 items-center">
              <div className="flex flex-col gap-1.5">
                <label className="text-[10px] uppercase tracking-[0.18em] text-[#9E4CFF]" htmlFor={`lite-onboarding-expense-name-${row.id}`}>Expense</label>
                <input
                  id={`lite-onboarding-expense-name-${row.id}`}
                  type="text"
                  value={row.name}
                  onChange={(event) => updateExpenseRow(row.id, { name: event.target.value })}
                  placeholder={`Expense ${index + 1}`}
                  className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#9E4CFF]"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-[10px] uppercase tracking-[0.18em] text-[#9E4CFF]" htmlFor={`lite-onboarding-expense-amount-${row.id}`}>Monthly</label>
                <input
                  id={`lite-onboarding-expense-amount-${row.id}`}
                  type="number"
                  value={row.amount}
                  onChange={(event) => updateExpenseRow(row.id, { amount: event.target.value })}
                  placeholder="0.00"
                  className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#9E4CFF]"
                />
              </div>
              <button
                type="button"
                onClick={() => removeExpenseRow(row.id)}
                disabled={liteOnboardingExpenseRows.length <= 1}
                className="self-end px-3 py-2 rounded-lg border border-[#5C1734] bg-[#2A0012] text-[#FFB7D5] text-xs font-semibold hover:bg-[#3A001A] disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Remove
              </button>
            </div>
          ))}
          <div className="rounded-2xl border border-dashed border-[#2A0047] bg-black/20 p-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-sm font-semibold text-white">Need another expense?</p>
                <p className="mt-1 text-xs text-[#D8D8D8]">Add a custom row or use one of the common options below.</p>
              </div>
              <button
                type="button"
                onClick={() => addExpenseRow("flexible", { isLiteAdded: true })}
                className="rounded-lg bg-[#9E4CFF] px-4 py-2 text-sm font-semibold tracking-wide text-black shadow-[0_0_16px_#9E4CFF]"
              >
                + Add Expense
              </button>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {liteExpenseQuickAdds.map((preset) => (
                <button
                  key={preset.name}
                  type="button"
                  onClick={() => addLiteExpensePreset(preset)}
                  className="rounded-full border border-[#2A0047] bg-black/50 px-3 py-2 text-xs font-semibold text-[#D8D8D8] hover:border-[#9E4CFF] hover:text-white"
                >
                  + {preset.name}
                </button>
              ))}
            </div>
          </div>
          <p className="text-xs text-[#D8D8D8]">You can add more details later. This setup only needs enough to calculate what is left this month.</p>
        </div>
        <div className="rounded-2xl border border-[#2A0047] bg-[#130018] px-5 py-4">
          <p className="text-sm text-[#D8D8D8]">Monthly Expenses</p>
          <p className="mt-2 text-4xl font-semibold text-white">{formatCurrency(liteExpensesMonthly)}</p>
        </div>
        <button
          type="button"
          onClick={goToNextLiteStep}
          disabled={!hasLiteExpenseSetup}
          className="w-full rounded-xl bg-[#9E4CFF] px-4 py-3 font-semibold text-black shadow-[0_0_20px_#9E4CFF] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          See My Result
        </button>
      </div>
    </div>
  );

  const renderLiteDashboardV2 = () => (
    <div className="flex flex-col gap-6">
      <SectionCard title="Left This Month" subtitle="The number that matters most in Flow Lite." dark>
        <div className="flex flex-col gap-4">
          <div className={`text-5xl sm:text-6xl font-semibold ${liteLeftoverMonthly < 0 ? "text-[#FF9BE5]" : "text-white"}`}>
            {formatCurrency(liteLeftoverMonthly)}
          </div>
          <p className="text-sm text-[#D8D8D8] max-w-2xl">{liteResultSupportText}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="rounded-xl border border-[#2A0047] bg-black/40 px-4 py-4">
              <span className="text-[11px] uppercase tracking-widest text-[#9E4CFF]">Income</span>
              <div className="mt-2 text-2xl font-semibold text-white">{formatCurrency(liteIncomeMonthly)}</div>
            </div>
            <div className="rounded-xl border border-[#2A0047] bg-black/40 px-4 py-4">
              <span className="text-[11px] uppercase tracking-widest text-[#9E4CFF]">Expenses</span>
              <div className="mt-2 text-2xl font-semibold text-white">{formatCurrency(liteExpensesMonthly)}</div>
            </div>
          </div>
        </div>
      </SectionCard>
      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_minmax(320px,0.9fr)] gap-6">
      <SectionCard title="Monthly Snapshot" subtitle="A simple visual of income, expenses, and what is left.">
          {renderLiteSnapshotVisual()}
        </SectionCard>
        <div className="max-w-3xl">
          {renderLiteUpgradePanel("Unlock more with FLOW System", "compact")}
        </div>
      </div>
    </div>
  );

  const renderLiteIncomeV2 = () => (
    <div>
      <h2 className="text-2xl font-semibold text-[#C9A6FF] mb-4">Income</h2>
      <p className="text-sm text-[#D8D8D8] mb-6 max-w-3xl">Keep this simple. Add your main income and any extra monthly income sources.</p>
      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.3fr)_320px] gap-6">
        <SectionCard title="Income Sources" subtitle="Flow Lite converts these inputs into one monthly total.">
          <div className="flex flex-col gap-5">
            <div className="rounded-2xl border border-[#2A0047] bg-black/30 p-4 grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_160px] gap-3">
              <div className="flex flex-col gap-2">
                <span className="text-[11px] uppercase tracking-widest text-[#9E4CFF]">Primary Income Amount</span>
                <input type="number" value={baseIncome} onChange={(event) => setBaseIncome(event.target.value)} placeholder="$0.00" className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#9E4CFF]" />
              </div>
              <div className="flex flex-col gap-2">
                <span className="text-[11px] uppercase tracking-widest text-[#9E4CFF]">Frequency</span>
                <select value={incomeFrequency} onChange={(event) => setIncomeFrequency(event.target.value)} className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#9E4CFF]">
                  {["Weekly", "Bi-weekly", "Monthly"].map((option) => <option key={option} value={option}>{option}</option>)}
                </select>
              </div>
            </div>
            <div className="flex flex-col gap-3">
              {!showLiteAdditionalIncomeInputs && liteVisibleAdditionalIncomes.length === 0 ? (
                <div className="rounded-xl border border-dashed border-[#2A0047] bg-black/20 px-4 py-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-sm text-[#D8D8D8]">No extra income added yet.</p>
                  <button
                    type="button"
                    onClick={() => {
                      setShowLiteAdditionalIncomeInputs(true);
                      setAdditionalIncomes((previous) =>
                        [...removeEmptyAdditionalIncomeRows(previous), createAdditionalIncomeRow()]
                      );
                    }}
                    className="px-3 py-2 rounded-lg bg-[#9E4CFF] text-black text-sm font-semibold shadow-[0_0_16px_#9E4CFF]"
                  >
                    + Add Income Source
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-base font-semibold text-[#C9A6FF]">Additional Income</p>
                    <button type="button" onClick={() => setAdditionalIncomes((previous) => [...previous, createAdditionalIncomeRow()])} className="px-3 py-2 rounded-lg bg-[#9E4CFF] text-black text-sm font-semibold shadow-[0_0_16px_#9E4CFF]">+ Add Income Source</button>
                  </div>
                  <div className="flex flex-col gap-3">
                    {liteVisibleAdditionalIncomes.length === 0 ? (
                      <div className="rounded-xl border border-dashed border-[#2A0047] bg-black/20 px-4 py-4 text-sm text-[#D8D8D8]">
                        No extra income added yet.
                      </div>
                    ) : liteVisibleAdditionalIncomes.map((row) => {
                      const index = additionalIncomes.findIndex((item) => item.id === row.id);
                      return (
                        <div key={row.id} className="rounded-xl border border-[#2A0047] bg-black/30 p-3 grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_140px_140px_96px] gap-2">
                          <input type="text" placeholder="Source name (optional)" value={row.source} onChange={(event) => {
                            const updated = [...additionalIncomes];
                            updated[index] = { ...row, source: event.target.value };
                            setAdditionalIncomes(updated);
                          }} className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#9E4CFF]" />
                          <select value={row.frequency} onChange={(event) => {
                            const updated = [...additionalIncomes];
                            updated[index] = { ...row, frequency: event.target.value };
                            setAdditionalIncomes(updated);
                          }} className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#9E4CFF]">
                            {["Weekly", "Bi-weekly", "Monthly"].map((option) => <option key={option} value={option}>{option}</option>)}
                          </select>
                          <input type="number" placeholder="$0.00" value={row.amount} onChange={(event) => {
                            const updated = [...additionalIncomes];
                            updated[index] = { ...row, amount: event.target.value };
                            setAdditionalIncomes(updated);
                          }} className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#9E4CFF]" />
                          <button type="button" onClick={() => setAdditionalIncomes((previous) => previous.filter((item) => item.id !== row.id))} className="px-3 py-2 rounded-lg border border-[#5C1734] bg-[#2A0012] text-[#FFB7D5] text-xs font-semibold hover:bg-[#3A001A]">Remove</button>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>
        </SectionCard>
        <SectionCard title="Monthly Income Total" subtitle="The monthly total after converting the income above." dark>
          <div className="text-4xl font-semibold text-white">{formatCurrency(liteIncomeMonthly)}</div>
          <p className="mt-3 text-sm text-[#D8D8D8]">Primary income is converted into a monthly amount and any extra income sources are added on top.</p>
        </SectionCard>
      </div>
    </div>
  );

  const renderLiteExpensesV2 = () => (
    <div>
      <h2 className="text-2xl font-semibold text-[#C9A6FF] mb-4">Expenses</h2>
      <p className="text-sm text-[#D8D8D8] mb-6 max-w-3xl">Add the categories you expect to pay this month so Flow Lite can show where your money is going.</p>
      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.3fr)_320px] gap-6">
        <SectionCard title="Monthly Expense Categories" subtitle="Keep this simple: expense name and monthly amount.">
          <div className="flex flex-col gap-3">
            {litePostOnboardingExpenseRows.map((row) => (
              <div key={row.id} className="rounded-xl border border-[#2A0047] bg-black/30 p-3 grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_140px_96px] gap-2">
                <input type="text" value={row.name} onChange={(event) => updateExpenseRow(row.id, { name: event.target.value })} placeholder="Expense name" className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#9E4CFF]" />
                <input type="number" value={row.amount} onChange={(event) => updateExpenseRow(row.id, { amount: event.target.value })} placeholder="0.00" className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#9E4CFF]" />
                <button type="button" onClick={() => removeExpenseRow(row.id)} className="px-3 py-2 rounded-lg border border-[#5C1734] bg-[#2A0012] text-[#FFB7D5] text-xs font-semibold hover:bg-[#3A001A]">Remove</button>
              </div>
            ))}
            <button type="button" onClick={() => addExpenseRow("flexible")} className="self-start px-4 py-2 rounded-lg bg-[#9E4CFF] text-black text-sm font-semibold tracking-wide shadow-[0_0_16px_#9E4CFF]">+ Add Expense</button>
          </div>
        </SectionCard>
        <SectionCard title="Expense Total" subtitle="The total monthly amount across the categories you entered." dark>
          <div className="text-4xl font-semibold text-white">{formatCurrency(liteExpensesMonthly)}</div>
          <div className="mt-4 flex flex-col gap-2">
            {expenses.filter((row) => row.name.trim() || parseNumber(row.amount) > 0).slice(0, 4).map((row) => (
              <div key={row.id} className="rounded-xl border border-[#2A0047] bg-black/40 px-4 py-3 flex items-center justify-between gap-3 text-sm">
                <span className="text-[#D8D8D8]">{row.name || "Untitled Expense"}</span>
                <span className="font-semibold text-white">{formatCurrency(parseNumber(row.amount))}</span>
              </div>
            ))}
          </div>
        </SectionCard>
      </div>
    </div>
  );

  const renderExpenses = () => (
    <div>
      <h2 className="text-2xl font-semibold text-[#C9A6FF] mb-4">Expense Planning</h2>
      <p className="text-sm text-[#D8D8D8] mb-6 max-w-3xl">
        This tab is your monthly plan. Put fixed essentials and flexible spending targets here, then use Transactions to track what actually happens.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-6">
        <StatTile label="Planning Income" value={formatCurrency(planningIncomeMonthly)} />
        <StatTile label="Fixed Essentials" value={formatCurrency(fixedMonthlyExpenses)} />
        <StatTile label="Flexible Plan" value={formatCurrency(flexibleMonthlyExpenses)} />
        <StatTile label="Total Monthly Debt" value={formatCurrency(totalMonthlyDebt)} />
        <StatTile label="Planned Leftover After Bills + Debt" value={formatCurrency(leftoverMonthly)} muted={leftoverMonthly < 0} />
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-6">
        <SectionCard title="Monthly Expense Planning" subtitle="Split your month into fixed essentials and flexible spending targets so you can see what is locked in versus what can move.">
          <div className="flex flex-col gap-6">
            <div className="planning-bucket rounded-2xl border border-[#2A0047] p-4 flex flex-col gap-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-base font-semibold text-[#C9A6FF]">Fixed Essentials</p>
                  <p className="text-xs text-[#D8D8D8]">Housing, utilities, insurance, and other bills that are hard to skip this month.</p>
                </div>
                <span className="text-base font-semibold text-white">{formatCurrency(fixedMonthlyExpenses)}</span>
              </div>
              {expenses.filter((row) => row.planType === "fixed").map((row) => (
                <div key={row.id} className="planning-row rounded-xl border border-[#2A0047] bg-black/30 p-3 flex flex-col gap-2">
                  <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_140px] gap-2 items-center">
                    <input type="text" value={row.name} onChange={(event) => updateExpenseRow(row.id, { name: event.target.value })} placeholder="Expense name" className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#9E4CFF]" />
                    <input type="number" value={row.amount} onChange={(event) => updateExpenseRow(row.id, { amount: event.target.value })} placeholder="0.00" className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#9E4CFF]" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_170px_110px] gap-2 items-center">
                    <select value={row.categoryKey || inferExpenseCategoryKey(row.name)} onChange={(event) => updateExpenseRow(row.id, { categoryKey: event.target.value })} className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#9E4CFF]">
                      {expenseCategoryOptions.map((category) => <option key={category.key} value={category.key}>{category.label}</option>)}
                    </select>
                    <button type="button" onClick={() => updateExpenseRow(row.id, { planType: "flexible" })} className="px-3 py-2 rounded-lg border border-[#2A0047] bg-black/40 text-[#D8D8D8] text-[11px] font-semibold hover:bg-black/60">Move to Flexible</button>
                    <button type="button" onClick={() => removeExpenseRow(row.id)} className="px-3 py-2 rounded-lg border border-[#5C1734] bg-[#2A0012] text-[#FFB7D5] text-xs font-semibold hover:bg-[#3A001A]">Remove</button>
                  </div>
                </div>
              ))}
              <button type="button" onClick={() => addExpenseRow("fixed")} className="self-start px-4 py-2 rounded-lg bg-[#9E4CFF] text-sm font-semibold tracking-wide text-black shadow-[0_0_16px_#9E4CFF]">+ Add Fixed Essential</button>
            </div>
            <div className="planning-bucket rounded-2xl border border-[#2A0047] p-4 flex flex-col gap-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-base font-semibold text-[#C9A6FF]">Flexible Spending Plan</p>
                  <p className="text-xs text-[#D8D8D8]">Food, gas, entertainment, and other categories you can adjust if the month gets tight.</p>
                </div>
                <span className="text-base font-semibold text-white">{formatCurrency(flexibleMonthlyExpenses)}</span>
              </div>
              {expenses.filter((row) => row.planType !== "fixed").map((row) => (
                <div key={row.id} className="planning-row rounded-xl border border-[#2A0047] bg-black/30 p-3 flex flex-col gap-2">
                  <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_140px] gap-2 items-center">
                    <input type="text" value={row.name} onChange={(event) => updateExpenseRow(row.id, { name: event.target.value })} placeholder="Expense name" className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#9E4CFF]" />
                    <input type="number" value={row.amount} onChange={(event) => updateExpenseRow(row.id, { amount: event.target.value })} placeholder="0.00" className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#9E4CFF]" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_170px_110px] gap-2 items-center">
                    <select value={row.categoryKey || inferExpenseCategoryKey(row.name)} onChange={(event) => updateExpenseRow(row.id, { categoryKey: event.target.value })} className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#9E4CFF]">
                      {expenseCategoryOptions.map((category) => <option key={category.key} value={category.key}>{category.label}</option>)}
                    </select>
                    <button type="button" onClick={() => updateExpenseRow(row.id, { planType: "fixed" })} className="px-3 py-2 rounded-lg border border-[#2A0047] bg-black/40 text-[#D8D8D8] text-[11px] font-semibold hover:bg-black/60">Move to Fixed</button>
                    <button type="button" onClick={() => removeExpenseRow(row.id)} className="px-3 py-2 rounded-lg border border-[#5C1734] bg-[#2A0012] text-[#FFB7D5] text-xs font-semibold hover:bg-[#3A001A]">Remove</button>
                  </div>
                </div>
              ))}
              <button type="button" onClick={() => addExpenseRow("flexible")} className="self-start px-4 py-2 rounded-lg bg-[#9E4CFF] text-sm font-semibold tracking-wide text-black shadow-[0_0_16px_#9E4CFF]">+ Add Flexible Category</button>
            </div>
          </div>
        </SectionCard>
        <SectionCard title="Debt Obligations" subtitle="Enter the current payment, balance, and APR so payoff timing and debt strategy stay useful.">
          <div className="hidden lg:grid grid-cols-[minmax(0,1.3fr)_140px_110px_110px_90px] gap-2 text-[8px] uppercase tracking-[0.16em] text-[#9E4CFF] px-1"><span>Name</span><span>Type</span><span>Monthly</span><span>Balance</span><span>APR</span></div>
          <div className="flex flex-col gap-3">
            {debts.map((row) => (
              <div key={row.id} className="rounded-xl border border-[#2A0047] bg-black/30 p-3 flex flex-col gap-2 lg:grid lg:grid-cols-[minmax(0,1.3fr)_140px_110px_110px_90px] lg:items-center lg:p-0 lg:border-0 lg:bg-transparent">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 lg:contents text-[10px]">
                  <input type="text" value={row.name} onChange={(event) => setDebts((current) => current.map((debt) => (debt.id === row.id ? { ...debt, name: event.target.value } : debt)))} className="h-8 bg-black/70 border border-[#2A0047] rounded-lg px-2.5 py-1.5 text-[10px] leading-tight outline-none focus:border-[#9E4CFF]" />
                  <select value={row.type} onChange={(event) => setDebts((current) => current.map((debt) => (debt.id === row.id ? { ...debt, type: event.target.value } : debt)))} className="h-8 bg-black/70 border border-[#2A0047] rounded-lg px-2.5 py-1.5 text-[10px] leading-tight outline-none focus:border-[#9E4CFF]"><option value="Credit Card">Credit Card</option><option value="Loan">Loan</option><option value="Line of Credit">Line of Credit</option><option value="Medical">Medical</option></select>
                  <input type="number" value={row.amount} onChange={(event) => setDebts((current) => current.map((debt) => (debt.id === row.id ? { ...debt, amount: event.target.value } : debt)))} className="h-8 bg-black/70 border border-[#2A0047] rounded-lg px-2.5 py-1.5 text-[10px] leading-tight outline-none focus:border-[#9E4CFF]" />
                  <input type="number" value={row.balance} onChange={(event) => setDebts((current) => current.map((debt) => (debt.id === row.id ? { ...debt, balance: event.target.value } : debt)))} className="h-8 bg-black/70 border border-[#2A0047] rounded-lg px-2.5 py-1.5 text-[10px] leading-tight outline-none focus:border-[#9E4CFF]" />
                  <input type="number" value={row.interest} onChange={(event) => setDebts((current) => current.map((debt) => (debt.id === row.id ? { ...debt, interest: event.target.value } : debt)))} className="h-8 bg-black/70 border border-[#2A0047] rounded-lg px-2.5 py-1.5 text-[10px] leading-tight outline-none focus:border-[#9E4CFF]" />
                </div>
              </div>
            ))}
          </div>
          <button type="button" onClick={() => setDebts((current) => [...current, { id: Date.now() + Math.random(), name: "", type: "Loan", amount: "", balance: "", interest: "" }])} className="self-start px-4 py-2 rounded-lg bg-[#9E4CFF] text-[10px] font-semibold tracking-wide text-black shadow-[0_0_16px_#9E4CFF]">+ Add Debt Account</button>
        </SectionCard>
        <SectionCard title="Monthly Plan vs Debt Load" subtitle="A fast gut-check on whether your monthly plan leaves enough room for goals and extra debt payments." dark>
          <div className="h-64 w-full"><ResponsiveContainer width="100%" height="100%"><BarChart data={[{ name: "Planning Income", value: planningIncomeMonthly }, { name: "Fixed", value: fixedMonthlyExpenses }, { name: "Flexible", value: flexibleMonthlyExpenses }, { name: "Debt", value: totalMonthlyDebt }, { name: "Leftover", value: Math.max(0, leftoverMonthly) }]}><XAxis dataKey="name" stroke="#9E4CFF" tick={{ fill: "#C9A6FF" }} /><YAxis stroke="#9E4CFF" tick={{ fill: "#C9A6FF" }} /><Tooltip contentStyle={{ background: "#130018", border: "1px solid #2A0047", color: "#D8D8D8" }} labelStyle={{ color: "#C9A6FF" }} formatter={(value) => formatCurrency(value)} /><Bar dataKey="value">{["#9E4CFF", "#AF6BFF", "#C78DFF", "#FF6BD5", "#CFFBE4"].map((color, index) => <Cell key={index} fill={color} />)}</Bar></BarChart></ResponsiveContainer></div>
        </SectionCard>
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <SectionCard title="Planned vs Actual This Month" subtitle="Use Transactions to see how real spending compares against the monthly plan.">
          {currentMonthPlannedVsActualRows.length === 0 ? <p className="text-sm text-[#D8D8D8]">Add planned categories and real transactions to compare what you expected against what actually happened this month.</p> : <div className="flex flex-col gap-3">{currentMonthPlannedVsActualRows.map((row) => <div key={row.id} className="rounded-xl border border-[#2A0047] bg-black/40 p-4"><div className="flex items-start justify-between gap-3"><div><p className="text-sm font-semibold text-[#C9A6FF]">{row.name}</p><p className="text-xs text-[#D8D8D8]">{row.planType === "fixed" ? "Fixed essential" : "Flexible"} | Category: {getSharedCategoryLabel(row.categoryKey)} | Plan: {formatCurrency(row.planned)} | Actual: {formatCurrency(row.actual)}</p></div><span className={`text-sm font-semibold ${row.actual > row.planned ? "text-[#FF9BE5]" : "text-[#CFFBE4]"}`}>{row.actual > row.planned ? `Over by ${formatCurrency(row.actual - row.planned)}` : `Under by ${formatCurrency(row.planned - row.actual)}`}</span></div><div className="mt-3"><ProgressBar value={safePercent(row.actual, row.planned || 1)} label="Actual vs monthly plan" /></div></div>)}</div>}
        </SectionCard>
        <SectionCard title="Debt Payoff Estimates" subtitle="Practical payoff math based on each debt's current payment and APR.">
          {debtDetails.filter((debt) => debt.balance > 0).length === 0 ? <p className="text-sm text-[#D8D8D8]">Add debt balances and payments first to unlock payoff timing and extra-payment strategy.</p> : <div className="flex flex-col gap-4">{debtDetails.filter((debt) => debt.balance > 0).map((debt) => <div key={debt.id} className="rounded-xl border border-[#2A0047] bg-black/40 p-4"><div className="flex items-start justify-between gap-4"><div><p className="text-sm font-semibold text-[#C9A6FF]">{debt.name || `Debt ${debt.id}`}</p><p className="text-xs text-[#D8D8D8]">Balance {formatCurrency(debt.balance)} | Payment {formatCurrency(debt.monthlyPayment)}/mo | APR {debt.apr}%</p></div><span className="text-sm font-semibold text-white">{debt.payoff?.months ? formatMonths(debt.payoff.months).toLowerCase() : "Needs stronger payment"}</span></div>{debt.payoff?.status === "ok" ? <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4 text-sm"><div className="rounded-lg border border-[#2A0047] bg-[#130018] px-3 py-2">Total paid: {formatCurrency(debt.payoff.totalPaid)}</div><div className="rounded-lg border border-[#2A0047] bg-[#130018] px-3 py-2">Interest cost: {formatCurrency(debt.payoff.interestPaid)}</div><div className="rounded-lg border border-[#2A0047] bg-[#130018] px-3 py-2">Years: {debt.payoff.years.toFixed(1)}</div></div> : <p className="mt-3 text-xs text-[#FF9BE5]">{debt.payoff?.message}</p>}{debt.fasterPayoff?.status === "ok" ? <p className="mt-3 text-xs text-[#D8D8D8]">Adding {formatCurrency(recommendationMonthlyAmounts.safe)} of extra monthly payoff money could shorten this to {formatMonths(debt.fasterPayoff.months).toLowerCase()}.</p> : null}</div>)}</div>}
        </SectionCard>
      </div>
    </div>
  );

  const renderTransactions = () => (
    <div>
      <h2 className="text-2xl font-semibold text-[#C9A6FF] mb-4">Transactions</h2>
      <p className="text-sm text-[#D8D8D8] mb-6 max-w-3xl">
        Use Quick Capture for routine daily entries, and switch to Detailed Entry only when you need more control. Editing always opens the full form so corrections stay safe.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <StatTile label={`Actual Expenses ${transactionMonthLabel}`} value={formatCurrency(actualExpenseMonthly)} />
        <StatTile label={`Actual Income ${transactionMonthLabel}`} value={formatCurrency(actualIncomeMonthly)} />
        <StatTile label={`Net Cash Movement ${transactionMonthLabel}`} value={formatCurrency(transactionNetCashFlow)} muted={transactionNetCashFlow < 0} />
        <StatTile label={`Entries ${transactionMonthLabel}`} value={String(filteredTransactions.length)} />
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <SectionCard
          title={editingTransactionId ? "Edit Transaction" : transactionEntryMode === "quick" ? "Quick Capture" : "Detailed Entry"}
          subtitle={
            editingTransactionId
              ? "Editing opens the full transaction form so you can correct anything safely."
              : transactionEntryMode === "quick"
              ? "Fast daily entry for routine purchases, bills, transfers, and deposits."
              : "Use the full form when a transaction needs more detail than the fast path."
          }
        >
          <div className="flex flex-col gap-4 text-sm">
            {!editingTransactionId ? (
              <div className="rounded-2xl border border-[#2A0047] bg-black/40 p-4 flex flex-col gap-3">
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => switchTransactionEntryMode("quick")}
                    className={`px-3 py-2 rounded-full text-xs font-semibold border ${
                      transactionEntryMode === "quick"
                        ? "bg-[#9E4CFF] text-black border-[#9E4CFF] shadow-[0_0_12px_#9E4CFF]"
                        : "bg-black/40 border-[#2A0047] text-[#C9A6FF] hover:bg-black/60"
                    }`}
                  >
                    Quick Capture
                  </button>
                  <button
                    type="button"
                    onClick={() => switchTransactionEntryMode("detailed")}
                    className={`px-3 py-2 rounded-full text-xs font-semibold border ${
                      transactionEntryMode === "detailed"
                        ? "bg-[#9E4CFF] text-black border-[#9E4CFF] shadow-[0_0_12px_#9E4CFF]"
                        : "bg-black/40 border-[#2A0047] text-[#C9A6FF] hover:bg-black/60"
                    }`}
                  >
                    Detailed Entry
                  </button>
                </div>
                <p className="text-xs text-[#D8D8D8]">
                  Quick Capture is the default path for repeated day-to-day entries. Detailed Entry stays available for uncommon transactions and full corrections.
                </p>
              </div>
            ) : null}

            {transactionMessage ? (
              <div
                className={`rounded-2xl border px-4 py-3 text-sm ${
                  transactionTone === "success"
                    ? "border-[#2A5B44] bg-[#0E2119] text-[#CFFBE4]"
                    : transactionTone === "error"
                    ? "border-[#5A1D55] bg-[#2A0A2E] text-[#FFD4F4]"
                    : "border-[#2A0047] bg-black/40 text-[#D8D8D8]"
                }`}
              >
                {transactionMessage}
              </div>
            ) : null}

            {editingTransactionId ? (
              <div className="rounded-2xl border border-[#2A0047] bg-black/40 px-4 py-3 text-xs text-[#D8D8D8]">
                Editing an existing transaction. Save when you are ready, or cancel to keep the original entry.
              </div>
            ) : null}

            {!editingTransactionId && transactionEntryMode === "quick" ? (
              <div className="flex flex-col gap-4">
                <div className="rounded-2xl border border-[#2A0047] bg-black/40 p-4 flex flex-col gap-3">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-[#9E4CFF] uppercase tracking-widest text-xs">Quick Add Presets</span>
                    <span className="text-[11px] text-[#D8D8D8]">Tap one, then enter the amount.</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {quickTransactionPresets.map((preset) => (
                      <button
                        key={preset.id}
                        type="button"
                        onClick={() => applyQuickTransactionPreset(preset)}
                        className="px-3 py-2 rounded-full text-xs font-semibold border border-[#2A0047] bg-black/40 text-[#C9A6FF] hover:bg-black/60"
                      >
                        {preset.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="rounded-2xl border border-[#2A0047] bg-black/40 p-4 flex flex-col gap-4">
                  <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_180px] gap-4 items-end">
                    <div className="flex flex-col gap-2">
                      <span className="text-[#9E4CFF] uppercase tracking-widest text-xs">Amount</span>
                      <input
                        type="number"
                        placeholder="$0.00"
                        value={transactionForm.amount}
                        onChange={(event) => handleTransactionFieldChange("amount", event.target.value)}
                        className="bg-black/70 border border-[#2A0047] rounded-xl px-4 py-4 text-2xl font-semibold outline-none focus:border-[#9E4CFF]"
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <span className="text-[#9E4CFF] uppercase tracking-widest text-xs">Date</span>
                      <input
                        type="date"
                        value={transactionForm.date}
                        onChange={(event) => handleTransactionFieldChange("date", event.target.value)}
                        className="bg-black/70 border border-[#2A0047] rounded-xl px-3 py-3 outline-none focus:border-[#9E4CFF]"
                      />
                    </div>
                  </div>

                  <div className="flex flex-col gap-2">
                    <span className="text-[#9E4CFF] uppercase tracking-widest text-xs">Type</span>
                    <div className="flex flex-wrap gap-2">
                      {transactionTypeOptions.map((option) => (
                        <button
                          key={option.id}
                          type="button"
                          onClick={() => applyQuickTransactionType(option.id)}
                          className={`px-3 py-2 rounded-full text-xs font-semibold border ${
                            transactionForm.type === option.id
                              ? "bg-[#9E4CFF] text-black border-[#9E4CFF] shadow-[0_0_12px_#9E4CFF]"
                              : "bg-black/40 border-[#2A0047] text-[#C9A6FF] hover:bg-black/60"
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="flex flex-col gap-2">
                    <span className="text-[#9E4CFF] uppercase tracking-widest text-xs">Common Categories</span>
                    <div className="flex flex-wrap gap-2">
                      {quickTransactionCategoryOptions.map((category) => (
                        <button
                          key={category}
                          type="button"
                          onClick={() => applyQuickTransactionCategory(category)}
                          className={`px-3 py-2 rounded-full text-xs font-semibold border ${
                            transactionForm.category === category
                              ? "bg-[#9E4CFF] text-black border-[#9E4CFF] shadow-[0_0_12px_#9E4CFF]"
                              : "bg-black/40 border-[#2A0047] text-[#C9A6FF] hover:bg-black/60"
                          }`}
                        >
                          {category}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="flex flex-col gap-2">
                    <span className="text-[#9E4CFF] uppercase tracking-widest text-xs">Account / Source</span>
                    <div className="flex flex-wrap gap-2">
                      {quickTransactionAccountOptions.map((account) => (
                        <button
                          key={account}
                          type="button"
                          onClick={() => applyQuickTransactionAccount(account)}
                          className={`px-3 py-2 rounded-full text-xs font-semibold border ${
                            transactionForm.account === account
                              ? "bg-[#9E4CFF] text-black border-[#9E4CFF] shadow-[0_0_12px_#9E4CFF]"
                              : "bg-black/40 border-[#2A0047] text-[#C9A6FF] hover:bg-black/60"
                          }`}
                        >
                          {account}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="flex flex-col gap-2">
                    <span className="text-[#9E4CFF] uppercase tracking-widest text-xs">Description (Optional)</span>
                    <input
                      type="text"
                      placeholder="e.g. Grocery run, payroll deposit, gas"
                      value={transactionForm.description}
                      onChange={(event) => handleTransactionFieldChange("description", event.target.value)}
                      className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-3 outline-none focus:border-[#9E4CFF]"
                    />
                  </div>

                  <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3">
                    <button
                      type="button"
                      onClick={handleAddTransaction}
                      className="w-full sm:w-auto px-4 py-3 rounded-lg bg-[#9E4CFF] text-black font-semibold tracking-wide shadow-[0_0_16px_#9E4CFF]"
                    >
                      Save Quick Transaction
                    </button>
                    <button
                      type="button"
                      onClick={() => switchTransactionEntryMode("detailed")}
                      className="w-full sm:w-auto px-4 py-3 rounded-lg border border-[#2A0047] bg-black/40 text-[#D8D8D8] font-semibold tracking-wide"
                    >
                      Open Detailed Entry
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="rounded-2xl border border-[#2A0047] bg-black/40 p-4 flex flex-col gap-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-[#C9A6FF] font-semibold">Detailed Entry</p>
                    <p className="text-xs text-[#D8D8D8]">
                      Use this when the fast path is not enough. Editing always stays here so you can confirm every field before saving.
                    </p>
                  </div>
                  {!editingTransactionId ? (
                    <button
                      type="button"
                      onClick={() => switchTransactionEntryMode("quick")}
                      className="px-3 py-2 rounded-lg border border-[#2A0047] bg-black/40 text-[#D8D8D8] text-xs font-semibold"
                    >
                      Back to Quick Capture
                    </button>
                  ) : null}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div className="flex flex-col gap-2">
                    <span className="text-[#9E4CFF] uppercase tracking-widest text-xs">Date</span>
                    <input
                      type="date"
                      value={transactionForm.date}
                      onChange={(event) => handleTransactionFieldChange("date", event.target.value)}
                      className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-3 outline-none focus:border-[#9E4CFF]"
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <span className="text-[#9E4CFF] uppercase tracking-widest text-xs">Amount</span>
                    <input
                      type="number"
                      placeholder="$0.00"
                      value={transactionForm.amount}
                      onChange={(event) => handleTransactionFieldChange("amount", event.target.value)}
                      className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-3 outline-none focus:border-[#9E4CFF]"
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <span className="text-[#9E4CFF] uppercase tracking-widest text-xs">Type</span>
                    <select
                      value={transactionForm.type}
                      onChange={(event) => handleTransactionFieldChange("type", event.target.value)}
                      className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-3 outline-none focus:border-[#9E4CFF]"
                    >
                      {transactionTypeOptions.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex flex-col gap-2">
                    <span className="text-[#9E4CFF] uppercase tracking-widest text-xs">Account / Source</span>
                    <select
                      value={transactionForm.account}
                      onChange={(event) => handleTransactionFieldChange("account", event.target.value)}
                      className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-3 outline-none focus:border-[#9E4CFF]"
                    >
                      {transactionAccountOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex flex-col gap-2">
                    <span className="text-[#9E4CFF] uppercase tracking-widest text-xs">Category</span>
                    <select
                      value={transactionForm.category}
                      onChange={(event) => handleTransactionFieldChange("category", event.target.value)}
                      className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-3 outline-none focus:border-[#9E4CFF]"
                    >
                      <option value="">Select a category</option>
                      {transactionCategoryOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex flex-col gap-2">
                    <span className="text-[#9E4CFF] uppercase tracking-widest text-xs">Description</span>
                    <input
                      type="text"
                      placeholder="e.g. Grocery run, payroll deposit, gas"
                      value={transactionForm.description}
                      onChange={(event) => handleTransactionFieldChange("description", event.target.value)}
                      className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-3 outline-none focus:border-[#9E4CFF]"
                    />
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={handleAddTransaction}
                    className="w-full sm:w-auto px-4 py-3 rounded-lg bg-[#9E4CFF] text-black font-semibold tracking-wide shadow-[0_0_16px_#9E4CFF]"
                  >
                    {editingTransactionId ? "Save Transaction Changes" : "Add Transaction"}
                  </button>
                  {editingTransactionId ? (
                    <button
                      type="button"
                      onClick={handleCancelTransactionEdit}
                      className="w-full sm:w-auto px-4 py-3 rounded-lg border border-[#2A0047] bg-black/40 text-[#D8D8D8] font-semibold tracking-wide"
                    >
                      Cancel Edit
                    </button>
                  ) : null}
                </div>
              </div>
            )}
          </div>
        </SectionCard>
        <SectionCard title="Recent Activity" subtitle={`Review what actually happened in ${transactionMonthLabel}. Use the month picker to change the list.`}>
          <div className="flex flex-col gap-3 text-sm">
            <div className="flex flex-col gap-2">
              <span className="text-[#9E4CFF] uppercase tracking-widest text-xs">Month</span>
              <input
                type="month"
                value={transactionMonth}
                onChange={(event) => setTransactionMonth(event.target.value)}
                className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-3 outline-none focus:border-[#9E4CFF]"
              />
            </div>
            {filteredTransactions.length === 0 ? (
              <p className="text-sm text-[#D8D8D8]">
                No real transactions have been logged for {transactionMonthLabel} yet. Add a few expenses, deposits, or transfers so the app can start showing what actually happened.
              </p>
            ) : (
              <div className="flex flex-col gap-3 max-h-[32rem] overflow-y-auto pr-1">
                {filteredTransactions.map((row) => (
                  <div key={row.id} className="rounded-xl border border-[#2A0047] bg-black/40 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-[#C9A6FF]">{row.description || row.category}</p>
                        <p className="text-xs text-[#D8D8D8]">{row.date} | {row.category} | {row.account}</p>
                      </div>
                      <span
                        className={`text-sm font-semibold ${
                          row.type === "expense"
                            ? "text-[#FF9BE5]"
                            : row.type === "income"
                            ? "text-[#CFFBE4]"
                            : "text-white"
                        }`}
                      >
                        {row.type === "expense" ? "-" : row.type === "income" ? "+" : ""}
                        {formatCurrency(parseNumber(row.amount))}
                      </span>
                    </div>
                    <div className="mt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <span className="text-[11px] uppercase tracking-widest text-[#9E4CFF]">{row.type}</span>
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleEditTransaction(row)}
                          className="px-3 py-2 rounded-lg border border-[#2A0047] bg-black/30 text-xs font-semibold text-[#C9A6FF] hover:text-white"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteTransaction(row.id)}
                          className="px-3 py-2 rounded-lg border border-red-300/30 bg-red-950/20 text-xs font-semibold text-red-300 hover:text-red-200"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </SectionCard>
        <SectionCard title="Actual Spending Snapshot" subtitle={`Use this to see where ${transactionMonthLabel} is going before full bank sync exists.`} dark>
          <div className="flex flex-col gap-4 text-sm">
            {topActualCategories.length === 0 ? (
              <p className="text-sm text-[#D8D8D8]">
                Add a few expense transactions and this section will show which categories are really taking the most money in {transactionMonthLabel}.
              </p>
            ) : (
              topActualCategories.map((row) => (
                <div key={row.name} className="rounded-xl border border-[#2A0047] bg-[#130018] p-4">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm font-semibold text-[#C9A6FF]">{row.name}</p>
                    <span className="text-sm font-semibold text-white">{formatCurrency(row.amount)}</span>
                  </div>
                  <div className="mt-3">
                    <ProgressBar value={row.share} label={`${Math.round(row.share)}% of actual expenses`} />
                  </div>
                </div>
              ))
            )}
            <div className="rounded-xl border border-[#2A0047] bg-[#130018] p-4">
              <p className="text-[#C9A6FF] font-semibold">What this unlocks later</p>
              <p className="mt-2 text-xs text-[#D8D8D8]">
                This transaction layer is the base for quick-add, widgets, and future bank or credit-card connections.
              </p>
            </div>
            {selectedMonthPlannedVsActualRows.length > 0 ? (
              <div className="rounded-xl border border-[#2A0047] bg-[#130018] p-4">
                <p className="text-[#C9A6FF] font-semibold">Planned vs actual tracked categories</p>
                <p className="mt-2 text-xs text-[#D8D8D8]">
                  In {transactionMonthLabel}, {selectedMonthPlannedVsActualRows.filter((row) => row.actual > row.planned).length} categories are currently over plan and {selectedMonthPlannedVsActualRows.filter((row) => row.actual <= row.planned).length} are on track or under.
                </p>
              </div>
            ) : null}
          </div>
        </SectionCard>
      </div>
    </div>
  );
  const renderGoals = () => (
    <div>
      <h2 className="text-2xl font-semibold text-[#C9A6FF] mb-4">Goals</h2>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <SectionCard title="Debt Freedom Preview" subtitle="What happens if leftover money goes to debt." dark>
          <div className="flex flex-col gap-1 text-sm"><span className="text-xs text-[#9E4CFF] uppercase tracking-widest">Total Debt Balance</span><span className="text-xl font-semibold text-white">{formatCurrency(totalDebtBalance)}</span></div>
          <div className="flex flex-col gap-1 text-sm"><span className="text-xs text-[#9E4CFF] uppercase tracking-widest">Leftover For Extra Payments</span><span className="text-lg font-semibold text-white">{formatCurrency(leftoverPositive)} / mo</span></div>
          {estimatedMonthsToDebtFree !== null ? <><p className="text-sm text-[#D8D8D8]">If you sent all leftover to debt, you could clear current balances in about {formatMonths(estimatedMonthsToDebtFree).toLowerCase()}.</p><ProgressBar value={(12 / estimatedMonthsToDebtFree) * 100} label="12-month payoff progress" /></> : <p className="text-xs text-[#D8D8D8]">Add balances and create some leftover to unlock a debt-free estimate.</p>}
        </SectionCard>
        <SectionCard title="Emergency Fund Preview" subtitle="A starter safety net before bigger goals." dark>
          <div className="flex flex-col gap-1 text-sm"><span className="text-xs text-[#9E4CFF] uppercase tracking-widest">Target (3 Months Planning Income)</span><span className="text-xl font-semibold text-white">{formatCurrency(emergencyFundTarget)}</span></div>
          {monthsToEmergencyFund !== null ? <><p className="text-sm text-[#D8D8D8]">At your current leftover, this would take about {formatMonths(monthsToEmergencyFund).toLowerCase()}.</p><ProgressBar value={(12 / monthsToEmergencyFund) * 100} label="12-month savings path" /></> : <p className="text-xs text-[#D8D8D8]">Once you have positive leftover cash, this will estimate the timeline automatically.</p>}
        </SectionCard>
        {taxReserveEnabled && estimatedAnnualTax > 0 ? <SectionCard title="Tax Reserve Strategy" subtitle="Keep owner-paid taxes from becoming a surprise." dark><div className="flex flex-col gap-1 text-sm"><span className="text-xs text-[#9E4CFF] uppercase tracking-widest">Estimated Annual Tax</span><span className="text-xl font-semibold text-white">{formatCurrency(estimatedAnnualTax)}</span></div><div className="flex flex-col gap-1 text-sm"><span className="text-xs text-[#9E4CFF] uppercase tracking-widest">Suggested Monthly Tax Savings</span><span className="text-lg font-semibold text-white">{formatCurrency(currentTaxSavingsTargetMonthly)} / mo</span></div>{suggestedTaxPerPaycheck > 0 ? <p className="text-sm text-[#D8D8D8]">That is about {formatCurrency(suggestedTaxPerPaycheck)} per paycheck.</p> : null}</SectionCard> : <SectionCard title="Quick Goal Ideas" subtitle="A few smart first moves if you are not sure where to start." dark><ul className="list-disc list-inside text-sm text-[#D8D8D8] space-y-2"><li>Build a $500 buffer first if your leftover is very small.</li><li>Get one high-interest credit card under control before adding optional spending goals.</li><li>Use the safe contribution option if your budget still feels tight.</li></ul></SectionCard>}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-6">
        <SectionCard title="Quick Start Goals" subtitle="Start fast with common milestones people usually need first.">
          <div className="grid grid-cols-1 gap-3">
            <button type="button" className="rounded-xl border border-[#2A0047] bg-black/40 px-4 py-3 text-left hover:bg-black/60 transition-colors" onClick={() => createGoalFromTemplate({ name: "Save $500", type: "savings", targetAmount: 500 })}><p className="text-[#C9A6FF] font-semibold">Save $500</p><p className="text-xs text-[#D8D8D8]">Small emergency cushion or bill shock buffer.</p></button>
            <button type="button" className="rounded-xl border border-[#2A0047] bg-black/40 px-4 py-3 text-left hover:bg-black/60 transition-colors" onClick={() => createGoalFromTemplate({ name: "Save $1,000", type: "savings", targetAmount: 1000 })}><p className="text-[#C9A6FF] font-semibold">Save $1,000</p><p className="text-xs text-[#D8D8D8]">Starter emergency fund with a practical first milestone.</p></button>
            <button type="button" className="rounded-xl border border-[#2A0047] bg-black/40 px-4 py-3 text-left hover:bg-black/60 transition-colors disabled:opacity-50" disabled={!smallestBalanceDebt} onClick={() => smallestBalanceDebt ? createGoalFromTemplate({ name: `Pay Off ${smallestBalanceDebt.name}`, type: "debt", linkedDebtId: String(smallestBalanceDebt.id), targetAmount: smallestBalanceDebt.balance }) : null}><p className="text-[#C9A6FF] font-semibold">Pay off one credit card</p><p className="text-xs text-[#D8D8D8]">Quick debt win using your smallest active balance first.</p></button>
            <button type="button" className="rounded-xl border border-[#2A0047] bg-black/40 px-4 py-3 text-left hover:bg-black/60 transition-colors disabled:opacity-50" disabled={emergencyFundTarget <= 0} onClick={() => createGoalFromTemplate({ name: "Build 3-Month Emergency Fund", type: "savings", targetAmount: emergencyFundTarget })}><p className="text-[#C9A6FF] font-semibold">Build 3-month emergency fund</p><p className="text-xs text-[#D8D8D8]">Turn your preview into a tracked goal you can manage.</p></button>
          </div>
        </SectionCard>

        <div className="xl:col-span-2">
          <SectionCard title="Create a Custom Goal" subtitle="Step 1: name the goal. Step 2: choose the target. Step 3: pick a contribution pace that your budget can actually hold." dark>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="flex flex-col gap-2"><span className="text-[#9E4CFF] uppercase tracking-widest text-xs">Goal Name</span><input type="text" placeholder="e.g. Move-out fund" className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2" value={goalForm.name} onChange={(event) => setGoalForm({ ...goalForm, name: event.target.value })} /></div>
              <div className="flex flex-col gap-2"><span className="text-[#9E4CFF] uppercase tracking-widest text-xs">Goal Type</span><select className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2" value={goalForm.type} onChange={(event) => setGoalForm({ ...goalForm, type: event.target.value, linkedDebtId: event.target.value === "debt" ? goalForm.linkedDebtId : "", targetAmount: event.target.value === "all-debt" ? String(totalDebtBalance || "") : goalForm.targetAmount })}><option value="savings">Savings Goal</option><option value="debt">Specific Debt Payoff</option><option value="all-debt">Pay Off All Debt</option><option value="custom">Custom Named Goal</option></select></div>
              {goalForm.type === "debt" ? <div className="flex flex-col gap-2"><span className="text-[#9E4CFF] uppercase tracking-widest text-xs">Choose Debt</span><select className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2" value={goalForm.linkedDebtId} onChange={(event) => { const linkedDebt = debtDetails.find((debt) => String(debt.id) === String(event.target.value)); setGoalForm({ ...goalForm, linkedDebtId: event.target.value, targetAmount: linkedDebt ? String(linkedDebt.balance) : "" }); }}><option value="">Select a debt</option>{debtDetails.map((debt) => <option key={debt.id} value={debt.id}>{debt.name || `Debt ${debt.id}`} - {formatCurrency(debt.balance)}</option>)}</select></div> : null}
              {goalForm.type !== "debt" && goalForm.type !== "all-debt" ? <div className="flex flex-col gap-2"><span className="text-[#9E4CFF] uppercase tracking-widest text-xs">Target Amount</span><input type="number" placeholder="$0.00" className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2" value={goalForm.targetAmount} onChange={(event) => setGoalForm({ ...goalForm, targetAmount: event.target.value })} /></div> : <div className="flex flex-col gap-2"><span className="text-[#9E4CFF] uppercase tracking-widest text-xs">Target Amount</span><div className="bg-black/60 border border-[#2A0047] rounded-lg px-3 py-2 text-[#D8D8D8]">{goalForm.type === "all-debt" ? formatCurrency(totalDebtBalance) : formatCurrency(parseNumber(goalForm.targetAmount))}</div></div>}
              <div className="flex flex-col gap-2"><span className="text-[#9E4CFF] uppercase tracking-widest text-xs">Contribution Style</span><div className="flex flex-wrap gap-2">{["monthly", "per-paycheck"].map((style) => <button key={style} type="button" onClick={() => setGoalForm({ ...goalForm, contributionStyle: style })} className={`px-3 py-1 rounded-full text-xs border transition-all ${goalForm.contributionStyle === style ? "bg-[#9E4CFF] text-black border-[#9E4CFF]" : "bg-black/70 border-[#2A0047] text-[#D8D8D8]"}`}>{style === "monthly" ? "Monthly" : "Per Paycheck"}</button>)}</div>{goalForm.contributionStyle === "per-paycheck" && !paychecksPerMonth ? <p className="text-[11px] text-[#FF9BE5]">Per-paycheck goals need a predictable paycheck pattern first.</p> : null}</div>
              <div className="flex flex-col gap-2 md:col-span-2"><span className="text-[#9E4CFF] uppercase tracking-widest text-xs">Contribution Mode</span><div className="flex flex-wrap gap-2">{["recommended", "custom"].map((mode) => <button key={mode} type="button" onClick={() => setGoalForm({ ...goalForm, contributionMode: mode })} className={`px-3 py-1 rounded-full text-xs border transition-all ${goalForm.contributionMode === mode ? "bg-[#9E4CFF] text-black border-[#9E4CFF]" : "bg-black/70 border-[#2A0047] text-[#D8D8D8]"}`}>{mode === "recommended" ? "Recommended" : "Custom Amount"}</button>)}</div></div>
              {goalForm.contributionMode === "recommended" ? <div className="md:col-span-2 flex flex-wrap gap-2">{Object.entries(recommendationMonthlyAmounts).map(([level, amount]) => <button key={level} type="button" onClick={() => setGoalForm({ ...goalForm, recommendationLevel: level })} className={`px-3 py-2 rounded-xl border text-left min-w-[140px] ${goalForm.recommendationLevel === level ? "bg-[#9E4CFF] text-black border-[#9E4CFF]" : "bg-black/70 text-[#D8D8D8] border-[#2A0047]"}`}><div className="font-semibold capitalize">{level}</div><div className="text-xs">{formatCurrency(amount)} / mo</div></button>)}</div> : <div className="md:col-span-2 flex flex-col gap-2"><span className="text-[#9E4CFF] uppercase tracking-widest text-xs">Custom Contribution</span><input type="number" placeholder="$0.00" className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2" value={goalForm.customContribution} onChange={(event) => setGoalForm({ ...goalForm, customContribution: event.target.value })} /></div>}
            </div>
            <div className="flex items-center justify-between gap-3 border-t border-[#2A0047] pt-4"><p className="text-xs text-[#D8D8D8]">Recommended contributions stay between 15% and 40% of your current leftover so your bills do not get crushed.</p><button type="button" disabled={!canCreateGoal || (goalForm.contributionStyle === "per-paycheck" && !paychecksPerMonth)} className="px-4 py-2 rounded-lg bg-[#9E4CFF] text-black font-semibold tracking-wide shadow-[0_0_16px_#9E4CFF] disabled:opacity-40" onClick={handleAddGoal}>Create Goal</button></div>
          </SectionCard>
        </div>
      </div>

      <SectionCard title="Custom Goals" subtitle="Each goal shows how much to contribute, how long it may take, and whether the pace looks realistic right now.">
        {goalAnalyses.length === 0 ? <p className="text-sm text-[#D8D8D8]">No active goals yet. Once your monthly plan is in place, create one clear goal so the app can show whether it realistically fits this month.</p> : <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">{goalAnalyses.map((goal) => <div key={goal.id} className="rounded-2xl border border-[#2A0047] bg-black/40 p-5 flex flex-col gap-4"><div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3"><div><h4 className="text-lg font-semibold text-[#C9A6FF]">{goal.name}</h4><p className="text-xs text-[#D8D8D8] capitalize">{goal.type === "all-debt" ? "Pay off all debt" : goal.type.replace("-", " ")}</p></div><button type="button" className="text-xs text-red-300 hover:text-red-400" onClick={() => deleteGoal(goal.id)}>Remove Goal</button></div><div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm"><div className="rounded-xl border border-[#2A0047] bg-[#130018] px-3 py-3"><span className="text-[11px] uppercase tracking-widest text-[#9E4CFF]">Target Amount</span><div className="text-lg font-semibold mt-1 text-white">{formatCurrency(goal.targetAmount)}</div></div><div className="rounded-xl border border-[#2A0047] bg-[#130018] px-3 py-3"><span className="text-[11px] uppercase tracking-widest text-[#9E4CFF]">Current Contribution</span><div className="text-lg font-semibold mt-1 text-white">{formatCurrency(goal.contributionValue)} {goal.contributionStyle === "monthly" ? "/ mo" : "/ paycheck"}</div></div><div className="rounded-xl border border-[#2A0047] bg-[#130018] px-3 py-3"><span className="text-[11px] uppercase tracking-widest text-[#9E4CFF]">Monthly Equivalent</span><div className="text-lg font-semibold mt-1 text-white">{formatCurrency(goal.monthlyEquivalent)} / mo</div></div><div className="rounded-xl border border-[#2A0047] bg-[#130018] px-3 py-3"><span className="text-[11px] uppercase tracking-widest text-[#9E4CFF]">Estimated Timeline</span><div className="text-lg font-semibold mt-1 text-white">{formatMonths(goal.estimatedMonths)}</div></div></div><div className="flex flex-wrap gap-2">{["safe", "balanced", "aggressive"].map((level) => <button key={level} type="button" onClick={() => updateGoal(goal.id, { contributionMode: "recommended", recommendationLevel: level })} className={`px-3 py-1 rounded-full text-xs border ${goal.contributionMode === "recommended" && goal.recommendationLevel === level ? "bg-[#9E4CFF] text-black border-[#9E4CFF]" : "bg-black/70 border-[#2A0047] text-[#D8D8D8]"}`}>{level.charAt(0).toUpperCase() + level.slice(1)}</button>)}<button type="button" onClick={() => updateGoal(goal.id, { contributionMode: "custom" })} className={`px-3 py-1 rounded-full text-xs border ${goal.contributionMode === "custom" ? "bg-[#9E4CFF] text-black border-[#9E4CFF]" : "bg-black/70 border-[#2A0047] text-[#D8D8D8]"}`}>Custom</button><button type="button" onClick={() => updateGoal(goal.id, { contributionStyle: goal.contributionStyle === "monthly" ? "per-paycheck" : "monthly" })} className="px-3 py-1 rounded-full text-xs border bg-black/70 border-[#2A0047] text-[#D8D8D8]">{goal.contributionStyle === "monthly" ? "Switch to Per Paycheck" : "Switch to Monthly"}</button></div>{goal.contributionMode === "custom" ? <input type="number" placeholder="$0.00" className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 text-sm" value={goal.customContribution} onChange={(event) => updateGoal(goal.id, { customContribution: event.target.value })} /> : null}<ProgressBar value={goal.annualPathPercent} label="12-month path to this goal" /><div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs"><div className="rounded-lg border border-[#2A0047] bg-black/50 px-3 py-2">6 months: {formatCurrency(goal.requiredFor6Months)}/mo</div><div className="rounded-lg border border-[#2A0047] bg-black/50 px-3 py-2">12 months: {formatCurrency(goal.requiredFor12Months)}/mo</div><div className="rounded-lg border border-[#2A0047] bg-black/50 px-3 py-2">24 months: {formatCurrency(goal.requiredFor24Months)}/mo</div></div><p className="text-sm text-[#D8D8D8]">{goal.goalMessage}</p>{goal.unrealistic ? <p className="text-xs text-[#FF9BE5]">{goal.unrealistic}</p> : null}{goal.contributionStyle === "per-paycheck" && !paychecksPerMonth ? <p className="text-xs text-[#FF9BE5]">Per-paycheck math needs a predictable paycheck pattern.</p> : null}</div>)}</div>}
      </SectionCard>

      <div className="mt-6">
        {renderGoalForecastSection()}
      </div>

      <div className="mt-6">
        <SectionCard title="Debt Strategy Planner" subtitle="Choose a target, choose an approach, and see the clearest next move before you send extra money.">
          {debtStrategyPool.length === 0 ? <p className="text-sm text-[#D8D8D8]">Debt strategy guidance starts once you add at least one debt balance. Begin with the card or loan that feels most urgent and the planner will show what to target first.</p> : <div className="grid grid-cols-1 xl:grid-cols-3 gap-6"><div className="flex flex-col gap-4"><div className="flex flex-col gap-2"><span className="text-[#9E4CFF] uppercase tracking-widest text-xs">Target</span><select className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2" value={debtStrategyTarget} onChange={(event) => setDebtStrategyTarget(event.target.value)}><option value="all">All Debts</option>{debtStrategyPool.map((debt) => <option key={debt.id} value={debt.id}>{debt.name} - {formatCurrency(debt.balance)}</option>)}</select></div>{debtStrategyTarget === "all" ? <div className="flex flex-col gap-2"><span className="text-[#9E4CFF] uppercase tracking-widest text-xs">Payoff Method</span><div className="flex flex-wrap gap-2">{DEBT_METHODS.map((method) => <button key={method.id} type="button" onClick={() => setDebtStrategyMethod(method.id)} className={`px-3 py-1 rounded-full text-xs border ${debtStrategyMethod === method.id ? "bg-[#9E4CFF] text-black border-[#9E4CFF]" : "bg-black/70 border-[#2A0047] text-[#D8D8D8]"}`}>{method.label}</button>)}</div></div> : null}<div className="flex flex-col gap-2"><span className="text-[#9E4CFF] uppercase tracking-widest text-xs">Extra Payment Amount</span><input type="number" placeholder="$0.00" className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2" value={debtStrategyExtraAmount} onChange={(event) => setDebtStrategyExtraAmount(event.target.value)} /><div className="flex flex-wrap gap-2">{Object.entries(recommendationMonthlyAmounts).map(([level, amount]) => <button key={level} type="button" className="px-3 py-1 rounded-full text-xs border bg-black/70 border-[#2A0047] text-[#D8D8D8]" onClick={() => setDebtStrategyExtraAmount(String(amount.toFixed(2)))}>{level}: {formatCurrency(amount)}</button>)}</div></div></div><div className="xl:col-span-2 flex flex-col gap-4">{debtStrategyTarget === "all" ? <><div className="rounded-xl border border-[#2A0047] bg-black/40 p-4"><p className="text-[#C9A6FF] font-semibold">Recommended payoff order for your current setup</p><div className="mt-3 flex flex-col gap-3 text-sm">{debtPriorityOrder.map((debt, index) => <div key={debt.id} className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 border border-[#2A0047] rounded-lg px-3 py-3 bg-[#130018]"><div><p className="font-semibold text-[#C9A6FF]">{index + 1}. {debt.name}</p><p className="text-xs text-[#D8D8D8]">Balance {formatCurrency(debt.balance)} | APR {debt.apr.toFixed(1)}% | Payment {formatCurrency(debt.monthlyPayment)} / mo</p></div><span className="text-xs text-[#D8D8D8]">{index === 0 ? getDebtMethodReason(debtStrategyMethod, debt) : "Next in line after the first target."}</span></div>)}</div></div>{firstPriorityDebt ? <div className="rounded-xl border border-[#2A0047] bg-black/40 p-4"><p className="text-[#C9A6FF] font-semibold">First payoff milestone</p><p className="text-sm text-[#D8D8D8] mt-2">With {formatCurrency(parseNumber(debtStrategyExtraAmount))} of extra payment, {firstPriorityDebt.name} could be gone in {formatMonths(firstPriorityEstimate?.months).toLowerCase()}.</p><p className="text-xs text-[#D8D8D8] mt-2">Full chained multi-debt simulation is not modeled yet, so this planner focuses on the right order and the first payoff milestone.</p></div> : null}</> : selectedStrategyDebt ? <div className="rounded-xl border border-[#2A0047] bg-black/40 p-4 flex flex-col gap-3"><p className="text-[#C9A6FF] font-semibold">Single debt focus</p><p className="text-sm text-[#D8D8D8]">{selectedStrategyDebt.name} has a balance of {formatCurrency(selectedStrategyDebt.balance)}, APR of {selectedStrategyDebt.apr.toFixed(1)}%, and a current payment of {formatCurrency(selectedStrategyDebt.monthlyPayment)} / mo.</p><div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm"><div className="rounded-lg border border-[#2A0047] bg-[#130018] px-3 py-3"><span className="text-[11px] uppercase tracking-widest text-[#9E4CFF]">Extra Payment</span><div className="text-lg font-semibold mt-1 text-white">{formatCurrency(parseNumber(debtStrategyExtraAmount))}</div></div><div className="rounded-lg border border-[#2A0047] bg-[#130018] px-3 py-3"><span className="text-[11px] uppercase tracking-widest text-[#9E4CFF]">Payoff Time</span><div className="text-lg font-semibold mt-1 text-white">{formatMonths(firstPriorityEstimate?.months)}</div></div><div className="rounded-lg border border-[#2A0047] bg-[#130018] px-3 py-3"><span className="text-[11px] uppercase tracking-widest text-[#9E4CFF]">Estimated Total Paid</span><div className="text-lg font-semibold mt-1 text-white">{formatCurrency(firstPriorityEstimate?.totalPaid)}</div></div></div>{firstPriorityEstimate?.message ? <p className="text-xs text-[#FF9BE5]">{firstPriorityEstimate.message}</p> : null}</div> : null}</div></div>}
        </SectionCard>
      </div>
    </div>
  );

  const renderTaxes = () => {
    const suggestedQuarterlyPayment = estimatedAnnualTax / 4;

    return (
      <div>
        <h2 className="text-2xl font-semibold text-[#C9A6FF] mb-4">Tax Planner</h2>
        <div className="bg-black/70 border border-[#402060] text-[#D8D8D8] text-xs sm:text-sm rounded-xl p-4 mb-6 shadow-lg"><span className="text-[#C9A6FF] font-semibold">Disclaimer:</span><p className="mt-1 leading-relaxed">These numbers are planning estimates only. LLCs, S-corps, corporations, payroll withholding, deductions, and state rules can all change how taxes actually work for you. Always confirm with a tax professional.</p></div>

        <SectionCard title="Tax Setup" subtitle="Pick the business or filing setup that matches how this income is really handled.">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 text-sm">
            <div className="flex flex-col gap-2">
              <span className="text-[#9E4CFF] uppercase tracking-widest text-xs">Entity / Tax Setup</span>
              <select className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2" value={selectedTaxEntity} onChange={(event) => setTaxProfile((current) => ({ ...current, entityType: event.target.value }))}>
                {taxEntityOptions.map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}
              </select>
              <p className="text-xs text-[#D8D8D8]">{taxEntityOptions.find((option) => option.id === selectedTaxEntity)?.description}</p>
            </div>
            <div className="flex flex-col gap-2">
              <span className="text-[#9E4CFF] uppercase tracking-widest text-xs">How Taxes Are Paid</span>
              <select className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2" value={selectedTaxPaymentStyle} onChange={(event) => setTaxProfile((current) => ({ ...current, paymentStyle: event.target.value }))}>
                {taxPaymentOptions.map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}
              </select>
              <p className="text-xs text-[#D8D8D8]">{taxPaymentOptions.find((option) => option.id === selectedTaxPaymentStyle)?.description}</p>
            </div>
          </div>
        </SectionCard>

        <div className="mt-6">
          {selectedTaxEntity === "c-corp" ? (
            <div className="bg-black/80 border border-[#2A0047] rounded-xl p-6 shadow-xl text-sm text-[#D8D8D8] max-w-3xl">
              <p className="mb-2 text-[#C9A6FF] font-semibold">Corporate tax handling is different.</p>
              <p>This planner is strongest for personal cash-flow tax planning. If you operate as a C-corp, the business usually handles corporate tax filings separately, so this tab stays informational instead of showing quarterly owner estimates.</p>
            </div>
          ) : withholdingHandlesTaxes ? (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <SectionCard title="Withholding View" subtitle="Use this when payroll or employer withholding is doing most of the tax work." dark>
                <div className="flex flex-col gap-1 text-sm"><span className="text-xs text-[#9E4CFF] uppercase tracking-widest">{isW2 && w2InputMode === "take-home" ? "Paystub Taxes Withheld" : "Estimated Withholding Input"}</span><span className="text-xl font-semibold text-white">{formatCurrency(currentTaxWithheldMonthly)} / mo</span></div>
                <p className="text-sm text-[#D8D8D8] mt-2">This is informational only. The app is not subtracting extra owner-paid tax reserves when withholding is already handling it, and take-home mode already treats that money as out of your spendable budget.</p>
              </SectionCard>
              <SectionCard title="What This Means" subtitle="Your tax planning may already be covered through payroll.">
                <p className="text-sm text-[#D8D8D8]">You probably do not need the quarterly due-date planner for this income unless you also have distributions, side income, or under-withholding that needs separate planning.</p>
              </SectionCard>
              <SectionCard title="Next Best Move" subtitle="Keep the estimate useful without overcomplicating it." dark>
                <ul className="list-disc list-inside space-y-2 text-sm text-[#D8D8D8]">
                  <li>Review your paystubs or payroll setup if withholding still feels too low.</li>
                  <li>Use the Goals tab if you want a separate tax cushion anyway.</li>
                  <li>If you also have business income, switch the tax setup here so the planner can show owner-paid estimates.</li>
                </ul>
              </SectionCard>
            </div>
          ) : businessTaxesHandledSeparately ? (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <SectionCard title="Business-Handled View" subtitle="Use this when your accountant, payroll, or business process handles tax planning separately." dark>
                <div className="flex flex-col gap-1 text-sm"><span className="text-xs text-[#9E4CFF] uppercase tracking-widest">Annual Planning Estimate</span><span className="text-xl font-semibold text-white">{formatCurrency(estimatedAnnualTax)}</span></div>
                <p className="text-sm text-[#D8D8D8] mt-2">This number stays informational so you can keep context, but it is not being turned into a monthly reserve or quarterly reminder inside the app.</p>
              </SectionCard>
              <SectionCard title="Why Quarterly Cards Are Hidden" subtitle="The app should not guess a payment schedule that your business handles elsewhere.">
                <p className="text-sm text-[#D8D8D8]">When taxes are handled separately, the app avoids showing quarter-by-quarter guidance that could conflict with your accountant or business filing process.</p>
              </SectionCard>
              <SectionCard title="Best Use Of This Tab" subtitle="Keep it as a context card, not a filing instruction panel." dark>
                <ul className="list-disc list-inside space-y-2 text-sm text-[#D8D8D8]">
                  <li>Use this annual estimate for awareness only.</li>
                  <li>Track actual payments outside the app until a more advanced business-tax feature exists.</li>
                  <li>Switch to planning-only if you want monthly guidance without due dates.</li>
                </ul>
              </SectionCard>
            </div>
          ) : !estimatedPlannerEligible ? (
            <div className="bg-black/80 border border-[#2A0047] rounded-xl p-6 shadow-xl text-sm text-[#D8D8D8] max-w-3xl">
              <p className="mb-2">Choose the tax setup that matches how this income is handled in real life. Once that is set, this tab will show the right planning view.</p>
            </div>
          ) : estimatedAnnualTax <= 0 ? (
            <div className="bg-black/80 border border-[#2A0047] rounded-xl p-6 shadow-xl text-sm text-[#D8D8D8] max-w-3xl">
              <p className="mb-2">Add income details and a tax percentage first. Then this tab can turn that information into a useful tax planning estimate.</p>
            </div>
          ) : selectedTaxPaymentStyle === "planning-only" ? (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <SectionCard title="Tax Overview" subtitle="A planning estimate without due-date assumptions." dark>
                <div className="flex flex-col gap-1 text-sm"><span className="text-xs text-[#9E4CFF] uppercase tracking-widest">Estimated Annual Tax</span><span className="text-xl font-semibold text-white">{formatCurrency(estimatedAnnualTax)}</span></div>
                <div className="flex flex-col gap-1 text-sm"><span className="text-xs text-[#9E4CFF] uppercase tracking-widest">Monthly Reserve Idea</span><span className="text-lg font-semibold text-white">{formatCurrency(estimatedAnnualTax / 12)} / mo</span></div>
                {paychecksPerMonth ? <div className="flex flex-col gap-1 text-sm"><span className="text-xs text-[#9E4CFF] uppercase tracking-widest">Per-Paycheck Reserve Idea</span><span className="text-lg font-semibold text-white">{formatCurrency((estimatedAnnualTax / 12) / paychecksPerMonth)}</span></div> : null}
              </SectionCard>
              <SectionCard title="Why There Are No Quarter Cards" subtitle="Planning-only keeps the estimate useful without pretending a filing schedule.">
                <p className="text-sm text-[#D8D8D8]">This view is best when you want rough owner-tax planning but do not want the app to assume quarterly due dates or a formal estimated-payment structure.</p>
              </SectionCard>
              <SectionCard title="Simple Action Plan" subtitle="Keep the estimate practical, not official." dark>
                <ul className="list-disc list-inside space-y-2 text-sm text-[#D8D8D8]">
                  <li>Use the monthly reserve amount as a planning buffer.</li>
                  <li>Move money into a separate savings bucket if that helps you stay ready.</li>
                  <li>Confirm actual filing/payment rules with your accountant.</li>
                </ul>
              </SectionCard>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <SectionCard title="Tax Overview" subtitle="Estimated owner-paid tax planning based on your current setup." dark>
                <div className="flex flex-col gap-1 text-sm"><span className="text-xs text-[#9E4CFF] uppercase tracking-widest">Estimated Annual Tax</span><span className="text-xl font-semibold text-white">{formatCurrency(estimatedAnnualTax)}</span></div>
                <div className="flex flex-col gap-1 text-sm"><span className="text-xs text-[#9E4CFF] uppercase tracking-widest">Suggested Quarterly Payment</span><span className="text-lg font-semibold text-white">{formatCurrency(suggestedQuarterlyPayment)}</span></div>
                <div className="flex flex-col gap-1 text-sm"><span className="text-xs text-[#9E4CFF] uppercase tracking-widest">Suggested Monthly Savings</span><span className="text-lg font-semibold text-white">{formatCurrency(currentTaxSavingsTargetMonthly)} / mo</span></div>
                {suggestedTaxPerPaycheck > 0 ? <div className="flex flex-col gap-1 text-sm"><span className="text-xs text-[#9E4CFF] uppercase tracking-widest">Suggested Per Paycheck</span><span className="text-lg font-semibold text-white">{formatCurrency(suggestedTaxPerPaycheck)}</span></div> : null}
                <p className="text-xs text-[#D8D8D8]">This estimate reflects your current entity choice, payment style, additional income, and write-off setting.</p>
              </SectionCard>
              <SectionCard title="Estimated Payment Dates" subtitle="Typical U.S. due dates for personal estimated tax planning.">
                {[{ label: "Q1 - Jan to Mar", due: "April 15" }, { label: "Q2 - Apr to Jun", due: "June 15" }, { label: "Q3 - Jul to Sep", due: "September 15" }, { label: "Q4 - Oct to Dec", due: "January 15 (next year)" }].map((quarter) => <div key={quarter.label} className="flex items-center justify-between bg-black/40 rounded-lg px-3 py-2 text-sm"><div className="flex flex-col"><span className="text-xs text-[#D8D8D8]">{quarter.label}</span><span className="text-[11px] text-[#9E4CFF]">Est. due date: {quarter.due}</span></div><span className="text-sm font-semibold text-white">{formatCurrency(suggestedQuarterlyPayment)}</span></div>)}
              </SectionCard>
              <SectionCard title="Simple Action Plan" subtitle="Keep the tax reserve separate from spendable cash." dark>
                <ul className="list-disc list-inside space-y-2 text-sm text-[#D8D8D8]">
                  <li>Create a separate tax savings account.</li>
                  <li>Move about {formatCurrency(suggestedTaxPerPaycheck || currentTaxSavingsTargetMonthly)} each paycheck or month.</li>
                  <li>Use the date card as a reminder, not a legal filing guarantee.</li>
                  <li>Revisit this tab if your income or entity setup changes.</li>
                </ul>
              </SectionCard>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderGoalForecastSection = () => (
    <SectionCard title="Goal Forecast" subtitle="Pick one goal to see how long the current contribution pace could take. This is a planning preview, not live tracking." dark>
      {goalAnalyses.length === 0 ? (
        <p className="text-sm text-[#D8D8D8]">Create a goal first, then this section will show a simple month-by-month forecast for that target.</p>
      ) : (
        <>
          <div className="flex flex-wrap gap-2">{timelineOptions.map((option) => <button key={option.id} type="button" onClick={() => setTimelineFocus(option.id)} className={`px-3 py-1 rounded-full text-xs border ${String(activeTimelineGoal?.id) === String(option.id) ? "bg-[#9E4CFF] text-black border-[#9E4CFF]" : "bg-black/70 border-[#2A0047] text-[#D8D8D8]"}`}>{option.label}</button>)}</div>
          <LineChartComponent data={timelineData} />
          <p className="text-sm text-[#D8D8D8]">{activeTimelineGoal?.type === "debt" || activeTimelineGoal?.type === "all-debt" ? "This forecast shows the projected remaining debt balance using the current required payment plus your extra goal contribution each month." : "This forecast shows how the selected contribution could build toward the goal over time."}</p>
        </>
      )}
    </SectionCard>
  );

  const renderInsights = () => (
    <div>
      <h2 className="text-2xl font-semibold text-[#C9A6FF] mb-4">Insights</h2>
      <SectionCard title="Rule-Based Guidance" subtitle="These are rule-based insights built from your numbers so the advice stays practical and honest.">
        {insights.length === 0 ? <p className="text-sm text-[#D8D8D8]">Once you add a little more data, this tab will turn your numbers into plain-English pressure points and next-best actions.</p> : <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">{insights.map((insight, index) => <div key={`${insight.title}-${index}`} className="rounded-xl border border-[#2A0047] bg-black/40 p-4 flex flex-col gap-3"><span className="text-sm font-semibold text-[#C9A6FF]">{insight.title}</span><p className="text-sm text-[#D8D8D8]">{insight.body}</p>{insight.action ? <div className="rounded-lg border border-[#2A0047] bg-[#130018] px-3 py-3 text-xs text-[#D8D8D8]"><span className="text-[#C9A6FF] font-semibold">Recommended move:</span> {insight.action.replace(/^Recommended move: /, "")}</div> : null}{insight.actionTab && insight.actionLabel ? <button type="button" onClick={() => setActiveTab(insight.actionTab)} className="self-start px-3 py-2 rounded-lg bg-[#9E4CFF] text-black text-xs font-semibold tracking-wide shadow-[0_0_12px_#9E4CFF]">{insight.actionLabel}</button> : null}</div>)}</div>}
      </SectionCard>
    </div>
  );

  const renderSettings = () => (
    <div>
      <h2 className="text-2xl font-semibold text-[#C9A6FF] mb-4">Profile & Settings</h2>
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <SectionCard title="Personal Profile" subtitle="Update the personal details tied to your account and greeting. Theme changes save automatically in Theme Studio.">
          <div className="grid grid-cols-1 gap-4 text-sm">
            <div className="flex flex-col gap-2">
              <span className="text-[#9E4CFF] uppercase tracking-widest text-xs">Current Email</span>
              <div className="bg-black/60 border border-[#2A0047] rounded-lg px-3 py-2 text-[#D8D8D8]">{accountEmail}</div>
            </div>
            <div className="flex flex-col gap-2">
              <span className="text-[#9E4CFF] uppercase tracking-widest text-xs">Full Name</span>
              <input type="text" value={settingsForm.fullName} onChange={(event) => handleSettingsFieldChange("fullName", event.target.value)} className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 outline-none focus:border-[#9E4CFF]" />
            </div>
            <div className="flex flex-col gap-2">
              <span className="text-[#9E4CFF] uppercase tracking-widest text-xs">Preferred Name</span>
              <input type="text" value={settingsForm.preferredName} onChange={(event) => handleSettingsFieldChange("preferredName", event.target.value)} className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 outline-none focus:border-[#9E4CFF]" placeholder="Optional" />
            </div>
            <div className="flex flex-col gap-2">
              <span className="text-[#9E4CFF] uppercase tracking-widest text-xs">Date of Birth</span>
              <input type="date" value={settingsForm.dateOfBirth} onChange={(event) => handleSettingsFieldChange("dateOfBirth", event.target.value)} className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 outline-none focus:border-[#9E4CFF]" />
            </div>
            <button type="button" onClick={handleSaveSettings} className="self-center sm:self-start px-5 py-2 rounded-lg bg-[#9E4CFF] text-black font-semibold tracking-wide shadow-[0_0_16px_#9E4CFF] disabled:opacity-50" disabled={settingsBusy}>
              {settingsBusy ? "Saving..." : "Save Profile"}
            </button>
          </div>
        </SectionCard>

        <SectionCard title="Security & Account" subtitle="Manage password recovery, verification, and account email changes in one place." dark>
          <div className="flex flex-col gap-4 text-sm">
            <div className="rounded-2xl border border-[#2A0047] bg-[#130018] p-4">
              <p className="text-[#C9A6FF] font-semibold">Email verification</p>
              <p className="mt-2 text-xs text-[#D8D8D8]">Status: {accountEmailVerified ? "Verified" : "Not verified yet"}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <button type="button" onClick={handleVerificationEmail} className="px-4 py-2 rounded-lg border border-[#2A0047] bg-black/60 text-[#C9A6FF] font-semibold hover:bg-black disabled:opacity-50" disabled={settingsBusy || accountEmailVerified}>
                  {accountEmailVerified ? "Email Verified" : "Send Verification Email"}
                </button>
                <button type="button" onClick={handleRefreshAccountStatus} className="px-4 py-2 rounded-lg border border-[#2A0047] bg-black/60 text-[#C9A6FF] font-semibold hover:bg-black disabled:opacity-50" disabled={settingsBusy}>
                  Refresh Account Status
                </button>
              </div>
            </div>
            <div className="rounded-2xl border border-[#2A0047] bg-[#130018] p-4 flex flex-col gap-3">
              <p className="text-[#C9A6FF] font-semibold">Change account email</p>
              <p className="text-xs text-[#D8D8D8]">Current account email: {accountEmail}</p>
              <input type="email" value={settingsForm.pendingEmail} onChange={(event) => handleSettingsFieldChange("pendingEmail", event.target.value)} className="bg-black/70 border border-[#2A0047] rounded-lg px-3 py-2 outline-none focus:border-[#9E4CFF]" placeholder="New email address" />
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={handleEmailChangeRequest} className="self-start px-4 py-2 rounded-lg border border-[#2A0047] bg-black/60 text-[#C9A6FF] font-semibold hover:bg-black disabled:opacity-50" disabled={settingsBusy}>
                  Request Email Change
                </button>
                <button type="button" onClick={handleRefreshAccountStatus} className="self-start px-4 py-2 rounded-lg border border-[#2A0047] bg-black/60 text-[#C9A6FF] font-semibold hover:bg-black disabled:opacity-50" disabled={settingsBusy}>
                  Refresh Account Status
                </button>
              </div>
              <p className="text-xs text-[#D8D8D8]">We will send a confirmation link to the new email. Your account email updates after you confirm it.</p>
            </div>
            <div className="rounded-2xl border border-[#2A0047] bg-[#130018] p-4">
              <p className="text-[#C9A6FF] font-semibold">Reset password</p>
              <p className="mt-2 text-xs text-[#D8D8D8]">We will email a secure reset link to {accountEmail}. You can use the same flow from the login screen if you ever get locked out.</p>
              <button type="button" onClick={handlePasswordReset} className="mt-3 px-4 py-2 rounded-lg border border-[#2A0047] bg-black/60 text-[#C9A6FF] font-semibold hover:bg-black disabled:opacity-50" disabled={settingsBusy}>
                Send Password Reset Email
              </button>
            </div>
          </div>
        </SectionCard>

        <SectionCard title="Theme Studio" subtitle="Pick a dark or light look, then choose an accent color that stays readable across the app. Changes here save automatically.">
          <div className="flex flex-col gap-4 text-sm">
            <div className="flex flex-col gap-2">
              <span className="text-[#9E4CFF] uppercase tracking-widest text-xs">Theme</span>
              <div className="grid grid-cols-1 gap-3">
                {themeOptions.map((option) => <button key={option.id} type="button" onClick={() => handleSettingsFieldChange("themeMode", option.id)} className={`rounded-2xl border px-4 py-3 text-left ${settingsForm.themeMode === option.id ? "bg-[#9E4CFF] text-black border-[#9E4CFF]" : "bg-black/40 border-[#2A0047] text-[#D8D8D8]"}`}>
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-semibold">{option.label}</span>
                    <span className="text-[11px] uppercase tracking-widest">{option.tone}</span>
                  </div>
                  <p className="mt-1 text-xs">{option.description}</p>
                </button>)}
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <span className="text-[#9E4CFF] uppercase tracking-widest text-xs">Accent Color</span>
              <div className="grid grid-cols-2 gap-2">
                {accentOptions.map((option) => <button key={option.id} type="button" onClick={() => handleSettingsFieldChange("accentColor", option.id)} className={`rounded-xl border px-3 py-2 text-sm font-semibold ${settingsForm.accentColor === option.id ? "bg-[#9E4CFF] text-black border-[#9E4CFF]" : "bg-black/40 border-[#2A0047] text-[#D8D8D8]"}`}>
                  {option.label}
                </button>)}
              </div>
            </div>
            <div className="rounded-2xl border border-[#2A0047] bg-black/50 p-4 text-xs text-[#D8D8D8]">
              Theme changes preview instantly and save automatically across devices.
            </div>
          </div>
        </SectionCard>
      </div>

      {settingsMessage ? (
        <div className={`mt-6 rounded-2xl border px-4 py-3 text-sm ${settingsTone === "success" ? "border-[#2A5B44] bg-[#0E2119] text-[#CFFBE4]" : "border-[#5A1D55] bg-[#2A0A2E] text-[#FFD4F4]"}`}>
          {settingsMessage}
        </div>
      ) : null}
    </div>
  );

  useEffect(() => {
    setSettingsForm(createInitialSettingsForm(userProfile, accountEmail));
  }, [userProfile, accountEmail]);

  useEffect(() => {
    setTaxProfile((current) => {
      if (!current) {
        return createDefaultTaxProfile(incomeType);
      }

      if (incomeType === INCOME_TYPES.W2) {
        return { ...createDefaultTaxProfile(incomeType), ...current, entityType: "w2-only", paymentStyle: "withholding" };
      }

      if ((incomeType === INCOME_TYPES.SELF_EMPLOYED || incomeType === INCOME_TYPES.BOTH) && current.entityType === "w2-only") {
        return { ...createDefaultTaxProfile(incomeType), ...current };
      }

      return current;
    });
  }, [incomeType]);

  useEffect(() => {
    if (onBudgetStateChange) {
      onBudgetStateChange({
        activeTab,
        liteOnboardingComplete: isDemoMode ? demoLiteComplete : liteOnboardingComplete,
        incomeType,
        incomeFrequency,
        baseIncome,
        taxPercent,
        writeOffMode,
        customWriteOffPercent,
        customPayDates,
        primaryPayday,
        primaryLastPaycheckDate,
        incomePreviewMonth,
        mixedIncome,
        deductions,
        additionalIncomes,
        expenses,
        transactions,
        transactionForm,
        transactionMonth,
        debts,
        customGoals,
        goalForm,
        timelineFocus,
        debtStrategyTarget,
        debtStrategyMethod,
        debtStrategyExtraAmount,
        taxProfile,
      });
    }
  }, [
    onBudgetStateChange,
    activeTab,
    demoLiteComplete,
    isDemoMode,
    incomeType,
    incomeFrequency,
    baseIncome,
    taxPercent,
    writeOffMode,
    customWriteOffPercent,
    customPayDates,
    primaryPayday,
    primaryLastPaycheckDate,
    incomePreviewMonth,
    mixedIncome,
        deductions,
    additionalIncomes,
    expenses,
    transactions,
    transactionForm,
    transactionMonth,
    debts,
    customGoals,
    goalForm,
    timelineFocus,
    debtStrategyTarget,
    debtStrategyMethod,
    debtStrategyExtraAmount,
    liteOnboardingComplete,
    taxProfile,
  ]);

  const renderTransactionReminder = () => (
    <div className="rounded-2xl border border-[#2A0047] bg-black/60 px-4 py-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex flex-col gap-1">
        <p className="text-sm font-semibold text-[#C9A6FF]">Your plan is in place. Now add real transactions.</p>
        <p className="text-sm text-[#D8D8D8]">
          You already have enough setup data for planning, but the app still needs real spending and deposit activity to compare plan vs reality this month.
        </p>
      </div>
      {activeTab !== "transactions" ? (
        <button
          type="button"
          onClick={() => openTab("transactions", { quickCapture: true })}
          className="self-start sm:self-auto px-4 py-2 rounded-lg bg-[#9E4CFF] text-black font-semibold tracking-wide shadow-[0_0_16px_#9E4CFF]"
        >
          Open Quick Capture
        </button>
      ) : (
        <span className="text-xs uppercase tracking-widest text-[#9E4CFF]">Add your first real expense or deposit below.</span>
      )}
    </div>
  );

  const renderSetupChecklist = () => (
    <SectionCard
      title="Start Here"
      subtitle={
        nextSetupItem
          ? `Next best step: ${nextSetupItem.label.toLowerCase()}.`
          : "Your core setup is in place. Keep transactions current and revisit goals as the month changes."
      }
      dark
    >
      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_260px] gap-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {setupChecklistItems.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => openTab(item.tab, { quickCapture: item.tab === "transactions" })}
              className={`text-left rounded-2xl border px-4 py-4 transition-colors ${
                item.complete
                  ? "border-[#1F5D45] bg-[#0D2018] hover:bg-[#11271d]"
                  : "border-[#2A0047] bg-black/40 hover:bg-black/60"
              }`}
            >
              <div className="flex items-center justify-between gap-3">
                <span className={`text-sm font-semibold ${item.complete ? "text-[#CFFBE4]" : "text-[#C9A6FF]"}`}>
                  {item.label}
                </span>
                <span className={`text-[11px] uppercase tracking-widest ${item.complete ? "text-[#9BE7BF]" : "text-[#9E4CFF]"}`}>
                  {item.complete ? "Done" : "Needed"}
                </span>
              </div>
              <p className="mt-2 text-xs text-[#D8D8D8]">{item.description}</p>
            </button>
          ))}
        </div>
        <div className="rounded-2xl border border-[#2A0047] bg-[#130018] p-4 flex flex-col gap-3">
          <p className="text-[#C9A6FF] font-semibold">What to do first</p>
          <p className="text-sm text-[#D8D8D8]">
            {nextSetupItem
              ? nextSetupItem.description
              : "Your budget foundation is in good shape. Keep real transactions updated and use goals to direct the leftover."}
          </p>
          {nextSetupItem ? (
            <button
              type="button"
              onClick={() => openTab(nextSetupItem.tab, { quickCapture: nextSetupItem.tab === "transactions" })}
              className="self-start px-4 py-2 rounded-lg bg-[#9E4CFF] text-black font-semibold tracking-wide shadow-[0_0_16px_#9E4CFF]"
            >
              {nextSetupItem.actionLabel}
            </button>
          ) : null}
        </div>
      </div>
    </SectionCard>
  );

  const renderContent = () => {
    if (isLiteMode) {
      if (shouldShowLiteOnboarding) {
        return renderLiteOnboarding();
      }

      switch (activeTab) {
        case "overview":
          return renderLiteDashboardV2();
        case "income":
          return renderLiteIncomeV2();
        case "expenses":
          return renderLiteExpensesV2();
        case "settings":
          return renderSettings();
        default:
          return renderLiteDashboardV2();
      }
    }

    switch (activeTab) {
      case "control":
        return renderControlCenter();
      case "setup":
        return renderIncomeSetup();
      case "income":
        return renderIncomeOverview();
      case "expenses":
        return renderExpenses();
      case "transactions":
        return renderTransactions();
      case "goals":
        return renderGoals();
      case "taxes":
        return renderTaxes();
            case "insights":
        return renderInsights();
      case "settings":
        return renderSettings();
      default:
        return null;
    }
  };

  return (
    <div className={`theme-shell ${activeThemeMode} accent-${activeAccentColor} min-h-screen w-full bg-[#0E0A12] text-[#D8D8D8] px-4 py-6 sm:px-6 lg:px-8 flex flex-col gap-6 sm:gap-8`}>
      <header className="flex flex-col gap-3">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex flex-col gap-2 min-w-0">
            <FlowLogo compact showTagline={false} />
            {shouldShowLiteOnboarding ? (
              <p className="text-[11px] text-[#C9A6FF]">Lite setup</p>
            ) : (
              <>
                <p className="text-base sm:text-lg text-white font-medium">{shellWelcomeMessage}</p>
                {!isLiteMode ? (
                  <p className="hidden lg:block text-sm text-[#9E4CFF] uppercase tracking-[0.25em]">
                    {tabs.map((tab) => tab.label).join(" | ")}
                  </p>
                ) : null}
              </>
            )}
          </div>
          {shouldShowLiteOnboarding ? (
            <div className="self-start lg:self-auto flex flex-wrap items-center gap-2">
              <div className="rounded-full border border-[#2A0047] bg-black/60 px-4 py-2 text-xs text-[#D8D8D8]">
                {isDemoMode ? "Demo mode - Nothing saves yet" : "Quick setup - About 2 minutes"}
              </div>
              <button type="button" onClick={onPrimaryAccountAction || onLogout} disabled={settingsBusy} className="px-3 py-2 rounded-full text-xs font-semibold border border-[#2A0047] bg-[#130018] text-[#C9A6FF] hover:bg-black disabled:opacity-50 disabled:cursor-not-allowed">
                {primaryAccountActionLabel}
              </button>
            </div>
          ) : isLiteMode && isDemoMode ? (
            <div className="flex items-center gap-2 sm:gap-3 rounded-2xl border border-[#2A0047] bg-black/50 px-3 py-2.5">
              <div className="rounded-full border border-[#2A0047] bg-[#130018] px-3 py-1 text-[11px] uppercase tracking-[0.2em] text-[#C9A6FF]">
                Demo
              </div>
              <button type="button" onClick={onPrimaryAccountAction || onLogout} disabled={settingsBusy} className="px-3 py-2 rounded-full text-xs font-semibold border border-[#2A0047] bg-[#130018] text-[#C9A6FF] hover:bg-black disabled:opacity-50 disabled:cursor-not-allowed">
                {primaryAccountActionLabel}
              </button>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-2xl border border-[#2A0047] bg-black/60 px-4 py-3">
              <div className="text-xs text-[#D8D8D8]">
                <p className="uppercase tracking-widest text-[#9E4CFF]">{accountPanelTitle}</p>
                <p className="font-semibold text-white">{displayName}</p>
                <p>{accountEmail}</p>
              </div>
              <div className="text-xs text-[#D8D8D8]">
                <p className="uppercase tracking-widest text-[#9E4CFF]">{savePanelTitle}</p>
                <p>{savePanelStatus}</p>
              </div>
              {isAdmin && onOpenAdminConsole ? (
                <button type="button" onClick={onOpenAdminConsole} className="px-3 py-2 rounded-full text-xs font-semibold border border-[#53E6D4]/50 bg-[#06201D] text-[#B8FFF5] hover:bg-[#0B2C29]">
                  Admin Console
                </button>
              ) : null}
              <button type="button" onClick={() => openTab("settings")} className="w-11 h-11 flex items-center justify-center rounded-full text-lg font-semibold border border-[#2A0047] bg-[#130018] text-[#C9A6FF] hover:bg-black" aria-label="Open settings" title="Settings">
                <span aria-hidden="true" className="leading-none">{"\u2699"}</span>
              </button>
              <button type="button" onClick={onPrimaryAccountAction || onLogout} disabled={settingsBusy} className="px-3 py-2 rounded-full text-xs font-semibold border border-[#2A0047] bg-[#130018] text-[#C9A6FF] hover:bg-black disabled:opacity-50 disabled:cursor-not-allowed">
                {primaryAccountActionLabel}
              </button>
            </div>
          )}
        </div>
      </header>
      {!shouldShowLiteOnboarding ? (
        <nav className="-mx-4 px-4 sm:mx-0 sm:px-0 mt-2 overflow-x-auto no-scrollbar">
          <div className="flex w-max min-w-full gap-2 pb-1">
            {tabs.map((tab) => (
              <button key={tab.id} type="button" onClick={() => openTab(tab.id)} className={`shrink-0 px-3.5 py-2 rounded-xl text-sm font-semibold transition-all border border-[#2A0047] ${activeTab === tab.id ? "bg-[#9E4CFF] text-black shadow-[0_0_18px_#9E4CFF]" : "bg-black/60 text-[#C9A6FF] hover:bg-black"}`}>
                {tab.label}
              </button>
            ))}
          </div>
        </nav>
      ) : null}
      {!isLiteMode && shouldShowSetupGuidance ? renderSetupChecklist() : null}
      {!isLiteMode && shouldShowTransactionReminder ? renderTransactionReminder() : null}
      {accountNotice && !isLiteMode ? (<div className="rounded-2xl border border-[#2A5B44] bg-[#0E2119] px-4 py-3 text-sm text-[#CFFBE4]">{accountNotice}</div>) : null}
      <main className="mt-4 flex-1 flex flex-col gap-6">{renderContent()}</main>
      {showLiteUpgradePrompt && selectedLiteUpgrade ? (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center px-4 py-6">
          <div className="w-full max-w-lg rounded-[28px] border border-[#2A0047] bg-[#0E0A12] p-8 shadow-2xl flex flex-col gap-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-[11px] uppercase tracking-[0.24em] text-[#9E4CFF]">Upgrade</p>
                <h3 className="mt-2 text-2xl font-semibold text-white">{selectedLiteUpgrade.title}</h3>
              </div>
              <button type="button" onClick={() => setShowLiteUpgradePrompt(false)} className="w-10 h-10 rounded-full border border-[#2A0047] bg-black/40 text-[#C9A6FF] text-xl leading-none hover:bg-black/60" aria-label="Close upgrade prompt">
                x
              </button>
            </div>
            <p className="text-sm text-[#D8D8D8]">{selectedLiteUpgrade.body}</p>
            <div className="rounded-2xl border border-[#2A0047] bg-black/30 p-4">
              <p className="text-sm font-semibold text-[#C9A6FF]">FLOW Lite gets you clarity fast.</p>
              <p className="mt-2 text-sm text-[#D8D8D8]">FLOW System unlocks deeper planning, smarter guidance, and the power tools behind the full product.</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <button type="button" onClick={() => setShowLiteUpgradePrompt(false)} className="w-full rounded-xl border border-[#2A0047] bg-black/40 px-4 py-3 font-semibold text-[#D8D8D8] hover:bg-black/60">
                Keep Exploring Lite
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowLiteUpgradePrompt(false);
                  if (isDemoMode && onPrimaryAccountAction) {
                    onPrimaryAccountAction();
                  }
                }}
                className="w-full rounded-xl bg-[#9E4CFF] px-4 py-3 font-semibold text-black shadow-[0_0_20px_#9E4CFF]"
              >
                {isDemoMode ? "Create Account to Unlock" : "Unlock FLOW System"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}




































